/**
 * Centralised API client + auth-token store.
 *
 * Production-readiness (2026-05-08). Wraps fetch with:
 *   - bearer-token injection from localStorage
 *   - 401 → redirect-to-login behaviour
 *   - JSON request/response framing
 *   - typed error surface
 *
 * The token is set by the login page (calling POST /v1/auth/login -
 * Phase 4 customer-login; /v1/auth/demo-login for the demo personas) and
 * picked up by every subsequent API call via this client. The useSseStream
 * hook also reads it via getAuthToken() so SSE connections carry the
 * same credential as REST calls.
 *
 * In production this is replaced by SSO/SCIM cookie-based session; the
 * client interface stays identical.
 */

import { permissionInWords, ROLE_LABELS } from '@/generated/persona/discovery-access';

const STORAGE_TOKEN_KEY = 'foctta.auth.token';
const STORAGE_USER_KEY = 'foctta.auth.user';
const TOKEN_EXPIRY_KEY = 'foctta.auth.expiresAt';

// Exported so SSE hooks + direct-download helpers across the app route
// through the SAME base. Defaults to `/api`; the Pattern-A build sets
// VITE_API_BASE=/api/discovery so bundled-tenant calls reach Discovery's
// controller (Core rewrites /api/discovery/* → /api/*) instead of colliding
// with Core on the bare /api/v1/* prefix. Legacy standalone leaves it `/api`.
//
// Injected via Vite `define` (vite.config.ts → __API_BASE__) because this
// Vite setup does NOT expose process.env VITE_* to import.meta.env. The
// `typeof` guard keeps vitest (no define) safe - it falls back to '/api'.
declare const __API_BASE__: string;
export const API_BASE = ((typeof __API_BASE__ !== 'undefined' ? __API_BASE__ : '/api') || '/api').replace(
  /\/$/,
  '',
);

export interface AuthenticatedUser {
  email: string;
  displayName: string;
  role: string;
  tenantId: string;
  /** Set when the user logged in as a custom_role. */
  customRoleId?: string;
  /** Human-readable label for the custom role (e.g. "SOC Tier-2 Triage"). */
  customRoleName?: string;
  /**
   * Set on a session that came across the bridge from FOCTTA Consent: that
   * deployment's public base URL, for the top bar's link back (release 3.1,
   * persona D6). Absent for a Discovery-native sign-in.
   */
  consentUrl?: string;
}

export function getAuthToken(): string | null {
  const token = localStorage.getItem(STORAGE_TOKEN_KEY);
  if (!token) return null;
  const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (expiresAt && new Date(expiresAt).getTime() < Date.now()) {
    clearAuth();
    return null;
  }
  return token;
}

export function getAuthUser(): AuthenticatedUser | null {
  const raw = localStorage.getItem(STORAGE_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthenticatedUser;
  } catch {
    return null;
  }
}

export function setAuth(token: string, user: AuthenticatedUser, expiresAt: string): void {
  localStorage.setItem(STORAGE_TOKEN_KEY, token);
  localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(user));
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt);
  // Notify same-tab listeners (Zustand stores etc.).
  window.dispatchEvent(new Event('foctta:auth-changed'));
}

export function clearAuth(): void {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
  localStorage.removeItem(STORAGE_USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  window.dispatchEvent(new Event('foctta:auth-changed'));
}

/**
 * Sign out (release 3.3 C3): tell the controller first, so the tenant's audit
 * trail records `auth.signed_out`, then drop the session here. Best effort and
 * bounded to two seconds: a sign-out never waits on, or fails because of, the
 * network. An expired session gets a 401, which changes nothing.
 */
export async function signOut(): Promise<void> {
  const token = getAuthToken();
  if (token) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2_000);
    try {
      await fetch(`${API_BASE}/v1/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
    } catch {
      // Offline or aborted: the session is still dropped below.
    } finally {
      window.clearTimeout(timer);
    }
  }
  clearAuth();
}

/**
 * SSO bridge exchange (Phase A - doc 21 §4.2).
 *
 * Called by the /auth/bridge SPA route when the user is redirected from
 * Core's tenant SPA carrying a bridge token in the URL fragment
 * (`#t=<jwt>`). Posts the token to /v1/auth/exchange; on success
 * persists the returned Discovery session via setAuth().
 *
 * The fragment is read client-side and never sent to the server in a
 * Referer header - keeps the bridge token off any HTTP intermediary's
 * access log.
 */
export interface BridgeExchangeResponse {
  token: string;
  expiresAt: string;
  user: {
    email: string;
    displayName: string;
    role: string;
    tenantId: string;
  };
  /** The issuing Consent deployment's public base URL (release 3.1, D6). */
  consentUrl?: string;
}

// Public-signup helpers were removed 2026-05-22. Core (app.foctta.com)
// is the canonical signup. Customers arrive via /auth/bridge.

// ── Phase 4 (2026-05-22) - customer auth helpers ────────────────────

export interface MagicLinkVerification {
  ok: true;
  purpose: 'first_login' | 'forgot_password' | 'invitation_resend';
  tenant: { id: string; slug: string | null };
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    isFirstLogin: boolean;
  };
}

export async function verifyMagicLinkApi(token: string): Promise<MagicLinkVerification> {
  const res = await fetch(
    `${API_BASE}/v1/auth/verify-magic-link?t=${encodeURIComponent(token)}`,
  );
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return JSON.parse(text) as MagicLinkVerification;
}

export interface SetPasswordResponse {
  ok: true;
  /** Discriminator for the CustomerLoginResponse union. */
  mfaChallengeRequired?: false;
  session: { token: string; expiresAt: string };
  user: AuthenticatedUser;
}

/**
 * Consume a sign-in link and set the password. Answers like customerLoginApi:
 * a session, or - for an account with MFA enrolled or required (decision 65,
 * 2026-09-27) - a partial-auth token for the MFA step. A reset link is not a
 * second factor.
 */
export async function setPasswordApi(args: {
  magicLinkToken: string;
  password: string;
}): Promise<CustomerLoginResponse> {
  const res = await fetch(`${API_BASE}/v1/auth/set-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return JSON.parse(text) as CustomerLoginResponse;
}

/**
 * Wave 6 (2026-05-27) - login response now discriminates between a
 * fully-authenticated session and a partial-auth response requiring
 * MFA enrolment or challenge.
 */
export interface CustomerLoginMfaResponse {
  ok?: false;
  partialAuth: { token: string; expiresAt: string };
  mfaChallengeRequired: true;
  mfaReason: 'mfa_required' | 'mfa_enrolment_due';
  user: AuthenticatedUser;
}
export type CustomerLoginResponse = SetPasswordResponse | CustomerLoginMfaResponse;

export async function customerLoginApi(args: {
  tenantSlug: string;
  email: string;
  password: string;
}): Promise<CustomerLoginResponse> {
  const res = await fetch(`${API_BASE}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return JSON.parse(text) as CustomerLoginResponse;
}

// ============================================================
// Wave 6 (2026-05-27) - MFA enrol + verify + challenge wrappers.
//
// The contract with /v1/auth/mfa/* (2026-09-27, tracker decision 62): the
// partial-auth token from /v1/auth/login is a bearer credential of its own
// audience, sent ONLY as `Authorization: Bearer <token>`, never in a body or
// a URL. The bodies are {} (enrol), { totpCode } (confirm enrolment) and
// { totpCode } or { backupCode } (sign-in challenge). Until that date the API
// read the token from the body, so every completion from here answered 400.
//
// The partial token is never passed to setAuth: only the session the API
// mints on success is stored, so no other request can carry it.
// ============================================================

export interface MfaEnrolResult {
  ok: true;
  secretBase32: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
  backupCodes: string[];
}
export interface MfaVerifyResult {
  ok: true;
  session: { token: string; expiresAt: string };
  user: AuthenticatedUser;
}
export interface MfaChallengeResult {
  ok: true;
  session: { token: string; expiresAt: string };
  user: AuthenticatedUser;
  method?: 'totp' | 'backup_code';
  backupCodesRemaining?: number;
}

/** A refused MFA request: the HTTP status and the problem `title` the API answered with. */
export class MfaRequestError extends Error {
  constructor(
    public readonly status: number,
    /** RFC 7807 `title`, e.g. `partial_auth_expired`, `mfa_invalid_code`; null when the body had none. */
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'MfaRequestError';
  }
}

/** Why an MFA page sends the person back to sign-in (the `?mfa_error=` value). */
export type MfaSignInAgainReason = 'expired' | 'invalid' | 'suspended' | 'role' | 'locked';

/**
 * Is this refusal the end of the MFA step? True when the partial-auth token is
 * unusable (`partial_auth_*`: missing, expired, not a partial token, bad
 * signature, already used) or the account can no longer sign in (suspended, a
 * role that cannot hold a session, gone, no longer at this step, MFA locked
 * after too many wrong codes). Null for everything the person can fix on the
 * page: a wrong, reused or malformed code, a rate limit, a server error.
 */
export function mfaSignInAgainReason(err: unknown): MfaSignInAgainReason | null {
  if (!(err instanceof MfaRequestError) || err.code === null) return null;
  if (err.code === 'partial_auth_expired') return 'expired';
  if (err.code.startsWith('partial_auth_')) return 'invalid';
  if (err.code === 'account_suspended') return 'suspended';
  if (err.code === 'role_not_permitted') return 'role';
  // Too many wrong codes on this account (2026-09-27, R84-7): the lock outlasts
  // the five-minute step, so waiting on this page cannot help.
  if (err.code === 'mfa_locked') return 'locked';
  if (err.code === 'mfa_user_not_found' || err.code === 'mfa_not_enrolled' || err.code === 'mfa_already_enrolled') {
    return 'invalid';
  }
  return null;
}

async function mfaFetch<T>(path: string, partialAuthToken: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${partialAuthToken}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    let code: string | null = null;
    try {
      const parsed = JSON.parse(text) as { detail?: string; title?: string };
      if (parsed.detail) detail = parsed.detail;
      if (typeof parsed.title === 'string') code = parsed.title;
    } catch {
      /* keep default */
    }
    throw new MfaRequestError(res.status, code, detail);
  }
  return JSON.parse(text) as T;
}

export async function mfaEnrol(partialAuthToken: string): Promise<MfaEnrolResult> {
  return mfaFetch<MfaEnrolResult>('/v1/auth/mfa/enroll', partialAuthToken, {});
}
export async function mfaVerifyEnrol(
  partialAuthToken: string,
  totpCode: string,
): Promise<MfaVerifyResult> {
  return mfaFetch<MfaVerifyResult>('/v1/auth/mfa/verify', partialAuthToken, { totpCode });
}
export async function mfaChallenge(
  partialAuthToken: string,
  args: { totpCode?: string; backupCode?: string },
): Promise<MfaChallengeResult> {
  return mfaFetch<MfaChallengeResult>('/v1/auth/mfa/challenge', partialAuthToken, args);
}

// ── Workstream B (2026-07-12) - login-page SSO discovery ────────────
//
// Tells the login page whether a tenant has an enabled IdP so it can show
// "Sign in with SSO". Pre-auth (no bearer). The endpoint returns the SAME
// shape for an unknown workspace and a workspace-without-SSO, so this never
// leaks which slugs are real tenants. Any failure degrades to ssoEnabled:false
// - SSO discovery must never block password login.

export interface SsoDiscovery {
  ssoEnabled: boolean;
  kind: 'saml' | 'oidc' | null;
}

export async function getSsoDiscovery(tenantSlug: string): Promise<SsoDiscovery> {
  try {
    const res = await fetch(
      `${API_BASE}/v1/auth/sso/discover?slug=${encodeURIComponent(tenantSlug)}`,
      { headers: { accept: 'application/json' } },
    );
    if (!res.ok) return { ssoEnabled: false, kind: null };
    const body = (await res.json()) as Partial<SsoDiscovery>;
    return {
      ssoEnabled: body.ssoEnabled === true,
      kind: body.kind === 'saml' || body.kind === 'oidc' ? body.kind : null,
    };
  } catch {
    return { ssoEnabled: false, kind: null };
  }
}

/**
 * Build the URL the "Sign in with SSO" button sends the browser to. The
 * server (GET /v1/auth/sso/login/:slug) resolves the slug, auto-picks
 * SAML vs OIDC, and 302-redirects to the IdP. Relative to API_BASE so it
 * stays same-origin (the IdP posts back to the same host's /sso/landing).
 */
export function ssoLoginUrl(tenantSlug: string, returnUrl = '/'): string {
  return `${API_BASE}/v1/auth/sso/login/${encodeURIComponent(tenantSlug)}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export async function forgotPasswordApi(args: {
  tenantSlug: string;
  email: string;
}): Promise<{ ok: true; emailSent: boolean }> {
  const res = await fetch(`${API_BASE}/v1/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json() as Promise<{ ok: true; emailSent: boolean }>;
}

/**
 * Resolve the apex domain from the current hostname. Returns the canonical
 * apex for which URL pattern the user is on so subsequent redirects /
 * displays stay on the same pattern during the migration window.
 *
 * Returns:
 *   - 'foctta.com'             (Pattern A canonical, per docs/architecture/24)
 *   - 'discovery.foctta.com'   (legacy, kept during 30-day migration window)
 *   - 'foctta.com'             (fallback for localhost / unknown - Pattern A canonical)
 */
export function tenantApexFromHost(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  // Legacy pattern wins if both end-with checks match (".discovery.foctta.com"
  // also ends-with ".foctta.com", so longest-first detection matters).
  if (host === 'discovery.foctta.com' || host.endsWith('.discovery.foctta.com')) {
    return 'discovery.foctta.com';
  }
  // Pattern A canonical default - covers <slug>.foctta.com, app.foctta.com,
  // foctta.com, and any future host under the apex.
  return 'foctta.com';
}

/** Resolve the tenantSlug from the current hostname. Returns null on apex / dev. */
export function tenantSlugFromHost(): string | null {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  // Reserved system hostnames - never treated as tenant slugs.
  const RESERVED = new Set([
    'foctta.com',
    'www.foctta.com',
    'app.foctta.com',
    'activate.foctta.com',
    'discovery.foctta.com',
    'www.discovery.foctta.com',
  ]);
  if (RESERVED.has(host)) return null;
  // <slug>.discovery.foctta.com   → "<slug>" (Pattern A migration: legacy)
  // <slug>.foctta.com             → "<slug>" (Pattern A canonical, per docs/architecture/24)
  // localhost / IP                → null
  const match = host.match(/^([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)\.(discovery\.foctta\.com|foctta\.com)$/);
  return match ? match[1]! : null;
}

// ── Phase 5 (2026-05-22) - per-tenant DAM retention policy ─────────

export interface RetentionPolicy {
  tenantId: string;
  tenantSlug: string;
  retentionDays: number;
  recommendedDays: number | null;
  minDays: number;
  maxDays: number;
}

export async function getRetentionPolicy(): Promise<RetentionPolicy> {
  return apiFetch<RetentionPolicy>('/v1/activity/retention');
}

export async function setRetentionPolicy(args: {
  retentionDays: number;
  reason: string;
}): Promise<RetentionPolicy> {
  return apiFetch<RetentionPolicy>('/v1/activity/retention', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export interface RetentionCleanupResult {
  tenantId: string;
  retentionDays: number;
  rowsMarkedForDelete: number | null;
  mutationsSubmitted: number;
  errors: string[];
  durationMs: number;
}

export async function triggerRetentionCleanup(): Promise<RetentionCleanupResult> {
  return apiFetch<RetentionCleanupResult>('/v1/activity/retention/cleanup', {
    method: 'POST',
  });
}

export async function exchangeBridgeToken(bridgeToken: string): Promise<BridgeExchangeResponse> {
  // Absolute URL surfaced in diagnostics so a "Failed to fetch" (network /
  // CORS / blocked) error shows exactly which endpoint the browser tried.
  const exchangeUrl = new URL(`${API_BASE}/v1/auth/exchange`, window.location.origin).href;
  let res: Response;
  try {
    res = await fetch(exchangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bridgeToken }),
    });
  } catch (netErr) {
    // fetch() only rejects on a network-level failure (DNS, refused, CORS
    // preflight blocked, offline, blocked by an extension/proxy). Surface the
    // URL + underlying reason so the failure is diagnosable from the screen.
    const reason = netErr instanceof Error ? `${netErr.name}: ${netErr.message}` : String(netErr);
    throw new Error(`${reason} — could not reach ${exchangeUrl}`);
  }
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* leave parsed as null; ApiError carries the raw text */
  }
  if (!res.ok) {
    const detail =
      (parsed as { detail?: string } | null)?.detail ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, detail, parsed);
  }
  return parsed as BridgeExchangeResponse;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

/**
 * The controller's answer when the Activity event store (ClickHouse) is absent
 * or unreachable: a 503 with this problem type, or a 200 carrying
 * `activityStore: { available: false }` next to the Postgres data it could
 * still read. Production runs without ClickHouse until a DAM customer exists,
 * so this is a normal state to render as "Not available", never as a failure
 * banner, and never something to retry.
 */
export const ACTIVITY_STORE_PROBLEM_TYPE = 'https://foctta.com/problems/activity-store-unavailable';

export interface ActivityStoreStatus {
  available: boolean;
  reason?: 'not_configured' | 'unreachable';
}

export function isActivityStoreUnavailable(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 503) return false;
  const body = err.body as { type?: unknown } | null;
  return body?.type === ACTIVITY_STORE_PROBLEM_TYPE;
}

/**
 * The sentence the server wrote for a refusal: the problem's `detail` (a 409
 * command_not_supported / pause_not_supported, a 423 AGENT_PAUSED), not the
 * "Error - ... (code: ...)" string apiFetch builds for its message. A 403
 * keeps apiFetch's clean text (its detail is a raw permission string), and
 * anything that is not an ApiError falls back to its message.
 */
export function problemDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status !== 403) {
    const body = err.body && typeof err.body === 'object' ? (err.body as Record<string, unknown>) : null;
    const detail = body?.['detail'];
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

interface RequestOptions extends RequestInit {
  /** When true, do NOT attach Authorization. Used by /v1/auth/login. */
  skipAuth?: boolean;
  /**
   * Optional Idempotency-Key. When provided, sent as the `Idempotency-Key`
   * header. Backend caches the response for 24h keyed on this value, so
   * retries with the same key return the same outcome - required for safe
   * POSTs that the user might double-click.
   */
  idempotencyKey?: string;
}

/**
 * Generate a fresh idempotency key. Useful when the caller doesn't have a
 * stable id of its own; bind once at form-mount time and re-use across
 * retries of the same submit. UUID v4 from the browser's WebCrypto.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for very old browsers: hex-shaped, NOT a real UUID but unique
  // enough for idempotency purposes (the backend treats it as opaque).
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * A 403 in plain words (release 3.1, persona D6). The controller refuses with
 * "role '<role>' lacks permission '<permission>'" (or "custom_role lacks
 * permission '<permission>'"); that becomes which permission is missing, in
 * the words the persona table gives it. Any other refusal keeps its own
 * explanation. Exported for tests.
 */
export function forbiddenMessage(serverMessage: string): string {
  const m =
    /role '([^']+)' lacks permission '([^']+)'/.exec(serverMessage) ??
    /(custom_role) lacks permission '([^']+)'/.exec(serverMessage);
  const [, roleId, permission] = m ?? [];
  if (roleId && permission) {
    const role = ROLE_LABELS[roleId] ?? roleId;
    return `Your role (${role}) does not have permission to ${permissionInWords(permission)} (${permission}). Ask your administrator if your work needs it.`;
  }
  const detail = serverMessage.replace(/^Forbidden\s*[-:]\s*/i, '').trim();
  return detail ? `You do not have access to this: ${detail}` : 'You do not have access to this.';
}

/**
 * True when a Content-Type names JSON: `application/json`, or any `+json`
 * structured suffix (RFC 6839) such as `application/problem+json` (RFC 7807),
 * with any parameters and in any case. Before this, apiFetch matched only the
 * substring `application/json`, so every `application/problem+json` error
 * (the reconciliation busy and timeout 503s among them, review of #187 round
 * 5) was read as text, its `detail` was lost, and the page showed a bare
 * "503 Service Unavailable". Not a regex over the raw header: the media type
 * is cut at the first `;`, trimmed and compared, so no input can make it slow.
 * `ndjson` and `json-seq` do not match, so those bodies stay text. Exported
 * for tests.
 */
export function isJsonMediaType(contentType: string): boolean {
  const semi = contentType.indexOf(';');
  const essence = (semi === -1 ? contentType : contentType.slice(0, semi)).trim().toLowerCase();
  if (essence.length > 255) return false;
  const slash = essence.indexOf('/');
  if (slash <= 0 || essence.indexOf('/', slash + 1) !== -1) return false;
  const subtype = essence.slice(slash + 1);
  return subtype === 'json' || (subtype.endsWith('+json') && subtype.length > '+json'.length);
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(opts.headers as Record<string, string> | undefined),
  };
  if (opts.body && !headers['content-type']) {
    headers['content-type'] = 'application/json';
  }
  if (opts.idempotencyKey) {
    headers['idempotency-key'] = opts.idempotencyKey;
  }
  if (!opts.skipAuth) {
    const token = getAuthToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401 && !opts.skipAuth) {
    clearAuth();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login');
    }
  }
  const contentType = res.headers.get('content-type') ?? '';
  let body: unknown = null;
  if (isJsonMediaType(contentType)) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => null);
  }
  if (!res.ok) {
    // Prefer the richest detail available. RFC 7807 problem-details
    // (`title` + `detail` + `code`) is the canonical shape across the
    // platform; fall back to legacy `error` field; finally fall back
    // to status code + reason phrase. The full body is preserved on
    // ApiError.body so callers can render structured detail.
    const b = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const title = typeof b?.title === 'string' ? b.title : null;
    const detail = typeof b?.detail === 'string' ? b.detail : null;
    const code = typeof b?.code === 'string' ? (b.code as string) : null;
    const legacyError = typeof b?.error === 'string' ? b.error : null;
    const message =
      // "Title - detail (code: xxx)" when we have rich problem-details
      (title && detail ? `${title} - ${detail}${code ? ` (code: ${code})` : ''}` : null) ??
      title ??
      detail ??
      legacyError ??
      `${res.status} ${res.statusText}`;
    if (res.status === 403) {
      // 403 problem-details carry raw RBAM permission strings (e.g.
      // "role 'dpo' lacks permission 'foundational:integration:read'"). They
      // are turned into plain words naming the missing permission (release
      // 3.1, persona D6); this used to say every section "typically" needs the
      // IT Admin, which was wrong for most of them. The technical detail stays
      // on ApiError.body (+ console) for operators/debugging.
      if (typeof console !== 'undefined') console.warn('[api] 403', path, message);
      throw new ApiError(403, forbiddenMessage(message), body);
    }
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

// ── High-level domain calls ─────────────────────────────────────────

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: AuthenticatedUser;
}

/**
 * Public auth policy probe - tells the LoginPage whether to render a
 * password field. Three modes:
 *   - 'disabled': demo login is turned off; the page should direct the
 *                  user to the SSO sign-in flow.
 *   - 'password': caller must send a shared demo password. The page
 *                  renders a password input.
 *   - 'open':     dev mode - email-only.
 */
export interface DemoLoginPolicy {
  mode: 'disabled' | 'password' | 'open';
}
export async function getDemoLoginPolicy(): Promise<DemoLoginPolicy> {
  return apiFetch<DemoLoginPolicy>('/v1/auth/policy', { skipAuth: true });
}

export async function loginRequest(
  email: string,
  options: { password?: string; customRoleId?: string } = {},
): Promise<LoginResponse> {
  const body: { email: string; password?: string; customRoleId?: string } = { email };
  if (options.password && options.password.length > 0) body.password = options.password;
  if (options.customRoleId && options.customRoleId.length > 0) body.customRoleId = options.customRoleId;
  // Demo-login moved /v1/auth/login → /v1/auth/demo-login on 2026-05-22.
  // The canonical /v1/auth/login is now Phase 4 customer-login.
  return apiFetch<LoginResponse>('/v1/auth/demo-login', {
    method: 'POST',
    skipAuth: true,
    body: JSON.stringify(body),
  });
}

export interface DevUserPreset {
  actorId: string;
  email: string;
  displayName: string;
  role: string;
}

export async function listDevUsers(): Promise<DevUserPreset[]> {
  const r = await apiFetch<{ users: DevUserPreset[] }>('/v1/auth/users', {
    skipAuth: true,
  });
  return r.users;
}

export interface ApprovedCustomRolePreset {
  id: string;
  name: string;
  description: string | null;
  permissionsCount: number;
}

/**
 * Fetch approved custom_roles for the demo tenant. Unauthenticated, dev
 * only - powers the LoginPage "Log in as Custom Role" picker. Production
 * SSO/SCIM via @foctta/auth replaces this entirely.
 */
export async function listApprovedCustomRoles(): Promise<ApprovedCustomRolePreset[]> {
  const r = await apiFetch<{ roles: ApprovedCustomRolePreset[] }>(
    '/v1/auth/approved-custom-roles',
    { skipAuth: true },
  );
  return r.roles;
}

// ── Discovery ──

export interface DiscoveryFinding {
  id: string;
  findingType: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string | null;
  recommendation: string | null;
  confidence: number;
  status: string;
  classifications: Array<{ label: string; confidence: number }>;
  relatedRegulations: string[];
  /**
   * Release 3.3, B4: the product tier. Confirmed and Likely count in headline
   * numbers; Possible is listed on its own. Absent on a controller older than
   * 3.3 (lib/finding-tier.ts reads the legacy rule then).
   */
  tier?: 'confirmed' | 'likely' | 'possible';
  tierBasis?: string;
  createdAt: string;
}

/** Release 3.3, B4: findings by product tier, status and severity. */
export interface FindingTierCounts {
  /** false when the estate was too large to count per request (the fleet counts stand in). */
  available: boolean;
  byTier: Record<'confirmed' | 'likely' | 'possible', Record<string, Record<string, number>>>;
  /** Confirmed + Likely: what headline numbers count. */
  headline: Record<string, Record<string, number>>;
}

export function getFindingTierCounts(): Promise<FindingTierCounts> {
  return apiFetch('/v1/discovery/findings/tier-counts');
}

export async function listDiscoveryFindings(params: {
  severity?: string;
  status?: string;
  limit?: number;
  /**
   * P1 OBS-2.alt (2026-05-26) - cursor-based pagination. Pass the
   * `nextCursor` from the previous page's response to get the next
   * page. Backend filters on `createdAt < cursor` (descending).
   */
  cursor?: string;
  /** Slice D: 'undeclared_personal_data' or 'declared_element_not_found'. */
  findingFamily?: string;
} = {}): Promise<{ findings: DiscoveryFinding[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (params.severity) q.set('severity', params.severity);
  if (params.status) q.set('status', params.status);
  if (params.findingFamily) q.set('findingFamily', params.findingFamily);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.cursor) q.set('cursor', params.cursor);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/discovery/findings${suffix}`);
}

/** One data type inside a by-file row. `estimated` is null when no scan estimated it. */
export interface DiscoveryFileGroupType {
  label: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  findings: number;
  estimated: number | null;
  weak: boolean;
}

/**
 * One row of the by-file findings view: a file, a set of look-alike files, or
 * an asset (a table). Mirrors api-controller findings-by-file.ts FileGroup.
 */
export interface DiscoveryFileGroup {
  key: string;
  kind: 'file' | 'asset';
  assetName: string | null;
  files: number;
  findings: number;
  openFindings: number;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  lastSeenAt: string;
  sampleRefs: string[];
  localPath: string | null;
  device: string | null;
  deviceUser: string | null;
  scanRoot: string | null;
  rows: number | null;
  types: DiscoveryFileGroupType[];
}

export interface DiscoveryFileGroupPage {
  groups: DiscoveryFileGroup[];
  total: number;
  hiddenWeak: number;
  limit: number;
  offset: number;
}

export interface DiscoveryFileGroupQuery {
  severity?: string;
  status?: string;
  tier?: 'confirmed' | 'review';
  includeWeak?: boolean;
  search?: string;
}

function fileGroupQuery(params: DiscoveryFileGroupQuery): URLSearchParams {
  const q = new URLSearchParams();
  if (params.severity) q.set('severity', params.severity);
  if (params.status) q.set('status', params.status);
  if (params.tier) q.set('tier', params.tier);
  if (params.includeWeak) q.set('includeWeak', 'true');
  if (params.search && params.search.trim()) q.set('search', params.search.trim());
  return q;
}

/** Findings one row per file; totals cover every matching finding, not a page. */
export async function listDiscoveryFindingFileGroups(
  params: DiscoveryFileGroupQuery & { limit?: number; offset?: number } = {},
): Promise<DiscoveryFileGroupPage> {
  const q = fileGroupQuery(params);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/discovery/findings/by-file${suffix}`);
}

/** The findings behind one by-file row, worst first (at most 500). */
export async function listDiscoveryFindingsInFileGroup(
  key: string,
  params: DiscoveryFileGroupQuery = {},
): Promise<{ findings: DiscoveryFinding[]; truncated: boolean }> {
  const q = fileGroupQuery(params);
  q.delete('search');
  q.set('key', key);
  return apiFetch(`/v1/discovery/findings/by-file/findings?${q.toString()}`);
}

/**
 * Discovery asset. Wire shape matches the server's `AssetSchema` at
 * apps/api-controller/src/modules/discovery/schemas.ts:166 - the
 * earlier minimal client interface (5 fields) silently dropped most of
 * the response; same drift class as the Section9 + triggerScan bugs
 * fixed in the Phase-2 hardening pass.
 */
export interface DiscoveryAsset {
  id: string;
  tenantId: string;
  businessUnitId: string | null;
  sourceType: string;
  externalRefs: Record<string, string | null>;
  displayName: string;
  networkZone: string | null;
  countryCode: string | null;
  discoveredVia: string[];
  confidenceScore: number;
  classifications: Array<{ label: string; confidence?: number }>;
  sensitivity: 'critical' | 'high' | 'medium' | 'low' | 'unknown';
  ownerEmail: string | null;
  rowCount: number | null;
  sizeBytes: number | null;
  lastScannedAt: string | null;
  lastAccessedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Release 2.1 item 4: inventory totals counted by the server over EVERY asset.
 * `listDiscoveryAssets` is one page (100 by default): never total it.
 */
export interface AssetInventorySummary {
  totalAssets: number;
  classifiedAssets: number;
  sensitiveAssets: number;
  unownedAssets: number;
  staleAssets: number;
  classifiedRows: number;
  classifiedBytes: number;
  bySourceType: Array<{
    sourceType: string;
    assetCount: number;
    classifiedAssetCount: number;
    sensitiveAssetCount: number;
    totalClassifications: number;
  }>;
  residency: Array<{ country: string; count: number }>;
}

export async function getAssetInventorySummary(staleDays = 30): Promise<AssetInventorySummary> {
  return apiFetch(`/v1/discovery/assets/summary?staleDays=${staleDays}`);
}

/**
 * Release 3.6 item 30: where the sources holding the same personal data
 * disagree. There is no "absent": nothing recorded proves a source's scanned
 * set complete (a table the scanner could not read leaves no asset), so the
 * strongest negative is "not found in scanned assets".
 */
/** What a column is (T-30): a source type, or one database, bucket, share or device. */
export type ReconciliationGranularity = 'source_type' | 'store';

export interface CrossSourceReconciliation {
  /** `sourceType` is the column key: the source type, or the store under `store` granularity. */
  sources: Array<{ sourceType: string; kind?: string; assetCount: number; examinedAssetCount: number }>;
  labels: Array<{
    label: string;
    sourcesPresent: number;
    /** Value evidence in two or more sources, not found in a fully scanned one. */
    presenceGap: boolean;
    countsDisagree: boolean;
    cells: Array<{
      sourceType: string;
      status: 'present' | 'name_only' | 'not_found' | 'partial' | 'not_scanned';
      assetCount: number;
      /** Null when any asset in the cell has no row count: unknown, not zero. */
      rowCount: number | null;
      nameOnlyAssetCount: number;
    }>;
  }>;
  summary: {
    labels: number;
    heldInSeveralSources: number;
    presenceGaps: number;
    countDisagreements: number;
  };
  truncated: boolean;
  /** Absent from a server older than T-30, which only answered by source type. */
  granularity?: ReconciliationGranularity;
  /** More stores than the report compares; the 200 with most assets are shown. */
  sourcesTruncated?: boolean;
  /** The view holds only the caller's business units (and assets with none). */
  businessUnitScoped: boolean;
  /** When the server computed this answer (it keeps one for 5 minutes). */
  generatedAt: string;
}

/**
 * A 503 problem (`reconciliation-timeout` when the report hit its time
 * ceiling, `reconciliation-busy` when this server is already working out its
 * limit of reports), sent as `application/problem+json`. apiFetch parses that
 * as JSON (isJsonMediaType), so `detail` reaches ApiError.body and is the
 * plain reason the card shows (proved through the real apiFetch in
 * cross-source-reconciliation-api-fetch.test.tsx).
 */
export async function getCrossSourceReconciliation(
  by: ReconciliationGranularity = 'source_type',
): Promise<CrossSourceReconciliation> {
  return apiFetch(`/v1/discovery/assets/reconciliation${by === 'source_type' ? '' : `?by=${by}`}`);
}

/**
 * The same report as a CSV file (T-30 export), saved through an object URL
 * because the request needs the Authorization header (as exportEdgeDevicesCsv).
 * A 503 problem's plain reason reaches the caller as the ApiError message.
 *
 * GET /v1/discovery/assets/reconciliation?format=csv
 */
export async function downloadCrossSourceReconciliationCsv(
  by: ReconciliationGranularity = 'source_type',
): Promise<{ filename: string }> {
  const token = getAuthToken();
  const headers: Record<string, string> = { accept: 'text/csv' };
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/v1/discovery/assets/reconciliation?by=${by}&format=csv`, { headers });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = typeof body?.detail === 'string' ? body.detail : typeof body?.title === 'string' ? body.title : '';
    } catch {
      detail = `${res.status} ${res.statusText}`;
    }
    throw new ApiError(res.status, detail || `Export failed (${res.status})`, null);
  }
  const blob = await res.blob();
  const filename =
    (res.headers.get('content-disposition') ?? '').match(/filename="?([^"]+)"?/i)?.[1] ?? 'reconciliation.csv';
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objUrl), 5_000);
  return { filename };
}

/** T-30 history: one stored reconciliation report (summary counts and new gap labels only). */
export interface ReconciliationSnapshot {
  takenAt: string;
  granularity: ReconciliationGranularity;
  trigger: 'scheduled' | 'on_demand';
  sourceCount: number;
  labelCount: number;
  heldInSeveral: number;
  presenceGaps: number;
  countDisagreements: number;
  truncated: boolean;
  sourcesTruncated: boolean;
  /** Kinds of personal data with a presence gap the previous snapshot did not have. */
  newGapLabels: string[];
  drifted: boolean;
}

/**
 * T-30 history: the caller's own snapshots at one granularity, oldest first.
 *
 * GET /v1/discovery/assets/reconciliation/history?by=
 */
export async function getReconciliationHistory(
  by: ReconciliationGranularity = 'source_type',
  limit = 90,
): Promise<{ granularity: ReconciliationGranularity; snapshots: ReconciliationSnapshot[] }> {
  return apiFetch(`/v1/discovery/assets/reconciliation/history?by=${by}&limit=${limit}`);
}

/** New findings per UTC day and severity, counted by the server over every finding. */
export interface FindingTrendDay {
  day: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export async function getFindingTrend(days = 30): Promise<{ days: FindingTrendDay[] }> {
  return apiFetch(`/v1/discovery/findings/trend?days=${days}`);
}

/**
 * Release 3.3 item C2: the risk-reduction series. One point per IST day;
 * `recorded: false` is a day nothing was snapshotted (a gap, never a zero).
 */
export interface RiskSeriesPoint {
  day: string;
  recorded: boolean;
  open: number | null;
  openCritical: number | null;
  openHigh: number | null;
  opened: number | null;
  resolved: number | null;
  riskScoreAvg: number | null;
  riskScoreTotal: number | null;
  exposedAssets: number | null;
  criticalAssets: number | null;
  highAssets: number | null;
}

export interface RiskSeries {
  dayBoundary: string;
  range: number;
  from: string | null;
  to: string;
  historyStartsOn: string | null;
  points: RiskSeriesPoint[];
  summary: {
    from: string;
    to: string;
    openChange: number;
    /** Change in the summed risk score between the first and last recorded day. */
    riskScoreChange: number | null;
    openedTotal: number;
    resolvedTotal: number;
  } | null;
  openDefinition: string;
  riskFormula: string;
}

export type RiskSeriesRange = 30 | 90 | 365;

export async function getRiskSeries(days: RiskSeriesRange = 30): Promise<RiskSeries> {
  return apiFetch(`/v1/discovery/risk-series?days=${days}`);
}

/** Scan counts over every scan (`listDiscoveryScans` is the 50 newest). */
export async function getScanSummary(): Promise<{
  total: number;
  running: number;
  completed: number;
  failed: number;
  findingsCreated: number;
}> {
  return apiFetch('/v1/discovery/scans/summary');
}

/** Detection dashboard counters over every decision (`listDecisions` is a page). */
export async function getDecisionSummary(): Promise<{
  lastHour: number;
  blocked24h: number;
  byDecision: Record<string, number>;
  perMinute: Array<{ block: number; mask: number; step_up: number }>;
}> {
  return apiFetch('/v1/detection/decisions/summary');
}

/**
 * Activity dashboard numbers over every event and finding. `events` is null
 * when ClickHouse could not be read: show "unknown", never 0.
 */
export async function getActivitySummary(): Promise<{
  uebaFindings: number;
  uebaBySeverity: Record<string, number>;
  uebaLast24h: number;
  earlyWarning24h: number;
  events: {
    eventsLastHour: number;
    blocked24h: number;
    perMinute: Array<{ qps: number; blocked: number }>;
    topActors24h: Array<{ actor: string; queries: number; blocked: number }>;
  } | null;
  /** Absent on controllers older than this field. */
  activityStore?: ActivityStoreStatus;
}> {
  return apiFetch('/v1/activity/summary');
}

export async function listDiscoveryAssets(): Promise<{ assets: DiscoveryAsset[] }> {
  return apiFetch('/v1/discovery/assets');
}

/**
 * Get a single discovery asset. Server response is wrapped as
 * `{ asset: ... }`, so we unwrap here to keep the call-site simple
 * (the alternative - every consumer doing `(await getDiscoveryAsset(id)).asset` -
 * is noise that doesn't justify itself).
 */
export async function getDiscoveryAsset(id: string): Promise<DiscoveryAsset> {
  const wrapped = await apiFetch<{ asset: DiscoveryAsset }>(
    `/v1/discovery/assets/${encodeURIComponent(id)}`,
  );
  return wrapped.asset;
}

/** Item-1: assign a data custodian (owner) to an asset. Returns the updated asset. */
export async function assignAssetOwner(id: string, ownerEmail: string): Promise<DiscoveryAsset> {
  const wrapped = await apiFetch<{ asset: DiscoveryAsset }>(
    `/v1/discovery/assets/${encodeURIComponent(id)}/owner`,
    { method: 'PATCH', body: JSON.stringify({ ownerEmail }) },
  );
  return wrapped.asset;
}

// ── Protected copies (Annexure H Part I 111) ──────────────────────────────

export type ProtectionMethod = 'mask' | 'tokenise' | 'pseudonymise';
/** What the operator asks for a column: a method, or leave it out of the copy. */
export type ColumnChoice = ProtectionMethod | 'exclude';

export interface ProtectedCopyPlan {
  assetId: string;
  /** Null when a copy can be asked for; otherwise why not. */
  unavailableReason: string | null;
  source: { systemKey: string; authority: string; schema: string; table: string } | null;
  connectionRegistered: boolean;
  columns: Array<{ column: string; label: string; defaultMethod: ProtectionMethod }>;
  /** Columns the scan did not classify: copied as they are only on the operator's confirmation. */
  unclassifiedColumns: string[];
  defaultTarget: { schema: string; table: string } | null;
}

export interface ProtectedCopyJob {
  id: string;
  assetId: string;
  sourceKey: string;
  source: { schema: string; table: string };
  target: { schema: string; table: string };
  columns: Array<{ column: string; label: string; method: ProtectionMethod }>;
  excludedColumns: string[];
  unprotectedColumns: string[];
  status: 'running' | 'completed' | 'failed';
  batchSize: number;
  rowsEstimated: number | null;
  rowsCopied: number;
  error: string | null;
  createdBy: string;
  createdAt: string;
  heartbeatAt: string;
  completedAt: string | null;
}

export async function getProtectedCopyPlan(assetId: string): Promise<ProtectedCopyPlan> {
  const r = await apiFetch<{ plan: ProtectedCopyPlan }>(
    `/v1/discovery/assets/${encodeURIComponent(assetId)}/protected-copy-plan`,
  );
  return r.plan;
}

export async function listProtectedCopies(assetId: string): Promise<ProtectedCopyJob[]> {
  const r = await apiFetch<{ jobs: ProtectedCopyJob[] }>(
    `/v1/discovery/assets/${encodeURIComponent(assetId)}/protected-copies`,
  );
  return r.jobs;
}

export async function startProtectedCopy(
  assetId: string,
  input: {
    targetSchema: string;
    targetTable?: string;
    methods: Record<string, ColumnChoice>;
    acknowledgedUnprotected: string[];
  },
): Promise<ProtectedCopyJob> {
  const r = await apiFetch<{ job: ProtectedCopyJob }>(
    `/v1/discovery/assets/${encodeURIComponent(assetId)}/protected-copies`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return r.job;
}

export async function getProtectedCopy(id: string): Promise<ProtectedCopyJob> {
  const r = await apiFetch<{ job: ProtectedCopyJob }>(`/v1/discovery/protected-copies/${encodeURIComponent(id)}`);
  return r.job;
}

export interface RemediationResult {
  action: string;
  executed: boolean;
  /** True when the finding no longer reproduces (re-checked by its own detector). */
  verified: boolean;
  /** Finding status after the loop ('resolved' when verified). */
  status: string;
  detail: string;
}

/**
 * Remediation loop: run a named action on a finding; the server executes the fix,
 * verifies, and auto-closes the finding on success. Phase 1: assign_owner.
 */
export async function remediateFinding(
  id: string,
  action: string,
  input: { ownerEmail?: string } = {},
): Promise<RemediationResult> {
  return apiFetch<RemediationResult>(
    `/v1/discovery/findings/${encodeURIComponent(id)}/remediate`,
    { method: 'POST', body: JSON.stringify({ action, ...input }) },
  );
}

export interface ReverseDsarResponse {
  principalIdentifierHash: string;
  assets: Array<{
    assetId: string;
    sourceType: string;
    displayName: string;
    classifications: string[];
  }>;
  cachedAt: string | null;
  ttlExpiresAt: string | null;
  /**
   * How this answer was produced (FCT-P0-007). `value_matched` means the
   * principal's identifier value was actually observed in these assets;
   * `label_scope` means these are assets that COULD hold an identifier of this
   * kind and would be the same list for any principal of that kind;
   * `no_assets_scanned` means nothing has been scanned, so an empty list is not
   * evidence of absence. The UI must not present the latter two as the first.
   */
  resolution: {
    mode: 'value_matched' | 'label_scope' | 'no_assets_scanned';
    valueIndexPopulated: boolean;
    assetsScanned: number;
    assetsTotal: number;
    detail: string;
  };
  labelLandscape: string[];
  toxicCombinations: Array<{ assetId: string; combination: string[] }>;
  /**
   * Present when FOCTTA core is connected - adds consent receipts +
   * datamap cross-references for the same principal so the operator sees
   * Discovery's view and FOCTTA core's view side-by-side.
   */
  focttaCoreEnrichment?: {
    coreIntegrated: true;
    /** Count of Core's authoritative DSPM-walk assets for the same principal. */
    coreAssetCount: number;
    /** Processing activities Core's RoPA links to this principal. */
    relatedActivities: Array<{
      processingActivityId: string;
      name: string;
      purpose: string;
    }>;
    /** Distinct source systems Core sees for the principal. */
    crossRefSystems: string[];
    /** Count of active consents (status === 'active'). */
    openConsents: number;
    /** Total count of consents (all statuses). */
    consentCount: number;
    /**
     * Full list of consents - active + expired + withdrawn. T1.5.A.2
     * extension lets the UI render a withdrawal-history timeline.
     */
    activeConsents: Array<{
      consentId: string;
      purpose: string;
      lawfulBasis: string;
      status: string;
      grantedAt: string;
      expiresAt?: string;
      withdrawnAt?: string;
      isMinor: boolean;
      receiptHash?: string;
    }>;
    integratedAt: string;
  };
}

export async function reverseDsar(
  principalIdentifier: string,
  identifierKind: 'email' | 'phone' | 'aadhaar' | 'pan' | 'customer_number' | 'custom',
): Promise<ReverseDsarResponse> {
  return apiFetch('/v1/discovery/reverse-dsar', {
    method: 'POST',
    body: JSON.stringify({ principalIdentifier, identifierKind }),
  });
}

// ── Threat signatures (malware YARA / hash catalogue) ──

/** Built-in catalogue entry - METADATA ONLY (rule bodies never leave the pod). */
export interface BuiltinThreatSignature {
  name: string;
  family: string;
  category: string;
  description: string;
}

/** A tenant-authored YARA rule or known-bad hash. */
export interface CustomThreatSignature {
  id: string;
  kind: 'yara' | 'hash';
  name: string;
  family: string | null;
  value: string;
  enabled: boolean;
  createdByUserId: string | null;
  createdAt: string | null;
}

export interface ThreatSignaturesResponse {
  builtin: BuiltinThreatSignature[];
  custom: CustomThreatSignature[];
}

export async function listThreatSignatures(): Promise<ThreatSignaturesResponse> {
  return apiFetch('/v1/discovery/threat-signatures');
}

export async function createThreatSignature(input: {
  kind: 'yara' | 'hash';
  name: string;
  family?: string;
  value: string;
  enabled?: boolean;
}): Promise<{ signature: CustomThreatSignature }> {
  return apiFetch('/v1/discovery/threat-signatures', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateThreatSignature(
  id: string,
  updates: { name?: string; family?: string | null; value?: string; enabled?: boolean },
): Promise<{ signature: CustomThreatSignature }> {
  return apiFetch(`/v1/discovery/threat-signatures/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteThreatSignature(
  id: string,
): Promise<{ signature: CustomThreatSignature }> {
  return apiFetch(`/v1/discovery/threat-signatures/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ── Exact Data Match (salted-hash EDM) ──
// The browser salts + SHA-256-hashes each value locally; only the salt + hex
// digests cross the wire. The server stores hashes and looks them up - it
// never sees plaintext.

/** How values are normalised before hashing (Tata AIA 3.6 T-19); see edmNormalise.ts. */
export type EdmNormalisation = 'exact' | 'case_insensitive' | 'alphanumeric';

export interface EdmDatasetSummary {
  id: string;
  name: string;
  fieldLabel: string;
  rowCount: number;
  createdAt: string;
  /** Off = kept but matched nowhere (T-19). Absent from an older controller = on. */
  enabled?: boolean;
  normalisation?: EdmNormalisation;
}

/** Single-dataset shape - carries the salt so the browser can re-hash a test
 *  value before calling /match. */
export interface EdmDatasetDetail extends EdmDatasetSummary {
  salt: string;
}

export interface CreateEdmDatasetResult {
  datasetId: string;
  rowCount: number;
}

export interface EdmMatchResult {
  matched: boolean;
  datasetName: string;
}

export async function listEdmDatasets(): Promise<EdmDatasetSummary[]> {
  return apiFetch('/v1/discovery/edm/datasets');
}

export async function getEdmDataset(id: string): Promise<EdmDatasetDetail> {
  return apiFetch(`/v1/discovery/edm/datasets/${encodeURIComponent(id)}`);
}

export async function createEdmDataset(input: {
  name: string;
  fieldLabel: string;
  salt: string;
  hashes: string[];
  normalisation?: EdmNormalisation;
}): Promise<CreateEdmDatasetResult> {
  return apiFetch('/v1/discovery/edm/datasets', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function deleteEdmDataset(id: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/v1/discovery/edm/datasets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function setEdmDatasetEnabled(id: string, enabled: boolean): Promise<{ id: string; enabled: boolean }> {
  return apiFetch(`/v1/discovery/edm/datasets/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

export async function matchEdmValue(
  datasetId: string,
  valueHash: string,
): Promise<EdmMatchResult> {
  return apiFetch('/v1/discovery/edm/match', {
    method: 'POST',
    body: JSON.stringify({ datasetId, valueHash }),
  });
}

// ── Trained classifiers (release 3.6 #19) ──
// Train on labelled example values; only the trained model is stored.
/** Counts and ratios only: never a value (T-19, trained-classifiers/model.ts). */
export interface TrainedAccuracyReport {
  version: number;
  evaluatedAt: string;
  positives: number;
  negatives: number;
  skipped: number;
  truePositives: number;
  falseNegatives: number;
  falsePositives: number;
  trueNegatives: number;
  precision: number | null;
  recall: number | null;
  f1: number | null;
  columnConfidence: { positives: number | null; negatives: number | null };
  positivesClearScanFloor: boolean;
  negativesClearScanFloor: boolean;
}

export interface TrainedClassifierSummary {
  id: string;
  name: string;
  label: string;
  exampleCount: number;
  createdAt: string;
  /** T-19 fields; absent from an older controller. */
  version?: number;
  enabled?: boolean;
  accuracy?: TrainedAccuracyReport | null;
  updatedAt?: string | null;
}

export interface TrainedClassifierHistory {
  current: TrainedClassifierSummary;
  superseded: Array<{
    version: number;
    exampleCount: number;
    accuracy: TrainedAccuracyReport | null;
    trainedAt: string;
    supersededAt: string;
  }>;
}

export async function listTrainedClassifiers(): Promise<TrainedClassifierSummary[]> {
  return apiFetch('/v1/discovery/trained-classifiers');
}

export async function trainClassifier(input: {
  name: string;
  examples: string[];
}): Promise<TrainedClassifierSummary> {
  return apiFetch('/v1/discovery/trained-classifiers', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function deleteTrainedClassifier(id: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/v1/discovery/trained-classifiers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function retrainClassifier(id: string, examples: string[]): Promise<TrainedClassifierSummary> {
  return apiFetch(`/v1/discovery/trained-classifiers/${encodeURIComponent(id)}/retrain`, {
    method: 'POST',
    body: JSON.stringify({ examples }),
  });
}

export async function setTrainedClassifierEnabled(id: string, enabled: boolean): Promise<{ id: string; enabled: boolean }> {
  return apiFetch(`/v1/discovery/trained-classifiers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

export async function evaluateTrainedClassifier(
  id: string,
  input: { positives: string[]; negatives: string[] },
): Promise<{ report: TrainedAccuracyReport; stored: boolean }> {
  return apiFetch(`/v1/discovery/trained-classifiers/${encodeURIComponent(id)}/evaluate`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getTrainedClassifierHistory(id: string): Promise<TrainedClassifierHistory> {
  return apiFetch(`/v1/discovery/trained-classifiers/${encodeURIComponent(id)}/versions`);
}

export async function restoreTrainedClassifierVersion(id: string, version: number): Promise<TrainedClassifierSummary> {
  return apiFetch(
    `/v1/discovery/trained-classifiers/${encodeURIComponent(id)}/versions/${encodeURIComponent(String(version))}/restore`,
    { method: 'POST' },
  );
}

// ── DPDPA Section 9 - verifiable parental consent ──
// Discovery detects children's data (minor_dob classifier + section_9_minor_data
// findings); these calls RECORD + track the verifiable parental consent Section 9
// mandates before that data may be processed.

export type ParentalConsentVerificationMethod =
  | 'digilocker'
  | 'id_document'
  | 'existing_kyc'
  | 'manual';

export type ParentalConsentStatus = 'verified' | 'pending' | 'revoked';

export interface ParentalConsentRecord {
  id: string;
  tenantId: string;
  /** Opaque/hashed reference to the minor (e.g. a KYC id). Never plaintext PII. */
  minorRef: string;
  guardianName: string | null;
  verificationMethod: ParentalConsentVerificationMethod;
  status: ParentalConsentStatus;
  verifiedAt: string | null;
  verifiedByUserId: string | null;
  expiresAt: string | null;
  /** sha256 (hex) of the canonical consent fields - tamper-evident receipt. */
  receiptHash: string | null;
  notes: string | null;
  createdAt: string | null;
}

export interface ParentalConsentSummary {
  verified: number;
  pending: number;
  revoked: number;
}

export interface ListParentalConsentsResponse {
  records: ParentalConsentRecord[];
  summary: ParentalConsentSummary;
}

export interface RecordParentalConsentInput {
  minorRef: string;
  guardianName?: string;
  verificationMethod: ParentalConsentVerificationMethod;
  status?: ParentalConsentStatus;
  /** ISO-8601 datetime. */
  expiresAt?: string;
  notes?: string;
}

export async function listParentalConsents(): Promise<ListParentalConsentsResponse> {
  return apiFetch('/v1/discovery/section-9/consents');
}

export async function recordParentalConsent(
  input: RecordParentalConsentInput,
): Promise<ParentalConsentRecord> {
  const wrapped = await apiFetch<{ record: ParentalConsentRecord }>(
    '/v1/discovery/section-9/consents',
    { method: 'POST', body: JSON.stringify(input) },
  );
  return wrapped.record;
}

export async function updateParentalConsent(
  id: string,
  updates: {
    status?: ParentalConsentStatus;
    guardianName?: string | null;
    expiresAt?: string | null;
    notes?: string | null;
  },
): Promise<ParentalConsentRecord> {
  const wrapped = await apiFetch<{ record: ParentalConsentRecord }>(
    `/v1/discovery/section-9/consents/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(updates) },
  );
  return wrapped.record;
}

export async function deleteParentalConsent(id: string): Promise<ParentalConsentRecord> {
  const wrapped = await apiFetch<{ record: ParentalConsentRecord }>(
    `/v1/discovery/section-9/consents/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  return wrapped.record;
}

// ── Activity ──

export interface UebaFinding {
  id: string;
  actorUserId: string;
  metric: string;
  rule: string;
  observed: string;
  expectedMean: string;
  expectedStddev: string;
  zScore: string;
  baselineSampleSize: number;
  windowSince: string;
  windowUntil: string;
  detectedAt: string;
  status: string;
}

export async function listUebaFindings(): Promise<{ findings: UebaFinding[] }> {
  return apiFetch('/v1/activity/ueba/findings');
}

// (Duplicate SonarFinding + listSonarFindings declarations previously
// lived here. Canonical definitions now live in the Activity block
// further down, with the wire shape aligned to the server's response
// from listSonarFindings.)

// ── Detection ──

/**
 * DDR decision row. Wire shape matches Drizzle's `$inferSelect` on
 * `ddr_decisions` (apps/api-controller/src/shared/database/schema/
 * detection.ts:57) - server returns camelCase keys via Drizzle's
 * column-name-to-TS-key mapping. Previous client type used snake_case
 * which never matched the wire - caught during Phase 4 wiring.
 */
export interface Decision {
  id: string;
  tenantId: string;
  ruleId: string | null;
  triggerType: 'discovery_finding' | 'activity_event' | 'combined' | string;
  triggerId: string | null;
  decision: 'allow' | 'mask' | 'alert' | 'step_up' | 'break_glass' | 'quarantine' | 'block' | string;
  decisionExplanation: string | null;
  tripleRedundancyVotes: Array<{ engine: string; vote: string; confidence: number }>;
  playbookId: string | null;
  playbookStatus: string | null;
  relatedFindingId: string | null;
  relatedEventId: string | null;
  relatedBreachId: string | null;
  requestId: string | null;
  decidedAt: string;
}

export async function listDecisions(
  params: { decision?: string; kind?: 'hourly'; limit?: number } = {},
): Promise<{ decisions: Decision[] }> {
  const q = new URLSearchParams();
  if (params.decision) q.set('decision', params.decision);
  // Issue #314: the hourly alerts of observed sources only (request id obsb_).
  if (params.kind) q.set('kind', params.kind);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/detection/decisions${suffix}`);
}

// ── Shadow AI (Bank I-162, I-165, III-71) ──
//
// AI service use read from web proxy logs. Backed by /v1/detection/shadow-ai/*.
// An unapproved service raises a DDR decision (trigger 'shadow_ai', 'alert');
// the usage row carries the user, the volume and the time.

export interface AiService {
  key: string;
  name: string;
  vendor: string | null;
  domains: string[];
  approved: boolean;
  /** Bank I-162: the service's own action while unapproved; null follows the tenant default. */
  unapprovedAction: 'alert' | 'block' | null;
  /** allow (approved), alert, or block (the customer's proxy refuses it). */
  action: 'allow' | 'alert' | 'block';
  source: 'built_in' | 'built_in_changed' | 'tenant';
  updatedAt: string | null;
}

export interface ShadowAiUsage {
  id: string;
  serviceKey: string;
  serviceName: string;
  user: string;
  usageDay: string;
  approved: boolean;
  requestCount: number;
  bytesOut: number;
  bytesIn: number;
  firstSeenAt: string;
  lastSeenAt: string;
  source: string;
  /** Bank I-162: requests the proxy refused, and requests with no outcome in the log. */
  refusedCount?: number;
  outcomeUnknownCount?: number;
  blockedByRule?: boolean;
  decisionId: string | null;
  requestId: string | null;
}

/** Bank I-162: the boundary rules, the blocklist the proxy enforces, and which proxies fetch it. */
export interface AiBoundary {
  blockUnapproved: boolean;
  updatedAt: string | null;
  services: Array<{ key: string; name: string; approved: boolean; unapprovedAction: 'alert' | 'block' | null; action: 'allow' | 'alert' | 'block'; domains: string[] }>;
  blocklist: {
    version: string;
    domainCount: number;
    serviceCount: number;
    exceptions: Array<{ domain: string; serviceKey: string; under: string }>;
    /** Blocked domains under an exception: the proxy denies them before the exceptions apply. Absent from a controller older than this card. */
    nested?: Array<{ domain: string; serviceKey: string; under: string }>;
    /** Allowed domains under a nested block: the proxy refuses them anyway. Absent from a controller older than this card. */
    overridden?: Array<{ domain: string; serviceKey: string; under: string }>;
  };
  proxies: Array<{ credentialId: string; instanceLabel: string; lastFetchedAt: string; lastVersion: string; lastFormat: string; fetchCount: number; stale: boolean; behind: boolean }>;
  /** What is enforced where, word for word from the API. */
  enforcement: string;
}

export async function getAiBoundary(): Promise<{ boundary: AiBoundary }> {
  return apiFetch('/v1/detection/shadow-ai/boundary');
}

export async function setAiBoundary(blockUnapproved: boolean): Promise<{ boundary: AiBoundary }> {
  return apiFetch('/v1/detection/shadow-ai/boundary', { method: 'PUT', body: JSON.stringify({ blockUnapproved }) });
}

/** Sets what happens to an unapproved service: alert, block, or null for the tenant default. */
export async function setAiServiceAction(key: string, unapprovedAction: 'alert' | 'block' | null): Promise<{ service: AiService }> {
  return apiFetch(`/v1/detection/shadow-ai/services/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({ approved: false, unapprovedAction }),
  });
}

export async function listAiServices(): Promise<{ services: AiService[] }> {
  return apiFetch('/v1/detection/shadow-ai/services');
}

export async function setAiServiceApproved(key: string, approved: boolean): Promise<{ service: AiService }> {
  return apiFetch(`/v1/detection/shadow-ai/services/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({ approved }),
  });
}

export async function listShadowAiUsage(params: { approved?: boolean; limit?: number } = {}): Promise<{ usage: ShadowAiUsage[] }> {
  const q = new URLSearchParams();
  if (params.approved !== undefined) q.set('approved', String(params.approved));
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/detection/shadow-ai/usage${suffix}`);
}

// ── DLP, UEBA and EDR alerts (Bank II-11) ──
//
// Alerts the customer's own tools raised, ingested into Detection. Backed by
// /v1/detection/external-alerts. Each alert raises a DDR decision (trigger
// 'external_alert', 'alert'); the counts here carry no user or device.

export interface ExternalAlertSource {
  sourceKind: 'dlp' | 'ueba' | 'edr';
  vendor: string;
  /** CEF and LEEF: the line header's "vendor/product"; null for the JSON formats. */
  sourceProduct: string | null;
  alerts24h: number;
  alerts7d: number;
  alerts30d: number;
  findings7d: number;
  awaitingFinding: number;
  lastReceivedAt: string | null;
  lastOccurredAt: string | null;
  /** Nothing received from this tool in the last 24 hours: ingest may have stopped. */
  stale: boolean;
}

/** Audit events and notifications of earlier ingests not delivered yet. */
export interface ExternalAlertDeliveryBacklog {
  auditEventsPending: number;
  notificationsPending: number;
  oldestPendingAt: string | null;
}

export interface ExternalAlertBatch {
  receivedAt: string;
  format: string;
  received: number;
  accepted: number;
  alreadyKnown: number;
  rejected: number;
  outOfScope: number;
  findingsRaised: number;
  /** Alerts already known that came again with a higher severity (each raised a new finding). */
  escalated: number;
}

export async function listExternalAlertSources(): Promise<{
  sources: ExternalAlertSource[];
  recentBatches: ExternalAlertBatch[];
  deliveryBacklog: ExternalAlertDeliveryBacklog;
}> {
  return apiFetch('/v1/detection/external-alerts/sources');
}

// ── Endpoint downloads and removable media (migration 0197) ──
//
// The policy is on the fleet policy (/v1/edge/fleet-policy/media); the DDR
// findings the background sweep raised are /v1/detection/endpoint-media.
// Detection only: FOCTTA blocks nothing on the endpoint. The MDM export lists
// the laptops holding restricted data and the setting that denies writes to
// removable disks, for the customer's MDM to apply.

export type EndpointMediaKind = 'removable_media' | 'download';

export interface EndpointMediaPolicyRow {
  scopeType: 'tenant' | 'business_unit';
  businessUnitId: string | null;
  businessUnitName: string | null;
  /** null = not set at this level (inherits). */
  restrictedClasses: string[] | null;
  scanRemovable: boolean | null;
  watchDownloads: boolean | null;
  updatedBy: string | null;
  updatedAt: string | null;
  editable: boolean;
}

export interface EndpointMediaStatement {
  enforced: string;
  detected: string[];
  downloads: string;
}

export async function getEndpointMediaPolicy(): Promise<{
  tenant: EndpointMediaPolicyRow | null;
  businessUnits: EndpointMediaPolicyRow[];
  statement: EndpointMediaStatement;
}> {
  return apiFetch('/v1/edge/fleet-policy/media');
}

export async function setEndpointMediaPolicy(input: {
  scopeType: 'tenant' | 'business_unit';
  businessUnitId?: string;
  restrictedClasses?: string[] | null;
  scanRemovable?: boolean | null;
  watchDownloads?: boolean | null;
  reason: string;
}): Promise<{ policy: EndpointMediaPolicyRow }> {
  return apiFetch('/v1/edge/fleet-policy/media', { method: 'PUT', body: JSON.stringify(input) });
}

export interface EndpointMediaMdmExport {
  devices: Array<{ deviceKey: string; businessUnitIds: string[]; restrictedFiles: number; classes: string[] }>;
  truncated: boolean;
  settings: Array<{ platform: 'windows' | 'linux'; channel: string; setting: string; value: string; effect: string }>;
  note: string;
}

export async function getEndpointMediaMdmExport(): Promise<EndpointMediaMdmExport> {
  return apiFetch('/v1/edge/fleet-policy/media/mdm-export');
}

export interface EndpointMediaSummary {
  counts: Array<{ kind: EndpointMediaKind; last24h: number; last7d: number; last30d: number; total: number }>;
  recent: Array<{
    kind: EndpointMediaKind;
    dataClasses: string[];
    severity: string;
    businessUnitId: string | null;
    businessUnitName: string | null;
    assetId: string;
    requestId: string | null;
    detectedAt: string;
  }>;
  coverage: { scanningRemovable: number; reporting: number; laptops: number };
  sweep: { lastPassAt: string | null; readThrough: string | null };
  deliveryBacklog: { pending: number; oldestAt: string | null };
}

export async function getEndpointMediaSummary(): Promise<EndpointMediaSummary> {
  return apiFetch('/v1/detection/endpoint-media');
}

// ── Frontier (Phase 5) ──
//
// Sovereign-tier opt-in. Backed by /v1/frontier/* and gated by
// product='foctta_platform' + feature.frontier_tier=true.

export interface FrontierHealth {
  status: 'ok' | 'degraded';
  surfaces: Record<string, string>;
}

export async function getFrontierHealth(): Promise<FrontierHealth> {
  return apiFetch('/v1/frontier/health');
}

export interface SmpcBenchmark {
  key: string;
  description: string;
  aggregationFn: 'sum' | 'avg' | 'p50' | 'p95' | 'count';
  targetIndustry: string;
}

export async function listSmpcBenchmarks(): Promise<{ benchmarks: SmpcBenchmark[] }> {
  return apiFetch('/v1/frontier/smpc/benchmarks');
}

export interface SmpcRoundtripResult {
  tenantId: string;
  benchmarkKey: string;
  revealed: {
    aggregate: number;
    aggregationFn: string;
    participantCount: number;
  };
  payloadHash: string;
}

export async function runSmpcDemoRoundtrip(
  benchmarkKey: string,
  secrets: number[],
): Promise<SmpcRoundtripResult> {
  return apiFetch('/v1/frontier/smpc/demo-roundtrip', {
    method: 'POST',
    body: JSON.stringify({ benchmarkKey, secrets }),
  });
}

export interface ByoLlmProvider {
  id: string;
  displayName: string;
  authMethod: string;
  defaultBaseUrl?: string;
  maxContextWindowTokens: number;
  supportsPromptCaching: boolean;
  supportsToolUse: boolean;
  supportsStreaming: boolean;
}

export async function listByoLlmProviders(): Promise<{ providers: ByoLlmProvider[] }> {
  return apiFetch('/v1/frontier/byo-llm/providers');
}

export interface EbpfTracingPolicy {
  name: string;
  description: string;
  syscalls: string[];
  risk: 'low' | 'medium' | 'high' | 'critical';
}

export async function listEbpfTracingPolicies(): Promise<{ policies: EbpfTracingPolicy[] }> {
  return apiFetch('/v1/frontier/sensors/tracing-policies');
}

// P2 OBS-16 (2026-05-26) - tenant-scoped Frontier list endpoints.
// These return real rows from the per-tenant tables (federated_rounds,
// smpc_sessions, pq_key_pairs, ebpf_sensors, byo_llm_keys) so Frontier
// pages can render real demo data instead of hardcoded mocks.

export interface FederatedRoundRow {
  id: string;
  tenantId: string;
  modelKind: string;
  roundNumber: number;
  coordinatorOrigin: string;
  secureAggregationProtocol: string;
  differentialPrivacyEpsilon: string | null;
  differentialPrivacyDelta: string | null;
  targetParticipants: number;
  actualParticipants: number;
  minParticipantsForRelease: number;
  status: string;
  aggregatedUpdateRef: string | null;
  metadata: Record<string, unknown>;
  startedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface FederatedParticipantRow {
  id: string;
  tenantId: string;
  roundId: string;
  encryptedUpdateRef: string;
  sampleCount: number;
  noiseSeedHash: string | null;
  submittedAt: string;
  accepted: boolean;
  rejectionReason: string | null;
  createdAt: string;
}
export async function listFederatedRounds(
  limit = 20,
): Promise<{ rounds: FederatedRoundRow[]; participants: FederatedParticipantRow[] }> {
  return apiFetch(`/v1/frontier/federated/rounds?limit=${limit}`);
}

export interface SmpcSession {
  id: string;
  tenantId: string;
  benchmarkKey: string;
  description: string | null;
  targetIndustry: string | null;
  minParticipants: number;
  actualParticipants: number;
  aggregationFn: string;
  status: string;
  revealedResultJson: Record<string, unknown> | null;
  coordinatorOrigin: string;
  metadata: Record<string, unknown>;
  openedAt: string;
  closedAt: string | null;
}
export async function listSmpcSessions(
  limit = 20,
): Promise<{ sessions: SmpcSession[] }> {
  return apiFetch(`/v1/frontier/smpc/sessions?limit=${limit}`);
}

export interface PqKeyPairRow {
  id: string;
  tenantId: string;
  purpose: string;
  algorithm: string;
  publicKeyB64: string;
  privateKeyKmsRef: string;
  libraryProvider: string;
  rotatedFromKeyId: string | null;
  activeFrom: string;
  rotatedAt: string | null;
  revokedAt: string | null;
}
export async function listPqKeyPairs(
  limit = 20,
): Promise<{ keyPairs: PqKeyPairRow[] }> {
  return apiFetch(`/v1/frontier/pq/key-pairs?limit=${limit}`);
}

export interface EbpfSensorRow {
  id: string;
  tenantId: string;
  sensorName: string;
  nodeOrigin: string;
  kernelVersion: string;
  tetragonVersion: string;
  captureMode: string;
  tracingPolicy: string;
  spiffeId: string;
  status: string;
  eventsLast24h: number;
  metadata: Record<string, unknown>;
  registeredAt: string;
  lastSeenAt: string | null;
}
export async function listEbpfSensors(
  limit = 50,
): Promise<{ sensors: EbpfSensorRow[] }> {
  return apiFetch(`/v1/frontier/sensors/ebpf?limit=${limit}`);
}

export interface ByoLlmKey {
  id: string;
  tenantId: string;
  provider: string;
  modelId: string;
  endpointUrl: string | null;
  apiKeyKmsRef: string;
  contextWindowTokens: number;
  monthlyTokenBudget: number | null;
  purposeTag: string;
  status: string;
}
export async function listByoLlmKeys(
  limit = 20,
): Promise<{ keys: ByoLlmKey[] }> {
  return apiFetch(`/v1/frontier/byo-llm/keys?limit=${limit}`);
}

// P2 OBS-15 (2026-05-27) - tenant-data-grounded chat surface.
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
export interface ChatResponse {
  content: string;
  liveAi: boolean;
  modelHint: string | null;
  groundedOn: string[];
}
export async function frontierChat(
  messages: ReadonlyArray<ChatMessage>,
): Promise<ChatResponse> {
  return apiFetch('/v1/frontier/chat', {
    method: 'POST',
    body: JSON.stringify({ messages }),
  });
}

// Federated learning
export interface RecruitRoundInput {
  modelKind: 'classifier_tier1' | 'classifier_tier2_distilbert_indic' | 'ueba_baseline' | 'sonar_signature';
  coordinatorOrigin: string;
  secureAggregationProtocol?: 'flwr_v1_secagg_plus' | 'flwr_v1_secagg' | 'fedavg_dp';
  differentialPrivacyEpsilon?: number;
  differentialPrivacyDelta?: number;
  targetParticipants: number;
  minParticipantsForRelease: number;
}

export interface FederatedRound {
  roundId: string;
  modelKind: string;
  protocol: string;
  targetParticipants: number;
  status: string;
  createdAt: string;
}

export async function recruitFederatedRound(input: RecruitRoundInput): Promise<FederatedRound> {
  return apiFetch('/v1/frontier/federated/rounds', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// PQ crypto
export type PqAlgorithm =
  | 'crystals_kyber_768'
  | 'crystals_kyber_1024'
  | 'crystals_dilithium_2'
  | 'crystals_dilithium_3'
  | 'crystals_dilithium_5';
export type PqPurpose = 'audit_chain' | 'breach_notification' | 'tokenisation_kem' | 'workload_identity';
export type PqProvider = 'liboqs' | 'cloudflare_circl' | 'aws_kms_pq_pre_release' | 'surrogate_test_only';

export interface GeneratePqKeyPairInput {
  algorithm: PqAlgorithm;
  purpose: PqPurpose;
  libraryProvider?: PqProvider;
}

export interface PqKeyPair {
  keyId: string;
  algorithm: PqAlgorithm;
  purpose: PqPurpose;
  publicKeyB64: string;
  privateKeyKmsRef: string;
  libraryProvider: string;
  createdAt: string;
}

export async function generatePqKeyPair(input: GeneratePqKeyPairInput): Promise<PqKeyPair> {
  return apiFetch('/v1/frontier/pq/key-pairs', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Compliance replay
export type Regulator =
  | 'dpdpa' | 'gdpr' | 'ccpa' | 'hipaa' | 'pci_dss' | 'rbi_dpf' | 'sebi_csscb'
  | 'uidai_2025_circular' | 'nbfc_dpf' | 'sec_5b' | 'glba' | 'pipeda'
  | 'pdpa_sg' | 'lgpd_brazil' | 'pipl';

export interface ReplayEvent {
  id: string;
  eventType: 'finding' | 'activity_event' | 'decision' | 'breach_incident';
  occurredAt: string;
  piiCategories: string[];
  crossBorder: boolean;
  involvesMinor: boolean;
  originalDecision?: string;
  originalNotificationClockHours?: number;
  metadata?: Record<string, unknown>;
}

export interface RunReplayInput {
  replayName: string;
  fromTimestamp: string;
  toTimestamp: string;
  targetRegulators: Regulator[];
  events: ReplayEvent[];
}

export interface ReplayDelta {
  eventId: string;
  regulator: Regulator;
  originalDecision?: string;
  counterFactualDecision: string;
  originalClockHours?: number;
  counterFactualClockHours: number;
  clockChangedBy: number;
  section9EquivalentTriggered: boolean;
  newHardBlock: boolean;
}

export interface RunReplayResult {
  replayId: string;
  replayName: string;
  perRegulator: Array<{
    regulator: Regulator;
    deltas: ReplayDelta[];
    summary: {
      clockTightenings: number;
      clockLoosens: number;
      newHardBlocks: number;
    };
  }>;
}

export async function runComplianceReplay(input: RunReplayInput): Promise<RunReplayResult> {
  return apiFetch('/v1/frontier/replay/run', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// T1.5 (2026-05-29) - historical replay: pulls real activity_events +
// ddr_decisions + breach_incidents over the window from the tenant's
// own stores rather than supplying synthetic events. Same result shape.
export interface RunHistoricalReplayInput {
  replayName: string;
  fromTimestamp: string;
  toTimestamp: string;
  targetRegulators: Regulator[];
  hypothesis?: string;
  maxEventsPerSource?: number;
}

export async function runHistoricalComplianceReplay(
  input: RunHistoricalReplayInput,
): Promise<RunReplayResult> {
  return apiFetch('/v1/frontier/replay/historical', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── Super Admin (Platform-ops only) ──

export interface TenantHealth {
  tenantId: string;
  tenantName: string;
  industryAnchor: string;
  decisions24h: number;
  blocks24h: number;
  stepUps24h: number;
  masks24h: number;
  findingsOpen: number;
  findingsCritical: number;
  uebaOpenAlerts: number;
  breachIncidentsOpen: number;
  topRuleFiring: string;
  licenseTier: string;
  lastSeenAt: string;
}

export interface CrossTenantHealth {
  generatedAt: string;
  /** false = nothing was aggregated; show "not measured", never zeros. */
  measured?: boolean;
  /** Reason shown when `measured` is false. */
  note?: string;
  tenantCount: number;
  totals: { decisions24h: number; blocks24h: number; findingsOpen: number; breachesOpen: number };
  tenants: TenantHealth[];
  fleetAlerts: Array<{
    severity: 'critical' | 'high' | 'medium';
    title: string;
    affectedTenantCount: number;
    rationale: string;
  }>;
}

export async function getSuperAdminHealth(): Promise<CrossTenantHealth> {
  return apiFetch('/v1/super-admin/ddr/health');
}

export interface CuratedRule {
  ruleId: string;
  name: string;
  category: 'discovery' | 'activity' | 'detection' | 'frontier';
  industryAnchors: string[];
  severity: 'critical' | 'high' | 'medium' | 'low';
  rationale: string;
  regoBodyExcerpt: string;
  curatedBy: string;
  curatedAt: string;
  distributionCount: number;
}

export async function listMasterRules(): Promise<{ rules: CuratedRule[] }> {
  return apiFetch('/v1/super-admin/ddr/rule-library');
}

export async function promoteMasterRule(ruleId: string, tenantId: string): Promise<{
  ruleId: string; tenantId: string; promotedAt: string; distributionCount: number;
  /** Always false today: promotion writes nothing to the tenant. */
  applied?: boolean;
}> {
  return apiFetch(`/v1/super-admin/ddr/rule-library/${encodeURIComponent(ruleId)}/promote`, {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  });
}

// ── FOCTTA core integration (Connect wizard) ──

export interface FocttaCoreIntegrationStatus {
  connected: boolean;
  integrationId?: string;
  displayName?: string;
  baseUrl?: string;
  remoteTenantId?: string;
  status?: 'pending' | 'active' | 'paused' | 'failed' | 'revoked';
  lastSuccessAt?: string;
  lastFailureAt?: string;
  consecutiveFailures?: number;
  /** Release 8: the health of discovery.scan_completed reports to Consent, kept apart from the counters above. */
  scanCompleted?: {
    lastSuccessAt: string | null;
    lastFailureAt: string | null;
    consecutiveFailures: number;
    lastError: string | null;
    /** Finished scans never reported because they finished more than 7 days before they could be sent. */
    expiredUnsent: number;
    lastExpiredAt: string | null;
  };
  subscribedEventTypes?: string;
  featureFlags?: Record<string, boolean>;
}

export interface ConnectFocttaCoreInput {
  displayName?: string;
  baseUrl: string;
  remoteTenantId: string;
  apiKey: string;
  hmacEgressSecret: string;
  scimToken?: string;
  inboundWebhookSecret: string;
  subscribedEventTypes?: string;
  featureFlags?: Record<string, boolean>;
}

export interface ConnectFocttaCoreResult {
  integrationId: string;
  status: string;
  probe: Array<{ check: string; ok: boolean; detail?: string }>;
  message: string;
}

export async function getFocttaCoreStatus(): Promise<FocttaCoreIntegrationStatus> {
  return apiFetch('/v1/admin/integrations/foctta-core');
}

export async function connectFocttaCore(input: ConnectFocttaCoreInput): Promise<ConnectFocttaCoreResult> {
  return apiFetch('/v1/admin/integrations/foctta-core/connect', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── BFSI-grade single-paste Connect (v1) ─────────────────────────
// See docs/integration-spec/FOCTTA-ENROLLMENT-TOKEN-V1.md
export interface ConnectByTokenInput {
  enrollmentToken: string;
  discoveryBaseUrl?: string;
}

export interface ConnectByTokenResult extends ConnectFocttaCoreResult {
  displayName: string;
  baseUrl: string;
  remoteTenantId: string;
  connectorInstanceId: string;
  subscribedEventTypes: string;
  scopes: string[];
  degradedChecks?: string[];
}

export async function connectFocttaCoreByToken(input: ConnectByTokenInput): Promise<ConnectByTokenResult> {
  return apiFetch('/v1/admin/integrations/foctta-core/connect-by-token', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function testFocttaCore(): Promise<{
  checks: Array<{ check: string; ok: boolean; detail?: string }>;
  allOk: boolean;
}> {
  return apiFetch('/v1/admin/integrations/foctta-core/test', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function disconnectFocttaCore(reason: string): Promise<{
  message: string;
  revokedIntegrationId: string;
}> {
  return apiFetch('/v1/admin/integrations/foctta-core/disconnect', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// =====================================================================
// Phase 1 (2026-05-14): client coverage expansion for Phases 2-5 wiring
//
// Adds wrappers for the ~35 backend routes that were uncovered per the
// Phase 1 audit (`docs/poc-roadmap/01-AUDIT-SUMMARY.md §2`). Each function
// matches the route's request/response shape; types reflect the Zod
// schemas from the api-controller side (cross-referenced at function
// definition time, not auto-generated - that's Phase 9).
// =====================================================================

// ── Foundational (10 routes - all previously uncovered) ────────────

/**
 * Wire shape returned by GET /v1/foundational/licenses. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (LicenseSchema).
 *
 * NOTE 2026-05-16 - prior product enum ('foctta_complete' /
 * 'foctta_enterprise' / 'foctta_discovery_only') did not match the server
 * enum ('foctta_platform' / 'discovery' / 'activity' / 'ddr'). Prior shape
 * was also missing tenantId / status / uatToProdPromotedAt /
 * revocationReason / createdAt / updatedAt. Corrected.
 */
export type FoundationalLicenseProduct = 'foctta_platform' | 'discovery' | 'activity' | 'ddr';
export type FoundationalLicenseTier = 'lite' | 'standard' | 'sovereign';
export type FoundationalLicenseStatus = 'active' | 'trial' | 'expired' | 'revoked';

export interface FoundationalLicense {
  id: string;
  tenantId: string;
  product: FoundationalLicenseProduct;
  tier: FoundationalLicenseTier;
  status: FoundationalLicenseStatus;
  activatedAt: string;
  expiresAt: string | null;
  limits: Record<string, number | null>;
  features: Record<string, boolean>;
  procurementRef: string | null;
  uatToProdPromotedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getFoundationalHealth(): Promise<{ status: 'ok'; service: string }> {
  return apiFetch('/v1/foundational/health');
}

export async function listLicenses(): Promise<{ licenses: FoundationalLicense[] }> {
  return apiFetch('/v1/foundational/licenses');
}

/**
 * Wire shape returned by GET /v1/foundational/egress-log. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (EgressLogEntrySchema). Each row records one metadata payload that
 * left the customer's network, with a SHA-256 hash chain so the CISO
 * can verify tamper-evidence via /v1/foundational/egress-log/verify.
 *
 * NOTE 2026-05-16 - the prior shape declared here was a copy/paste of
 * the audit-chain entry shape (eventType/entityType/actorUserId), not
 * the egress-log shape. Corrected as part of Phase 5b item #8 wire-up.
 */
export type EgressPayloadKind =
  | 'discovery_finding'
  | 'activity_event_summary'
  | 'ddr_decision'
  | 'health_check'
  | 'attestation_doc';

export interface EgressLogEntry {
  id: string;
  tenantId: string;
  payloadHash: string;
  payloadSizeBytes: number;
  destinationUrl: string;
  payloadKind: EgressPayloadKind;
  schemaVersion: string;
  bannedFieldsDropped: Record<string, unknown>;
  previousHash: string;
  chainHash: string;
  createdAt: string;
}

// ── Edge fleet (Phase 7 batch A) ──────────────────────────────────────

/**
 * Wire shape returned by GET /v1/edge/fleet. Server-side source:
 * apps/api-controller/src/modules/edge/schemas.ts (EdgePodSummarySchema).
 */
export type EdgeComponent = 'discovery' | 'activity' | 'detection';
/**
 * THE ONE HEALTH VOCABULARY (release 3.1, fleet wave 1). Every edge API that
 * says how a device is doing answers in these words, computed by the same SQL
 * on the controller (apps/api-controller/src/modules/edge/fleet-health.ts,
 * FLEET_STATES). The old words are never emitted any more: `stale` is now
 * `silent`, and `impaired` / `never_worked` are `degraded` with that
 * `healthReason`. Presentation (labels, hints, badge colours): lib/edge-health.ts.
 */
export type EdgeHealthStatus =
  | 'healthy'
  | 'degraded'
  | 'silent'
  | 'down'
  | 'pending'
  | 'decommissioned';

/** Why a device that checks in is degraded. Null for every other state. */
export type EdgeHealthReason = 'impaired' | 'never_worked' | 'agent_reported';

/**
 * Release 3.3 (R3-OI-184): why a Degraded device is degraded, in plain words
 * with the next step. `code` is the agent's own reason (coverage_partial,
 * scan_overdue, ...) when the agent reported the fault, the controller's
 * (impaired, never_worked) otherwise; `detail` is the agent's sentence.
 * Vocabulary: lib/edge-health.ts EDGE_DEGRADED_REASONS (the controller's
 * degraded-reasons.ts, kept equal by a test).
 */
export interface EdgeDegradedReason {
  code: string;
  label: string;
  nextStep: string;
  detail: string | null;
  source: 'agent' | 'controller';
}

/**
 * endpoint = laptop agent, vpcPod = server-side agent, gateway = gateway,
 * service = not metered per device (DSAR connector, agentless collection;
 * release 3.1 wave 2a).
 */
export type EdgeAgentClass = 'endpoint' | 'vpcPod' | 'gateway' | 'service';

/**
 * Every agent type in one registry (release 3.1 wave 2a; the controller's
 * modules/edge/agent-types.ts). The one discriminator of a fleet row; the
 * console's names are in lib/edge-agent-types.ts.
 */
export const EDGE_AGENT_TYPES = [
  'laptop_agent',
  'server_scanner',
  'dam_collector',
  'gateway',
  'dsar_connector',
  'agentless_collector',
] as const;
export type EdgeAgentType = (typeof EDGE_AGENT_TYPES)[number];

/**
 * Who makes a pause of this device real: the agent reads it itself, the
 * controller enforces it on the device's calls, or nothing would (then the
 * console offers no pause and the API refuses one with 409 pause_not_supported).
 */
export type EdgePauseSupport = 'agent' | 'controller' | 'none';

/**
 * Set on decommission (release 3.1 wave 2a): `revoked` = the controller revoked
 * the service credential of a gateway or DSAR connector in the same act;
 * `required` = a DAM collector's reader accounts (and the tenant's ClickHouse
 * writer) must be revoked on their own systems, which an operator confirms;
 * `confirmed` = an operator did. `detail` is what the controller recorded.
 */
export interface EdgeCredentialsRevocation {
  state: 'required' | 'revoked' | 'confirmed';
  detail: Record<string, unknown> | null;
  at: string | null;
}

export interface EdgePodSummary {
  id: string;
  podId: string;
  component: EdgeComponent;
  displayName: string;
  businessUnitId: string | null;
  version: string;
  helmChartVersion: string | null;
  clusterName: string | null;
  namespace: string | null;
  lastHeartbeatAt: string | null;
  stalenessThresholdSeconds: number;
  /**
   * Cadence the AGENT declares for itself. The effective staleness window is
   * GREATEST(stalenessThresholdSeconds, this * 2) - which is why a laptop that
   * reports every 4 hours is no longer permanently `down`.
   */
  heartbeatIntervalSeconds: number | null;
  lagWatermarkSeconds: number | null;
  eventsProcessed24h: number;
  eventsDropped24h: number;
  /**
   * Agent self-measurement (Command Plane Phase 1). NULL means NOT REPORTED,
   * which is not the same as zero - every agent class except the scanner
   * reports nothing today, and rendering those as 0% would show an
   * un-instrumented agent as idle.
   */
  cpuSecondsTotal: number | null;
  cpuPercent: number | null;
  rssMb: number | null;
  measuredAt: string | null;
  /** The one vocabulary; see EdgeHealthStatus. */
  healthStatus: EdgeHealthStatus;
  /** Why a device that checks in is degraded; null for every other state. */
  healthReason: EdgeHealthReason | null;
  /** Release 3.3: the specific reason for Degraded; null for every other state. Absent from an older controller. */
  degradedReason?: EdgeDegradedReason | null;
  agentClass: EdgeAgentClass;
  // ── Release 3.1 wave 2a: every agent type in one registry. Absent from a
  // controller before wave 2a; the console then offers what it always did
  // (all four commands, the pause toggle). ──
  agentType?: EdgeAgentType;
  /** The queued commands this device executes; the console offers only these. */
  supportedCommands?: EdgeCommandName[];
  /** Who makes a pause real; 'none' = no pause is offered. */
  pauseSupport?: EdgePauseSupport;
  /** Why a control is limited or enforced elsewhere; null when everything offered works. */
  controlNote?: string | null;
  /** Credentials a decommission revoked or left to revoke; null when none. */
  credentialsRevocation?: EdgeCredentialsRevocation | null;
  statusDetail: string | null;
  /** What the agent reports it can scan and which credential names it holds. Null: never reported (older agent). */
  capabilities: { scannerKinds: string[]; credentialNames: string[] } | null;
  /**
   * Release 3.2 telemetry item 4: the agent declares `collect_logs`, so its
   * recent log can be pulled. Optional: a controller older than 3.2 does not send it.
   */
  logPull?: boolean;
  /** Release 8 item 11: the agent removes itself on an approved request (declares remove_agent). */
  agentRemoval?: boolean;
  /** Files the last scan did not read. Null: never reported (older agent or no file scanner). */
  lastScanFilesNotRead: number | null;
  /**
   * Release 3.2 telemetry item 2: CPU and memory measured DURING the last
   * scan. Optional: a controller older than 3.2 does not send it; null = the
   * agent did not report it (older than 3.2, or no scan yet).
   */
  lastScanResources?: EdgeScanResources | null;
  decommissionedAt: string | null;
  createdAt: string;
  // ── Release 3.1: what an operator finds a device by. NULL = the agent never
  // reported it (agents before 3.1 send only hostname and OS family); the
  // console renders "not reported", never a blank or a zero. ──
  hostname: string | null;
  osName: string | null;
  osVersion: string | null;
  primaryIp: string | null;
  loggedInUser: string | null;
  serialNumber: string | null;
  /** The wave (install token) the device enrolled on or was moved onto. */
  installTokenId: string | null;
  businessUnitName: string | null;
  /** The stable check-in cadence the health window is built on, in seconds. */
  livenessIntervalSeconds: number | null;
  /** Open reportable findings on this laptop; null = no rollup (not a laptop, or none yet). */
  openFindings: number | null;
  /**
   * Release 3.2 phase 2: where the device's self-update stands and its place
   * in the latest update rollout that included it. Optional: a controller
   * older than 3.2 phase 2 does not send it; null = nothing to show.
   */
  update?: EdgeDeviceUpdate | null;
}

/** A device's self-update, as the agent reported it, and its latest rollout (release 3.2 phase 2). */
export interface EdgeDeviceUpdate {
  /** idle, waiting_window, downloading, verifying, staged, installing, pending_commit, committed, rolled_back, failed, managed_externally. */
  state: string | null;
  /** A stable reason code (SELF-UPDATE.md section 7). */
  reason: string | null;
  targetVersion: string | null;
  previousVersion: string | null;
  route: string | null;
  selfUpdate: string | null;
  trust: string | null;
  rollout: {
    rolloutId: string;
    targetVersion: string;
    rolloutState: string;
    stage: number;
    stageLabel: string;
    /** instructed, updated, failed, rolled_back, managed_externally, stood_down, gone, rollback_requested, reverted, rollback_failed. */
    status: string;
    lastState: string | null;
    lastReason: string | null;
    at: string | null;
  } | null;
}

/** The device list's update filters (release 3.2 phase 2). */
export type EdgeDeviceUpdateFilter = 'behind_target' | 'failed' | 'rolled_back' | 'managed_externally';

/**
 * CPU and memory the agent measured while a scan ran (release 3.2 telemetry
 * item 2). CPU is percent of the WHOLE machine, the unit of the Windows
 * job-object hard cap, so it reads directly against `cpuCeilingPercent`.
 * An absent percentage means the run was too short to measure a rate.
 */
export interface EdgeScanResources {
  cpuAvgPercent?: number;
  cpuPeakPercent?: number;
  memPeakMb?: number;
  cpuSeconds: number;
  wallSeconds: number;
  samples: number;
  /** The window a peak is measured over, seconds. */
  sampleSeconds: number;
  logicalCpus: number;
  /** agent_and_workers | agent_only */
  scope: string;
  /** The CPU ceiling in force during the run, percent of the machine; absent = none known. */
  cpuCeilingPercent?: number;
}

/** What a laptop's last scan read and did not read (release 2.1 item 10). */
export interface EdgeLastScanFiles {
  filesRead: number;
  filesPartlyRead: number;
  /** Of filesRead, the images and scanned PDFs read by the OCR add-on (release 3.3; server scanner only). */
  filesReadByOcr?: number;
  filesNotRead: number;
  notRead: Array<{
    /** needs_extraction (not read - OCR add-on off) | too_large | could_not_open | no_text | read_error | not_reached | folder_unreadable */
    reason: string;
    count: number;
    types: Array<{ ext: string; count: number }>;
    /** Present only when the deployment reports full paths. */
    examples?: string[];
  }>;
  otherTypes: Array<{ ext: string; count: number }>;
  otherTypesTotal: number;
  /** Release 8: of the files counted, those skipped as unchanged since they were last read. */
  filesUnchanged?: number;
  /** Release 8: 'incremental' (only changed files read) or 'full'. Absent from older agents and server scanners. */
  scanMode?: string;
  /** Release 8: why a scan was full (no_state, scheduled, agent_changed, classifier_changed ...). */
  fullScanReason?: string;
}

/**
 * Counts over the WHOLE filtered set, in the one vocabulary (never the page).
 * On the devices list they ignore the state filter, so clicking a state tile
 * does not zero the others. `stale` is the pre-3.1 alias of `silent`.
 */
export interface EdgeFleetSummary {
  total: number;
  healthy: number;
  degraded: number;
  silent: number;
  down: number;
  pending: number;
  decommissioned: number;
  stale: number;
}

export interface EdgePodDetail extends EdgePodSummary {
  decisionRpcP50Ms: number | null;
  decisionRpcP95Ms: number | null;
  decisionRpcP99Ms: number | null;
  metadata: Record<string, unknown>;
  activeOverride: {
    overrideEtag: string;
    patch: Record<string, unknown>;
    pushedAt: string;
    appliedAt: string | null;
  } | null;
  /**
   * What the agent says it is ACTUALLY running (Command Plane Phase 5), as
   * opposed to activeOverride, which is what we ASKED for.
   *
   * null means the agent has never reported one - a pre-Phase-5 build. That is
   * NOT the same as "running defaults", and the UI must say so, because an
   * operator who reads defaults for a silent agent concludes a scan is paused
   * when it is still running.
   */
  effectiveConfig: Record<string, unknown> | null;
  effectiveConfigAt: string | null;
  /** What the last scan did not read, and why. Null: never reported. */
  lastScanFiles: EdgeLastScanFiles | null;
  lastScanFinishedAt: string | null;
  /**
   * Release 3.2 telemetry item 5: every limit the device is under, sent and
   * confirmed side by side. Optional: a controller older than 3.2 does not
   * send it. Empty for an agent type with no scan limits.
   */
  limits?: EdgeLimitRow[];
  /**
   * Release 3.3: the scan hours in force, the level they come from, every
   * level's value and the device's groups. Absent before 3.3, null before 0185.
   */
  scanPolicy?: DeviceScanPolicy | null;
}

/** One limit, what was sent and what the agent confirms (release 3.2 telemetry item 5). */
export interface EdgeLimitRow {
  key: string;
  label: string;
  /** null = nothing was sent. `by`: console | fleet_policy | install | agent_default | agent_reported. */
  sent: { value: string; by: string } | null;
  /** null = the agent did not report it. */
  confirmed: { value: string; detail: string | null } | null;
  /** confirmed | differs | pending | not_reported | not_sent | not_matched | not_enforced */
  state: string;
  note: string | null;
}

/**
 * The legacy fleet list (GET /v1/edge/fleet). Release 3.1: `limit` is a page
 * size with `nextCursor` behind it, and `summary` counts the whole filtered
 * set. The Edge Fleet page reads listEdgeDevices instead; this stays for the
 * small pickers (provisioning wait, scan form).
 */
export async function listEdgeFleet(
  params: {
    component?: EdgeComponent;
    healthStatus?: EdgeHealthStatus;
    businessUnitId?: string;
    includeDecommissioned?: boolean;
    limit?: number;
    cursor?: string;
  } = {},
): Promise<{ pods: EdgePodSummary[]; summary: EdgeFleetSummary; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (params.component) q.set('component', params.component);
  if (params.healthStatus) q.set('healthStatus', params.healthStatus);
  if (params.businessUnitId) q.set('businessUnitId', params.businessUnitId);
  if (params.includeDecommissioned) q.set('includeDecommissioned', 'true');
  if (params.limit) q.set('limit', String(params.limit));
  if (params.cursor) q.set('cursor', params.cursor);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/edge/fleet${suffix}`);
}

/** Sort keys of GET /v1/edge/devices (DEVICE_SORTS on the controller). */
export type EdgeDeviceSort = 'lastSeen' | 'name' | 'hostname' | 'state' | 'version' | 'os' | 'enrolled' | 'reason';

/** Filters shared by the devices list and its CSV export. */
export interface EdgeDeviceFilters {
  /** Free text over hostname, label, instance id, signed-in user, IP and serial. */
  q?: string;
  component?: EdgeComponent;
  /** Release 3.1 wave 2a: one agent type (server-side filter). */
  agentType?: EdgeAgentType;
  agentClass?: EdgeAgentClass;
  businessUnitId?: string;
  /** The wave. */
  installTokenId?: string;
  version?: string;
  /** OS family as reported: windows, linux, macos. */
  os?: string;
  state?: EdgeHealthStatus;
  reason?: EdgeHealthReason;
  /** Release 3.3: Degraded for this reason code (the agent's or the controller's). */
  degradedReason?: string;
  /** ISO timestamps. A device that never checked in matches neither. */
  lastSeenAfter?: string;
  lastSeenBefore?: string;
  hasFindings?: boolean;
  /** Release 3.2 phase 2: behind the current rollout's target, or the update state reported. */
  update?: EdgeDeviceUpdateFilter;
  includeDecommissioned?: boolean;
  sort?: EdgeDeviceSort;
  /** Default per sort on the server: lastSeen/enrolled newest first, state worst first, text A-Z. */
  order?: 'asc' | 'desc';
}

export interface EdgeDeviceQuery extends EdgeDeviceFilters {
  /** Only valid for the sort and order it came from (400 INVALID_CURSOR otherwise). */
  cursor?: string;
  /** 1-500, server default 50. */
  limit?: number;
}

export interface EdgeDeviceList {
  devices: EdgePodSummary[];
  nextCursor: string | null;
  /** Every state of the whole filtered set, ignoring the state/reason filter. */
  summary: EdgeFleetSummary;
  /** Devices matching every filter, state included: what the pages cover. */
  matching: number;
}

/** The query string for the devices list and export; empty values are left out. */
export function edgeDeviceQueryString(p: EdgeDeviceQuery): string {
  const q = new URLSearchParams();
  const put = (k: string, v: string | number | boolean | undefined | null) => {
    if (v === undefined || v === null || v === '') return;
    q.set(k, String(v));
  };
  put('q', p.q?.trim());
  put('component', p.component);
  put('agentType', p.agentType);
  put('agentClass', p.agentClass);
  put('businessUnitId', p.businessUnitId);
  put('installTokenId', p.installTokenId);
  put('version', p.version?.trim());
  put('os', p.os);
  put('state', p.state);
  put('reason', p.reason);
  put('degradedReason', p.degradedReason);
  put('lastSeenAfter', p.lastSeenAfter);
  put('lastSeenBefore', p.lastSeenBefore);
  put('hasFindings', p.hasFindings);
  put('update', p.update);
  if (p.includeDecommissioned) put('includeDecommissioned', true);
  put('sort', p.sort);
  put('order', p.order);
  put('cursor', p.cursor);
  put('limit', p.limit);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * Find any device (release 3.1): server-side search, filters, sort and keyset
 * paging over the whole fleet the caller can see.
 *
 * GET /v1/edge/devices
 */
export async function listEdgeDevices(params: EdgeDeviceQuery = {}): Promise<EdgeDeviceList> {
  return apiFetch(`/v1/edge/devices${edgeDeviceQueryString(params)}`);
}

/**
 * Every device matching the filters, as CSV, in the list's sort order. The
 * server streams it (header row + one line per device) and audits the export.
 * It needs the Authorization header, so it is fetched here and saved through
 * an object URL, like downloadEvidencePack. Returns the file name and the row
 * count the server announced (x-total-count), when it did.
 *
 * GET /v1/edge/devices/export
 */
export async function exportEdgeDevicesCsv(
  params: EdgeDeviceFilters = {},
): Promise<{ filename: string; total: number | null }> {
  const { cursor: _c, limit: _l, ...filters } = params as EdgeDeviceQuery;
  const token = getAuthToken();
  const url = `${API_BASE}/v1/edge/devices/export${edgeDeviceQueryString(filters)}`;
  const headers: Record<string, string> = { accept: 'text/csv' };
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      const title = typeof body?.title === 'string' ? body.title : '';
      const more = typeof body?.detail === 'string' ? body.detail : typeof body?.message === 'string' ? body.message : '';
      detail = title && more ? `${title} - ${more}` : title || more;
    } catch {
      detail = `${res.status} ${res.statusText}`;
    }
    throw new ApiError(res.status, detail || `Export failed (${res.status})`, null);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const filenameMatch = cd.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] ?? `edge-devices-${new Date().toISOString().slice(0, 10)}.csv`;
  const totalHeader = res.headers.get('x-total-count');
  const total = totalHeader !== null && /^\d+$/.test(totalHeader) ? Number(totalHeader) : null;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Deferred so Firefox finishes writing the file before the URL goes away.
  setTimeout(() => URL.revokeObjectURL(objUrl), 5_000);
  return { filename, total };
}

export async function getEdgePod(id: string): Promise<{ pod: EdgePodDetail }> {
  return apiFetch(`/v1/edge/fleet/${id}`);
}

/**
 * Decommission an edge pod (operator-driven). Marks `decommissioned_at`
 * + flips `health_status` to `decommissioned`. Idempotent.
 *
 * POST /v1/edge/fleet/:id/decommission
 */
export async function decommissionEdgePod(
  id: string,
  reason: string,
): Promise<{ pod: EdgePodDetail }> {
  return apiFetch(`/v1/edge/fleet/${id}/decommission`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Bring a retired device back into service (release 2.1). Its next check-in is
 * accepted and it scans again. 409 when the device already re-enrolled.
 *
 * POST /v1/edge/fleet/:id/recommission
 */
export async function recommissionEdgePod(
  id: string,
  reason: string,
): Promise<{ pod: EdgePodDetail }> {
  return apiFetch(`/v1/edge/fleet/${id}/recommission`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Confirm that the credentials a decommission flagged (a DAM collector's
 * reader accounts, the tenant's ClickHouse writer) have been revoked on their
 * own systems (release 3.1 wave 2a). Audited with the reason (3-250). 409 when
 * nothing is waiting to be revoked.
 *
 * POST /v1/edge/fleet/:id/credentials-revoked
 */
export async function confirmEdgeCredentialsRevoked(
  id: string,
  reason: string,
): Promise<{ pod: EdgePodDetail }> {
  return apiFetch(`/v1/edge/fleet/${id}/credentials-revoked`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ── Agentless collection (release 3.1 wave 2a) ──────────────────────────

/**
 * The caps on collection the controller runs itself (catalog crawl, value
 * sampling, residual scan, CMDB and identity pulls). `isDefault` = no row was
 * ever saved (defaults: 2 runs, 20 queries a second, 60 s per statement).
 */
export interface AgentlessSettings {
  maxConcurrentRuns: number;
  maxQueriesPerSecond: number;
  statementTimeoutMs: number;
  isDefault: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  reason: string | null;
}

export interface AgentlessSettingsResponse {
  settings: AgentlessSettings;
  /** Collection runs in flight now. */
  running: number;
}

/** The bounds the controller accepts (PUT .../settings). */
export const AGENTLESS_LIMITS = {
  maxConcurrentRuns: { min: 1, max: 64 },
  maxQueriesPerSecond: { min: 0.1, max: 10_000 },
  statementTimeoutMs: { min: 1_000, max: 3_600_000 },
  reason: { min: 3, max: 500 },
} as const;

export type AgentlessRunKind =
  | 'catalog_crawl'
  | 'value_sample'
  | 'residual_scan'
  | 'protected_copy'
  | 'cmdb_pull'
  | 'identity_pull';
export type AgentlessRunOutcome = 'succeeded' | 'failed' | 'paused' | 'refused';

export interface AgentlessRun {
  id: string;
  podRowId: string | null;
  podId: string;
  target: string;
  kind: AgentlessRunKind | string;
  startedAt: string;
  /** Null while the run is in flight. */
  finishedAt: string | null;
  outcome: AgentlessRunOutcome | string | null;
  queries: number | null;
  detail: string | null;
  startedBy: string | null;
}

/** GET /v1/edge/agent-types/agentless_collector/settings */
export async function getAgentlessSettings(): Promise<AgentlessSettingsResponse> {
  return apiFetch('/v1/edge/agent-types/agentless_collector/settings');
}

/** PUT /v1/edge/agent-types/agentless_collector/settings (audited with the reason). */
export async function updateAgentlessSettings(input: {
  maxConcurrentRuns: number;
  maxQueriesPerSecond: number;
  statementTimeoutMs: number;
  reason: string;
}): Promise<AgentlessSettingsResponse> {
  return apiFetch('/v1/edge/agent-types/agentless_collector/settings', {
    method: 'PUT',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ── Server scanner load policy (release 3.1 wave 2a, review fixes 2 and 3) ──

/**
 * The operator's load policy for every server scanner of the tenant: the load
 * governor (on/off and its thresholds), the per-statement timeout on each
 * target session, a queries-per-second ceiling and targets scanned at once.
 * Sent on every server scanner heartbeat; the agent applies it over its own
 * environment, so a host can no longer switch the governor off.
 */
export interface ScannerGovernorPolicy {
  enabled: boolean;
  warmUp: number;
  slowFactor: number;
  pauseFactor: number;
  stopAfter: number;
}

/** How SQL Server reads treat locks; Oracle reads never block, so it sends Oracle nothing. */
export type ScannerReadIsolation = 'engine_default' | 'snapshot' | 'no_lock';

/** At most this many targets of one system (`host:port`, lowercase) are read at once. */
/** Release 3.6 T-27: pause and resume a system's reads on the source server's own load. */
export interface ScannerSourceLoadRule {
  pauseAtCpuPercent?: number;
  resumeAtCpuPercent?: number;
  pauseAtActiveSessions?: number;
  resumeAtActiveSessions?: number;
  sampleEverySeconds: number;
  maxPauseMinutes: number;
}

export interface ScannerSystemLimit {
  system: string;
  maxConcurrentReads: number;
  /** T-27: absent from a controller before it. */
  sourceLoad?: ScannerSourceLoadRule;
  window?: string;
  windowUtcOffsetMinutes?: number;
  paused?: boolean;
  maxConnections?: number;
}

export interface ScannerPolicy {
  governor: ScannerGovernorPolicy;
  statementTimeoutMs: number;
  readIsolation: ScannerReadIsolation;
  /** False until the install enables snapshot and no-lock reads: only the engine default may be saved. */
  readIsolationAvailable: boolean;
  maxQueriesPerSecond: number;
  maxConcurrentTargets: number;
  /** Absent from a controller before release 3.6 #27. */
  systemLimits?: ScannerSystemLimit[];
  /** Stored entries the controller could not read and does not send (PR #191 round 3). */
  systemLimitsUnreadable?: number;
  isDefault: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  reason: string | null;
}

/**
 * Per system limit, how many server scanners gated targets under it in their
 * latest scan. A limit is per scanner, so K scanners can read the system up
 * to K times the limit. `business_unit`: counted over the caller's units only.
 */
export interface ScannerSystemLimitSharing {
  scope: 'fleet' | 'business_unit';
  systems: Array<{ system: string; scanners: number }>;
}

export interface ScannerPolicyResponse {
  settings: ScannerPolicy;
  /** What a scanner is sent (its etag changes with any value). */
  agentPolicy: { policyEtag: string; source: 'default' | 'operator' };
  /** Absent from a controller before PR #191 round 3. */
  systemLimitSharing?: ScannerSystemLimitSharing;
}

/** The bounds the controller accepts (PUT .../server_scanner/settings). */
export const SCANNER_POLICY_LIMITS = {
  warmUp: { min: 1, max: 1000 },
  slowFactor: { min: 1.1, max: 50 },
  pauseFactor: { max: 100 },
  stopAfter: { min: 1, max: 100 },
  statementTimeoutMs: { min: 1_000, max: 3_600_000 },
  maxQueriesPerSecond: { min: 0.1, max: 10_000 },
  maxConcurrentTargets: { min: 1, max: 64 },
  maxConcurrentReads: { min: 1, max: 64 },
  systemLimits: { max: 200 },
  reason: { min: 3, max: 500 },
  // Release 3.6 T-27 (edge/scanner-policy.ts SourceLoadRuleSchema).
  cpuPercent: { min: 1, max: 100 },
  activeSessions: { min: 1, max: 100_000 },
  sampleEverySeconds: { min: 5, max: 3600 },
  maxPauseMinutes: { min: 1, max: 1440 },
  maxConnections: { min: 1, max: 64 },
  windowUtcOffsetMinutes: { min: -720, max: 840 },
} as const;

/** GET /v1/edge/agent-types/server_scanner/settings */
export async function getScannerPolicy(): Promise<ScannerPolicyResponse> {
  return apiFetch('/v1/edge/agent-types/server_scanner/settings');
}

/** PUT /v1/edge/agent-types/server_scanner/settings (audited with the reason). */
export async function updateScannerPolicy(input: {
  governor: ScannerGovernorPolicy;
  statementTimeoutMs: number;
  readIsolation: ScannerReadIsolation;
  maxQueriesPerSecond: number;
  maxConcurrentTargets: number;
  /** Omitted keeps the stored list (and is all an older controller accepts). */
  systemLimits?: ScannerSystemLimit[];
  reason: string;
}): Promise<ScannerPolicyResponse> {
  return apiFetch('/v1/edge/agent-types/server_scanner/settings', {
    method: 'PUT',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ----- Device groups and fleet scan policy (release 3.3, R3-OI-194) -----

/** Where a device's scan hours come from, highest first. */
export type ScanPolicySource = 'device' | 'group' | 'business_unit' | 'tenant' | 'install' | 'default';

export interface DeviceGroupRule {
  os?: string;
  agentType?: string;
  hostnameContains?: string;
  ipPrefix?: string;
  version?: string;
}

/**
 * Release 3.3 (R3-OI-194): branch-link settings a group, a unit or the tenant
 * sets (each resolved on its own: group, unit, tenant, install, default).
 * null = not set at that level.
 */
export type CheckinProfile = 'standard' | 'low_chatter';
export interface BranchLinkSettings {
  checkinProfile: CheckinProfile | null;
  checkinQuietHours: string | null;
  uploadKbPerMinute: number | null;
}
/** Absent = leave as it is; null = clear this level. */
export type BranchLinkInput = { [K in keyof BranchLinkSettings]?: BranchLinkSettings[K] };

export interface DeviceGroup extends BranchLinkSettings {
  id: string;
  name: string;
  description: string;
  kind: 'static' | 'rule';
  rule: DeviceGroupRule | null;
  businessUnitId: string | null;
  businessUnitName: string | null;
  priority: number;
  scanWindow: string | null;
  memberCount: number;
  tokenCount: number;
  editable: boolean;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface EffectiveScanPolicy {
  window: string | null;
  source: ScanPolicySource;
  groupId: string | null;
  groupName: string | null;
  businessUnitId: string | null;
}

export interface DeviceScanPolicy {
  effective: EffectiveScanPolicy;
  /** Inherited hours only: the device applied the override carrying them. Null otherwise. */
  delivered: boolean | null;
  levels: {
    device: string | null;
    group: { id: string; name: string; window: string } | null;
    businessUnit: { id: string; name: string | null; window: string } | null;
    tenant: string | null;
    install: string | null;
  };
  groups: Array<{ id: string; name: string; via: 'static' | 'token' | 'rule'; priority: number; scanWindow: string | null }>;
}

export interface PolicyDeviceRow {
  podRowId: string;
  podId: string;
  displayName: string;
  hostname: string | null;
  os: string | null;
  agentType: string | null;
  businessUnitId: string | null;
  lastSeenAt: string | null;
  via?: 'static' | 'token' | 'rule';
  effective: EffectiveScanPolicy;
  reported: { window: string | null; source: string | null } | null;
}

export interface FleetPolicyRow extends BranchLinkSettings {
  scopeType: 'tenant' | 'business_unit';
  businessUnitId: string | null;
  businessUnitName: string | null;
  scanWindow: string | null;
  updatedBy: string;
  updatedAt: string;
  editable: boolean;
}

/** The Edge Fleet filter a bulk act selects with (live devices only). */
export interface DeviceGroupFilter {
  q?: string;
  os?: string;
  agentType?: string;
  businessUnitId?: string;
  installTokenId?: string;
  version?: string;
  state?: string;
}

export interface FleetPolicySummary {
  total: number;
  bySource: Record<ScanPolicySource, number>;
  byGroup: Array<{ groupId: string; groupName: string; devices: number }>;
}

/** GET /v1/edge/device-groups. A controller before 3.3 answers 404. */
export async function listDeviceGroups(): Promise<{ groups: DeviceGroup[] }> {
  return apiFetch('/v1/edge/device-groups');
}

/** POST /v1/edge/device-groups (audited edge.device_group.created). */
export async function createDeviceGroup(input: {
  name: string;
  description?: string;
  kind: 'static' | 'rule';
  rule?: DeviceGroupRule;
  businessUnitId?: string;
  priority?: number;
  scanWindow?: string | null;
  reason: string;
} & BranchLinkInput): Promise<{ group: DeviceGroup }> {
  return apiFetch('/v1/edge/device-groups', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

/** PATCH /v1/edge/device-groups/:id. `scanWindow: null` clears the group's hours. */
export async function updateDeviceGroup(
  id: string,
  input: { name?: string; description?: string; rule?: DeviceGroupRule; priority?: number; scanWindow?: string | null; reason: string } & BranchLinkInput,
): Promise<{ group: DeviceGroup }> {
  return apiFetch(`/v1/edge/device-groups/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
}

/** DELETE /v1/edge/device-groups/:id (flags it; the history is kept). */
export async function deleteDeviceGroup(
  id: string,
  reason: string,
): Promise<{ id: string; deletedAt: string; tokensCarryingIt: number }> {
  return apiFetch(`/v1/edge/device-groups/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
}

/** GET /v1/edge/device-groups/:id/members, each device with the hours in force. */
export async function listDeviceGroupMembers(
  id: string,
  params: { limit?: number; cursor?: string } = {},
): Promise<{ group: DeviceGroup; devices: PolicyDeviceRow[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (params.limit) q.set('limit', String(params.limit));
  if (params.cursor) q.set('cursor', params.cursor);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/edge/device-groups/${encodeURIComponent(id)}/members${suffix}`);
}

/** POST /v1/edge/device-groups/:id/members/preview: how many devices a filter selects. */
export async function previewDeviceGroupMembers(
  id: string,
  filter: DeviceGroupFilter,
): Promise<{ matching: number; alreadyMembers: number }> {
  return apiFetch(`/v1/edge/device-groups/${encodeURIComponent(id)}/members/preview`, {
    method: 'POST',
    body: JSON.stringify({ filter }),
  });
}

/** POST /v1/edge/device-groups/:id/members: add every device the filter selects (409 when the count changed). */
export async function addDeviceGroupMembers(
  id: string,
  input: { filter?: DeviceGroupFilter; podIds?: string[]; expectedCount?: number; reason: string },
): Promise<{ matched: number; added: number }> {
  return apiFetch(`/v1/edge/device-groups/${encodeURIComponent(id)}/members`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

/** POST /v1/edge/device-groups/:id/members/remove (flags the memberships). */
export async function removeDeviceGroupMembers(
  id: string,
  input: { filter?: DeviceGroupFilter; podIds?: string[]; expectedCount?: number; reason: string },
): Promise<{ matched: number; removed: number }> {
  return apiFetch(`/v1/edge/device-groups/${encodeURIComponent(id)}/members/remove`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

/** POST /v1/edge/device-groups/:id/scan-now: a rescan for every member, starts spread over the minutes given. */
export async function scanDeviceGroupNow(
  id: string,
  input: { spreadMinutes: number; reason: string },
): Promise<{ queued: number; alreadyQueued: number; notSupported: number; spreadMinutes: number; lastStartBy: string | null }> {
  return apiFetch(`/v1/edge/device-groups/${encodeURIComponent(id)}/scan-now`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

/** GET /v1/edge/fleet-policy: the tenant's scan hours and the units' the caller may see. */
export async function getFleetPolicy(): Promise<{ tenant: FleetPolicyRow | null; businessUnits: FleetPolicyRow[] }> {
  return apiFetch('/v1/edge/fleet-policy');
}

/** PUT /v1/edge/fleet-policy (audited edge.fleet_policy.set). `scanWindow: null` clears the level. */
export async function setFleetPolicy(input: {
  scopeType: 'tenant' | 'business_unit';
  businessUnitId?: string;
  /** Absent leaves the hours as they are (a write may set only branch-link settings). */
  scanWindow?: string | null;
  reason: string;
} & BranchLinkInput): Promise<{ policy: FleetPolicyRow }> {
  return apiFetch('/v1/edge/fleet-policy', { method: 'PUT', body: JSON.stringify(input) });
}

/** GET /v1/edge/fleet-policy/summary: live devices by the level their hours come from. */
export async function getFleetPolicySummary(): Promise<FleetPolicySummary> {
  return apiFetch('/v1/edge/fleet-policy/summary');
}

/** GET /v1/edge/agentless/runs?limit=&podId= (newest first). */
export async function listAgentlessRuns(params: { limit?: number; podId?: string } = {}): Promise<{ runs: AgentlessRun[] }> {
  const q = new URLSearchParams();
  if (params.limit) q.set('limit', String(params.limit));
  if (params.podId) q.set('podId', params.podId);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/edge/agentless/runs${suffix}`);
}

/**
 * What the signed-in operator may do about a device's business unit: whether
 * they may move it at all (and why not), its current unit, and the units they
 * may move it to. Judged by the server with the same rules as the move.
 *
 * GET /v1/edge/fleet/:id/move-business-unit
 */
export interface EdgePodBusinessUnitMoveOptions {
  allowed: boolean;
  reason: string | null;
  current: { id: string; name: string } | null;
  targets: Array<{ id: string; name: string }>;
}

export async function getEdgePodBusinessUnitMoveOptions(id: string): Promise<EdgePodBusinessUnitMoveOptions> {
  return apiFetch(`/v1/edge/fleet/${id}/move-business-unit`);
}

export interface EdgePodBusinessUnitMove {
  podId: string;
  fromBusinessUnitId: string | null;
  toBusinessUnitId: string;
  assetsMoved: number;
  findingsMoved: number;
}

/**
 * Move a device to another business unit (the only way a laptop changes unit;
 * an install package never moves one). Its existing findings move with it:
 * visible to the new unit, hidden from the old one. Needs authority over both
 * units. Audited with the reason.
 *
 * POST /v1/edge/fleet/:id/move-business-unit
 */
export async function moveEdgePodBusinessUnit(
  id: string,
  businessUnitId: string,
  reason: string,
): Promise<{ pod: EdgePodDetail; move: EdgePodBusinessUnitMove }> {
  return apiFetch(`/v1/edge/fleet/${id}/move-business-unit`, {
    method: 'POST',
    body: JSON.stringify({ businessUnitId, reason }),
  });
}

/** Deployment platform the provisioning wizard targets (2026-07-11). */
export type EdgeInstallPlatform = 'kubernetes' | 'docker' | 'linux' | 'windows' | 'macos';

/** One generated install file (download/copy) + its one-line run hint. */
export interface EdgeInstallArtifact {
  filename: string;
  contents: string;
  language: 'yaml' | 'bash' | 'powershell';
  runNote: string;
}

export interface MintInstallTokenInput {
  component: EdgeComponent;
  platform?: EdgeInstallPlatform;
  displayName: string;
  businessUnitId?: string;
  ttlSeconds?: number;
  allowedIpCidr?: string;
  scope?: Record<string, unknown>;
  /** 'fleet' + maxDevices: one installer for a whole MDM rollout. */
  kind?: 'single' | 'fleet';
  maxDevices?: number;
  /** Release 3.3: the device group the token's devices join at their first check-in. */
  deviceGroupId?: string;
  /** 'target_file': a docker scanner for named data stores; its installer reads scan-targets.json. */
  scanTargets?: 'host_folders' | 'target_file';
}

/** One store of a scan list: the file FOCTTA Consent offers, or one written by hand. No credential. */
export type ScanListStore = { id: string; engine: string; catalogue?: string } & Record<string, unknown>;

export interface ScannerTargetFile {
  fileName: 'scan-targets.json';
  contents: string;
  targets: number;
  stores: Array<{ store: string; engine: string; targets: number; notCovered: number; pickUpNewObjects?: true }>;
  passwords: Array<{ store: string; placeholder: string }>;
  notCovered: {
    total: number;
    shown: Array<{ store: string; location: string; reason: string; line: number }>;
    csv: string;
  };
}

/** Builds the scanner's target file from a store list and each store's column list. Nothing is stored. */
export async function buildScannerTargetFile(input: {
  stores: ScanListStore[];
  catalogues: Record<string, string>;
}): Promise<ScannerTargetFile> {
  return apiFetch('/v1/edge/scanner-setup/target-file', { method: 'POST', body: JSON.stringify(input) });
}

export async function mintEdgeInstallToken(
  input: MintInstallTokenInput,
  idempotencyKey?: string,
): Promise<{
  tokenId: string;
  bootstrapToken: string;
  /** Release 8: sha256(token) as the controller stored it; absent from an older controller. */
  bootstrapTokenSha256?: string;
  expiresAt: string;
  component: EdgeComponent;
  platform: EdgeInstallPlatform;
  artifacts: EdgeInstallArtifact[];
  helmValues: string;
  installScript: string;
}> {
  return apiFetch('/v1/edge/install-tokens', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface EdgeInstallTokenRow {
  id: string;
  component: EdgeComponent;
  kind: 'single' | 'fleet';
  /** Fleet only. The licence ceiling; null on a single token. */
  maxDevices: number | null;
  /**
   * How much of this token has been SPENT. A ceiling ledger - it only ever goes
   * up, because recycling slots on decommission would let a fleet quietly
   * exceed its licence.
   */
  enrolledCount: number;
  /** How many of those devices are running RIGHT NOW. Diverges from
   * enrolledCount as soon as any device is retired, and reading one as the
   * other will mis-size the next rollout. */
  livePods: number;
  boundPodId: string | null;
  /** The unit every device on this token is placed in; null = none. */
  businessUnitId: string | null;
  expiresAt: string;
  usedAt: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
}

export async function listEdgeInstallTokens(): Promise<{ tokens: EdgeInstallTokenRow[] }> {
  return apiFetch('/v1/edge/install-tokens');
}

export type AgentEntitlementState = 'unlimited' | 'within' | 'grace' | 'over';

export interface AgentEntitlementVerdict {
  state: AgentEntitlementState;
  live: number;
  limit: number | null;
  over: number;
  graceCeiling: number | null;
  enrolmentAllowed: boolean;
  tokenMintingAllowed: boolean;
  summary: string;
}

/**
 * Agent entitlement per class.
 *
 * Read-only by design. A tenant sees exactly where they stand and can ask for
 * more; the ceiling itself is set by FOCTTA, which is why the response also
 * says who manages it rather than rendering an editable field the operator
 * cannot actually change.
 */
export async function getAgentEntitlement(): Promise<{
  classes: Record<'endpoint' | 'vpcPod' | 'gateway', AgentEntitlementVerdict>;
  ceilingManagedBy: string;
}> {
  return apiFetch('/v1/edge/entitlement');
}

/**
 * STOPS every device on the token at its next heartbeat. The response names
 * them, because on a fleet token that can be a hundred machines.
 */
export async function revokeEdgeInstallToken(
  id: string,
  reason: string,
): Promise<{ revoked: boolean; livePodsStopped: string[] }> {
  return apiFetch(`/v1/edge/install-tokens/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Extend a fleet token's enrolment window and/or raise its device limit
 * (release 2.1). Audited server-side with before and after values.
 */
export async function updateEdgeInstallToken(
  id: string,
  input: { ttlSeconds?: number; maxDevices?: number; reason: string },
): Promise<{ updated: true; expiresAt: string; maxDevices: number | null; enrolledCount: number }> {
  return apiFetch(`/v1/edge/install-tokens/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ── Edge rollout controls (release 2.1 items 24 and 22) ──────────────────

/** Release 3.1 wave 2a adds `agent_type`: every agent of one type, named by `scopeKey`. */
export type EdgeScopeType = 'tenant' | 'business_unit' | 'install_token' | 'agent_type';

/**
 * How far one scope act has reached its devices (release 3.1): "N of M
 * confirmed paused". `devices` = live agents in the scope that read a pause
 * THEMSELVES (laptop agents, server scanners, DAM collectors from 3.1). The
 * rest are split by why they have not confirmed: checked in since the act but
 * still reporting the old state; not checked in yet but within their window
 * (they learn it at their next check-in); or offline (silent, down or never
 * checked in: they learn it when they come back, and the controller refuses
 * their findings meanwhile).
 *
 * Release 3.1 wave 2a: `enforcedByController` counts gateways, DSAR connectors
 * and agentless targets in the scope - the controller makes the pause real on
 * their next call, so they have nothing to confirm (absent from an older
 * controller). `ignoresPause` counts what nothing would stop: a DAM collector
 * older than 3.1, or a gateway with no service credential on record.
 */
export interface EdgeScopePropagation {
  devices: number;
  confirmed: number;
  checkedInNotConfirmed: number;
  pendingOnline: number;
  pendingOffline: number;
  enforcedByController?: number;
  ignoresPause: number;
}

export interface EdgeScopeOverride {
  id: string;
  scopeType: EdgeScopeType;
  /** Null for the whole fleet and for an agent type. */
  scopeId: string | null;
  /** The agent type, for scope `agent_type`; null (or absent, older controller) otherwise. */
  scopeKey?: EdgeAgentType | null;
  paused: boolean;
  reason: string;
  pushedByActorId: string;
  createdAt: string;
  /** Absent from a controller before release 3.1. */
  progress?: EdgeScopePropagation;
}

export async function listEdgeScopeOverrides(): Promise<{ scopeOverrides: EdgeScopeOverride[] }> {
  return apiFetch('/v1/edge/rollout/scope-overrides');
}

/**
 * Pause or resume a wave (install token), a business unit, the whole fleet, or
 * (release 3.1 wave 2a) every agent of one type (`scopeType: 'agent_type'`,
 * `scopeKey` = the type, no scopeId). Reaches each device on its next
 * check-in or call. Audited with the reason.
 */
export async function setEdgeScopePause(input: {
  scopeType: EdgeScopeType;
  scopeId?: string;
  scopeKey?: EdgeAgentType;
  paused: boolean;
  reason: string;
}): Promise<{ scopeOverride: EdgeScopeOverride }> {
  return apiFetch('/v1/edge/rollout/scope-overrides', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface EdgeWaveHealth {
  tokenId: string;
  enrolled: number;
  live: number;
  reportedLast15m: number;
  scannedOkLast24h: number;
  failing: number;
  paused: boolean;
}

export async function listEdgeWaves(): Promise<{ waves: EdgeWaveHealth[] }> {
  return apiFetch('/v1/edge/install-tokens/waves');
}

export async function previewEdgeSilentDevices(
  tokenId: string,
  days: number,
): Promise<{
  tokenId: string;
  silentDays: number;
  count: number;
  sample: Array<{ podId: string; displayName: string; lastHeartbeatAt: string | null }>;
}> {
  return apiFetch(`/v1/edge/install-tokens/${tokenId}/silent-devices?days=${encodeURIComponent(String(days))}`);
}

export async function decommissionEdgeSilentDevices(
  tokenId: string,
  input: { silentDays: number; reason: string; expectedCount?: number },
): Promise<{ decommissioned: number; podIds: string[] }> {
  return apiFetch(`/v1/edge/install-tokens/${tokenId}/decommission-silent`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface EdgeFleetVersions {
  counts: Array<{ version: string; businessUnitId: string | null; installTokenId: string | null; count: number }>;
  targets: Array<{
    scopeType: 'business_unit' | 'install_token';
    scopeId: string;
    targetVersion: string;
    reason: string;
    setByActorId: string;
    createdAt: string;
  }>;
  behind: Array<{
    podId: string;
    displayName: string;
    version: string;
    targetVersion: string;
    businessUnitId: string | null;
    installTokenId: string | null;
    lastHeartbeatAt: string | null;
  }>;
  behindTotal: number;
}

export async function getEdgeFleetVersions(): Promise<EdgeFleetVersions> {
  return apiFetch('/v1/edge/fleet/versions');
}

/** Descriptive: sends nothing to any device. Null clears the target. */
export async function setEdgeVersionTarget(input: {
  scopeType: 'business_unit' | 'install_token';
  scopeId: string;
  targetVersion: string | null;
  reason: string;
}): Promise<{ scopeType: string; scopeId: string; targetVersion: string | null }> {
  return apiFetch('/v1/edge/fleet/version-targets', {
    method: 'PUT',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

/** No new devices; everything already running keeps running. */
export async function sealEdgeInstallToken(
  id: string,
): Promise<{ sealed: boolean; atDevices: number }> {
  return apiFetch(`/v1/edge/install-tokens/${id}/seal`, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function pushEdgeConfigOverride(
  podId: string,
  patch: Record<string, unknown>,
  idempotencyKey?: string,
): Promise<{ overrideEtag: string; patch: Record<string, unknown>; pushedAt: string }> {
  return apiFetch(`/v1/edge/fleet/${podId}/config-override`, {
    method: 'POST',
    body: JSON.stringify({ patch }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Edge fleet - resource budgets + watchdog (Phase 6) ────────────────

/**
 * What the watchdog may do about a sustained breach. `throttle` was removed in
 * release 3.1 (no agent ever applied it; it behaved exactly like observe), and
 * the API now refuses it with a 400.
 */
export type EdgeEnforcement = 'observe' | 'pause';

export interface EdgeResourceBudget {
  component: EdgeComponent;
  cpuPercentCeiling: number | null;
  rssMbCeiling: number | null;
  sustainedSamples: number;
  enforcement: EdgeEnforcement;
  /** True when no tenant row exists and the platform default is in force. */
  isDefault: boolean;
}

export interface EdgeBudgetBreach {
  id: string;
  podId: string;
  component: EdgeComponent;
  observedCpuPercent: number | null;
  observedRssMb: number | null;
  ceilingCpuPercent: number | null;
  ceilingRssMb: number | null;
  consecutiveSamples: number;
  /**
   * What the watchdog DID. `none` means observe-only - it recorded, nothing
   * more. `throttle` appears only on breaches recorded before release 3.1.
   */
  actionTaken: 'none' | 'throttle' | 'pause';
  actionDetail: string | null;
  firstObservedAt: string;
  resolvedAt: string | null;
}

export async function listEdgeBudgets(): Promise<{ budgets: EdgeResourceBudget[] }> {
  return apiFetch('/v1/edge/budgets');
}

export async function upsertEdgeBudget(
  component: EdgeComponent,
  input: Partial<Pick<EdgeResourceBudget,
    'cpuPercentCeiling' | 'rssMbCeiling' | 'sustainedSamples' | 'enforcement'>>,
  idempotencyKey?: string,
): Promise<{ budget: EdgeResourceBudget }> {
  return apiFetch(`/v1/edge/budgets/${component}`, {
    method: 'PUT',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function listEdgeBreaches(): Promise<{ breaches: EdgeBudgetBreach[] }> {
  return apiFetch('/v1/edge/breaches');
}

// ── Edge fleet - diagnose + update (Phase 7 batch B) ──────────────────

export type DiagnoseCheckStatus = 'ok' | 'warn' | 'fail' | 'unknown';

export interface DiagnoseCheck {
  name: string;
  status: DiagnoseCheckStatus;
  detail: string;
}

export interface DiagnoseReport {
  podId: string;
  generatedAt: string;
  mode: 'synthesised_from_telemetry' | 'live_pod_rpc';
  overall: DiagnoseCheckStatus;
  checks: DiagnoseCheck[];
  summary: string;
}

export async function diagnoseEdgePod(
  id: string,
  idempotencyKey?: string,
): Promise<{ report: DiagnoseReport }> {
  return apiFetch(`/v1/edge/fleet/${id}/diagnose`, {
    method: 'POST',
    body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * Ask one device for a version (audited). Release 3.2: a self-updating agent
 * installs it itself if it is signed with the FOCTTA release key; other
 * installs record it. Release 3.2 phase 2: needs the approver permission, and
 * is refused while agent updates are on hold (except a rollback) and for a
 * withdrawn version. Fleet-wide changes go through an update rollout
 * (lib/update-rollouts-api.ts).
 */
export async function triggerEdgeUpdate(
  podId: string,
  input: { targetVersion: string; rollback?: boolean },
  idempotencyKey?: string,
): Promise<{
  overrideEtag: string;
  targetVersion: string;
  pushedAt: string;
}> {
  return apiFetch(`/v1/edge/fleet/${podId}/update`, {
    method: 'POST',
    // Release 3.2: `rollback` only when the operator explicitly asked to go
    // back; a self-updating agent never reads a lower target as one otherwise.
    body: JSON.stringify(input.rollback
      ? { targetVersion: input.targetVersion, rollback: true }
      : { targetVersion: input.targetVersion }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Edge command queue (Command Plane Phase 5) ───────────────────

/**
 * The commands an agent carries out. `upload_logs` was removed in release 3.1:
 * no agent ever implemented it (each acked it failed) and the API refuses it.
 * `collect_diagnostics` is the working self-report.
 */
export type EdgeCommandName = 'pause' | 'resume' | 'collect_diagnostics' | 'rescan';
export type EdgeCommandState = 'queued' | 'sent' | 'acked' | 'expired';

export interface EdgeCommand {
  id: string;
  command: string;
  payload: Record<string, unknown>;
  state: EdgeCommandState;
  priority: number;
  /** Required at enqueue time - "who told this laptop to stop, and why". */
  reason: string | null;
  result: 'ok' | 'failed' | null;
  resultDetail: string | null;
  sentAt: string | null;
  ackedAt: string | null;
  expiresAt: string;
  createdAt: string;
  /**
   * How many times this was handed to the agent. >1 with no ack means the
   * device is taking commands and not answering - which is NOT the same as
   * never receiving one, and needs a different response from the operator.
   */
  deliveryAttempts: number;
}

export async function listEdgeCommands(podId: string): Promise<{ commands: EdgeCommand[] }> {
  return apiFetch(`/v1/edge/fleet/${podId}/commands`);
}

export async function enqueueEdgeCommand(
  podId: string,
  input: { command: EdgeCommandName; reason: string; payload?: Record<string, unknown>; ttlSeconds?: number },
  idempotencyKey?: string,
): Promise<{ id: string; command: string; state: string; expiresAt: string }> {
  return apiFetch(`/v1/edge/fleet/${podId}/commands`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance (Phase 8 batch A) ──────────────────────────────────────

export type RegulationId =
  | 'DPDPA_2023' | 'DPDPA_S9' | 'DPDPA_S8' | 'DPDPA_S10'
  | 'GDPR' | 'GDPR_Art8' | 'GDPR_Art9' | 'GDPR_Art13' | 'GDPR_Art16'
  | 'GDPR_Art17' | 'GDPR_Art18' | 'GDPR_Art20' | 'GDPR_Art21' | 'GDPR_Art22'
  | 'GDPR_Art33' | 'GDPR_Art35'
  | 'CCPA' | 'COPPA_1998' | 'UK_Children_Code'
  | 'RBI_MD_Cyber' | 'IRDAI_2023' | 'SEBI_CSCRF';

export type ComplianceStatus = 'green' | 'amber' | 'red' | 'not_started' | 'unknown';

export interface ComplianceControl {
  id: string;
  name: string;
  status: ComplianceStatus;
  detail: string;
  sourceRef?: string;
}

export interface RegulationCoverage {
  regulationId: RegulationId;
  displayName: string;
  jurisdiction: string;
  coverageScore: number;
  status: ComplianceStatus;
  controlsTotal: number;
  controlsGreen: number;
  controlsAmber: number;
  controlsRed: number;
  /** Not begun / not assessable here; with these the breakdown sums to controlsTotal. */
  controlsNotStarted: number;
  controlsUnknown: number;
  controls: ComplianceControl[];
}

export interface ComplianceCoverageResponse {
  generatedAt: string;
  regulations: RegulationCoverage[];
  overall: {
    score: number;
    status: ComplianceStatus;
    greenRegulations: number;
    notStartedRegulations: number;
    unknownRegulations: number;
    amberRegulations: number;
    redRegulations: number;
  };
}

export interface ComplianceGap {
  id: string;
  regulationId: RegulationId;
  controlId: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  recommendedAction: string;
  ownerRoleHint: string;
  sourceRoute?: string;
}

export interface ComplianceGapsResponse {
  generatedAt: string;
  gaps: ComplianceGap[];
  byRegulation: Array<{ regulationId: RegulationId; count: number }>;
  bySeverity: { critical: number; high: number; medium: number; low: number };
}

export type ComplianceExportFormat = 'json' | 'csv' | 'pdf';

export interface ComplianceExportResponse {
  format: ComplianceExportFormat;
  regulationId: RegulationId | null;
  fromDate: string | null;
  toDate: string | null;
  generatedAt: string;
  signedDigest: string;
  rowCount: number;
  body: string;
  contentType: string;
}

export async function getComplianceCoverage(): Promise<ComplianceCoverageResponse> {
  return apiFetch('/v1/compliance/coverage');
}

export async function getComplianceGaps(): Promise<ComplianceGapsResponse> {
  return apiFetch('/v1/compliance/gaps');
}

export async function buildComplianceExport(input: {
  format: ComplianceExportFormat;
  regulationId?: RegulationId;
  fromDate?: string;
  toDate?: string;
}): Promise<ComplianceExportResponse> {
  return apiFetch('/v1/compliance/export', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── Compliance / Notice generator (Phase 8 batch B item 1) ────────────

export type NoticeJurisdiction = 'India' | 'EU' | 'US-CA' | 'UK' | 'global';
export type NoticeStatus = 'draft' | 'published' | 'expired' | 'revoked';

export interface ComplianceNotice {
  id: string;
  tenantId: string;
  regulationId: RegulationId;
  jurisdiction: NoticeJurisdiction;
  templateVersion: string;
  processingActivity: string;
  dataClasses: string[];
  legalBases: string[];
  retentionPeriodDays: number | null;
  crossBorderDestinations: string[];
  contactDpoEmail: string;
  bodyHtml: string;
  signedDigest: string;
  status: NoticeStatus;
  publicSlug: string | null;
  publishedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNoticeInput {
  regulationId: RegulationId;
  jurisdiction: NoticeJurisdiction;
  processingActivity: string;
  dataClasses: string[];
  legalBases: string[];
  retentionPeriodDays?: number;
  crossBorderDestinations?: string[];
  contactDpoEmail: string;
  publish?: boolean;
  publicSlug?: string;
  expiresInDays?: number;
}

export async function listComplianceNotices(params: {
  status?: NoticeStatus;
  regulationId?: RegulationId;
  limit?: number;
} = {}): Promise<{
  notices: ComplianceNotice[];
  byStatus: { draft: number; published: number; expired: number; revoked: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.regulationId) q.set('regulationId', params.regulationId);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/notices${suffix}`);
}

export async function getComplianceNotice(id: string): Promise<{ notice: ComplianceNotice }> {
  return apiFetch(`/v1/compliance/notices/${id}`);
}

export async function createComplianceNotice(
  input: CreateNoticeInput,
  idempotencyKey?: string,
): Promise<{ notice: ComplianceNotice }> {
  return apiFetch('/v1/compliance/notices', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function publishComplianceNotice(
  id: string,
  input: { publicSlug: string; expiresInDays?: number },
  idempotencyKey?: string,
): Promise<{ notice: ComplianceNotice }> {
  return apiFetch(`/v1/compliance/notices/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function revokeComplianceNotice(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ notice: ComplianceNotice }> {
  return apiFetch(`/v1/compliance/notices/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Erasure executor (Phase 8 batch B item 7) ─────────────

export type ErasureIdentifierKind =
  | 'email' | 'phone' | 'aadhaar' | 'pan' | 'customer_number' | 'custom';
export type ErasureJobStatus =
  | 'pending' | 'approved' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface ErasureLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
}

export interface ErasureJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: ErasureIdentifierKind;
  status: ErasureJobStatus;
  assetsScope: string[];
  executionLog: ErasureLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listErasureJobs(params: {
  status?: ErasureJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: ErasureJob[];
  byStatus: { pending: number; approved: number; running: number; completed: number; failed: number; cancelled: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/erasure-jobs${suffix}`);
}

export async function createErasureRequest(
  input: { principalIdentifier: string; identifierKind: ErasureIdentifierKind; requestReason?: string },
  idempotencyKey?: string,
): Promise<{ job: ErasureJob }> {
  return apiFetch('/v1/compliance/erasure-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveErasureJob(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ job: ErasureJob }> {
  return apiFetch(`/v1/compliance/erasure-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executeErasureJob(
  id: string,
  idempotencyKey?: string,
): Promise<{ job: ErasureJob }> {
  return apiFetch(`/v1/compliance/erasure-jobs/${id}/execute`, {
    method: 'POST',
    body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Rectification executor (Phase 8 batch C item 1) ──────

export type RectificationIdentifierKind = ErasureIdentifierKind;
export type RectificationJobStatus = ErasureJobStatus;

export interface RectificationLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
  fieldsUpdated: string[];
}

export interface RectificationJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: RectificationIdentifierKind;
  status: RectificationJobStatus;
  /** Field path → new value the subject asked us to apply. */
  corrections: Record<string, unknown>;
  assetsScope: string[];
  executionLog: RectificationLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listRectificationJobs(params: {
  status?: RectificationJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: RectificationJob[];
  byStatus: { pending: number; approved: number; running: number; completed: number; failed: number; cancelled: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/rectification-jobs${suffix}`);
}

export async function createRectificationRequest(
  input: {
    principalIdentifier: string;
    identifierKind: RectificationIdentifierKind;
    corrections: Record<string, unknown>;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: RectificationJob }> {
  return apiFetch('/v1/compliance/rectification-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveRectificationJob(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ job: RectificationJob }> {
  return apiFetch(`/v1/compliance/rectification-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executeRectificationJob(
  id: string,
  idempotencyKey?: string,
): Promise<{ job: RectificationJob }> {
  return apiFetch(`/v1/compliance/rectification-jobs/${id}/execute`, {
    method: 'POST',
    body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Restriction executor (Phase 8 batch D item 1) ────────

export type RestrictionIdentifierKind = ErasureIdentifierKind;
export type RestrictionJobStatus =
  | 'pending' | 'approved' | 'running' | 'restricted' | 'lifted' | 'failed' | 'cancelled';
export type RestrictionBasis =
  | 'accuracy_challenged'
  | 'unlawful_subject_opposes'
  | 'controller_no_longer_needs'
  | 'objection_pending';

export interface RestrictionLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
}

export interface RestrictionJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: RestrictionIdentifierKind;
  status: RestrictionJobStatus;
  restrictionBasis: RestrictionBasis;
  /** Optional sunset for the restriction. NULL = indefinite. */
  untilDate: string | null;
  assetsScope: string[];
  executionLog: RestrictionLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  liftedAt: string | null;
  liftedByActorId: string | null;
  liftReason: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listRestrictionJobs(params: {
  status?: RestrictionJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: RestrictionJob[];
  byStatus: {
    pending: number; approved: number; running: number;
    restricted: number; lifted: number; failed: number; cancelled: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/restriction-jobs${suffix}`);
}

export async function createRestrictionRequest(
  input: {
    principalIdentifier: string;
    identifierKind: RestrictionIdentifierKind;
    restrictionBasis: RestrictionBasis;
    untilDate?: string;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch('/v1/compliance/restriction-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveRestrictionJob(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch(`/v1/compliance/restriction-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executeRestrictionJob(
  id: string, idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch(`/v1/compliance/restriction-jobs/${id}/execute`, {
    method: 'POST', body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function liftRestrictionJob(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch(`/v1/compliance/restriction-jobs/${id}/lift`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Portability executor (Phase 8 batch E item 1) ────────

export type PortabilityIdentifierKind = ErasureIdentifierKind;
export type PortabilityJobStatus =
  | 'pending' | 'approved' | 'running' | 'completed' | 'expired' | 'failed' | 'cancelled';
export type BundleFormat = 'json' | 'csv' | 'xml';

export interface PortabilityLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
  recordsExtracted: number;
}

export interface PortabilityJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: PortabilityIdentifierKind;
  status: PortabilityJobStatus;
  bundleFormat: BundleFormat;
  assetsScope: string[];
  executionLog: PortabilityLogEntry[];
  bundleUrl: string | null;
  bundleSha256: string | null;
  bundleSizeBytes: number | null;
  /** When the signed download URL stops working. NULL until completed. */
  expiresAt: string | null;
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listPortabilityJobs(params: {
  status?: PortabilityJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: PortabilityJob[];
  byStatus: {
    pending: number; approved: number; running: number;
    completed: number; expired: number; failed: number; cancelled: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/portability-jobs${suffix}`);
}

export async function createPortabilityRequest(
  input: {
    principalIdentifier: string;
    identifierKind: PortabilityIdentifierKind;
    bundleFormat?: BundleFormat;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: PortabilityJob }> {
  return apiFetch('/v1/compliance/portability-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approvePortabilityJob(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ job: PortabilityJob }> {
  return apiFetch(`/v1/compliance/portability-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executePortabilityJob(
  id: string, idempotencyKey?: string,
): Promise<{ job: PortabilityJob }> {
  return apiFetch(`/v1/compliance/portability-jobs/${id}/execute`, {
    method: 'POST', body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Objection executor (Phase 8 batch F item 1) ──────────

export type ObjectionIdentifierKind = ErasureIdentifierKind;
export type ObjectionJobStatus =
  | 'pending' | 'assessing' | 'upheld' | 'overridden' | 'failed' | 'cancelled';
export type ObjectionGround =
  | 'direct_marketing'
  | 'legitimate_interests'
  | 'public_task'
  | 'scientific_research';

export interface ObjectionLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
}

export interface ObjectionJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: ObjectionIdentifierKind;
  status: ObjectionJobStatus;
  objectionGround: ObjectionGround;
  assetsScope: string[];
  executionLog: ObjectionLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  assessedByActorId: string | null;
  assessedAt: string | null;
  assessmentOutcome: 'upheld' | 'overridden' | null;
  assessmentReason: string | null;
  compellingGroundsText: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listObjectionJobs(params: {
  status?: ObjectionJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: ObjectionJob[];
  byStatus: {
    pending: number; assessing: number; upheld: number;
    overridden: number; failed: number; cancelled: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/objection-jobs${suffix}`);
}

export async function createObjectionRequest(
  input: {
    principalIdentifier: string;
    identifierKind: ObjectionIdentifierKind;
    objectionGround: ObjectionGround;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: ObjectionJob }> {
  return apiFetch('/v1/compliance/objection-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function startObjectionAssessment(
  id: string, idempotencyKey?: string,
): Promise<{ job: ObjectionJob }> {
  return apiFetch(`/v1/compliance/objection-jobs/${id}/start-assessment`, {
    method: 'POST', body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function assessObjectionJob(
  id: string,
  input: {
    outcome: 'upheld' | 'overridden';
    assessmentReason: string;
    compellingGroundsText?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: ObjectionJob }> {
  return apiFetch(`/v1/compliance/objection-jobs/${id}/assess`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Phase 8 batch G registers ────────────────────────────

// ---- DPIA assessments ----
export type DpiaStatus = 'draft' | 'reviewed' | 'approved' | 'rejected' | 'archived';
export type DpiaLegalBasis =
  | 'consent' | 'contract' | 'legal_obligation'
  | 'vital_interests' | 'public_task' | 'legitimate_interests';
export interface DpiaRiskItem {
  id: string; description: string;
  likelihood: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  residualRisk: 'low' | 'medium' | 'high';
  mitigation?: string;
}
export interface DpiaAssessment {
  id: string; tenantId: string;
  title: string; processingPurpose: string; legalBasis: DpiaLegalBasis;
  dataCategories: string[]; affectedSubjectsEstimate: number | null;
  riskRegister: DpiaRiskItem[]; mitigationPlan: string | null;
  consultationDpo: boolean; consultationLegal: boolean; consultationItAdmin: boolean;
  status: DpiaStatus;
  reviewedByActorId: string | null; reviewedAt: string | null;
  approverActorId: string | null; approvedAt: string | null;
  rejectionReason: string | null; archivedAt: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function listDpias(params: { status?: DpiaStatus; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ dpias: DpiaAssessment[]; byStatus: Record<DpiaStatus, number> }>(
    `/v1/compliance/dpia${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createDpia(
  input: {
    title: string; processingPurpose: string; legalBasis: DpiaLegalBasis;
    dataCategories: string[]; affectedSubjectsEstimate?: number;
    riskRegister: DpiaRiskItem[]; mitigationPlan?: string;
    consultationDpo?: boolean; consultationLegal?: boolean; consultationItAdmin?: boolean;
  },
  idempotencyKey?: string,
): Promise<{ dpia: DpiaAssessment }> {
  return apiFetch('/v1/compliance/dpia', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function transitionDpia(
  id: string,
  input: { action: 'review' | 'approve' | 'reject' | 'archive'; reason?: string },
  idempotencyKey?: string,
): Promise<{ dpia: DpiaAssessment }> {
  return apiFetch(`/v1/compliance/dpia/${id}/transition`, {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ---- CCPA opt-outs ----
export type CcpaOptoutStatus = 'active' | 'revoked';
export type CcpaJurisdiction =
  | 'CA' | 'CO' | 'VA' | 'CT' | 'UT' | 'OR' | 'TX' | 'FL' | 'DE' | 'MT';
export type CcpaRequestChannel =
  | 'webform' | 'authorized_agent' | 'gpc_signal' | 'phone' | 'paper';
export interface CcpaOptout {
  id: string; tenantId: string;
  principalIdentifierHash: string; identifierKind: ErasureIdentifierKind;
  status: CcpaOptoutStatus;
  optOutSale: boolean; optOutSharing: boolean;
  jurisdiction: CcpaJurisdiction;
  requestChannel: CcpaRequestChannel | null; requestReason: string | null;
  requestedByActorId: string;
  revokedByActorId: string | null; revokedAt: string | null; revokeReason: string | null;
  createdAt: string; updatedAt: string;
}
export async function listCcpaOptouts(params: { status?: CcpaOptoutStatus; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ optouts: CcpaOptout[]; byStatus: Record<CcpaOptoutStatus, number> }>(
    `/v1/compliance/ccpa-optouts${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createCcpaOptout(
  input: {
    principalIdentifier: string; identifierKind: ErasureIdentifierKind;
    optOutSale?: boolean; optOutSharing?: boolean;
    jurisdiction?: CcpaJurisdiction; requestChannel?: CcpaRequestChannel;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ optout: CcpaOptout }> {
  return apiFetch('/v1/compliance/ccpa-optouts', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function revokeCcpaOptout(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ optout: CcpaOptout }> {
  return apiFetch(`/v1/compliance/ccpa-optouts/${id}/revoke`, {
    method: 'POST', body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ---- Service providers ----
export type ProviderStatus = 'active' | 'paused' | 'offboarded';
export type ProviderRole = 'processor' | 'sub_processor' | 'joint_controller' | 'controller';
export type TransferMechanism =
  | 'adequacy' | 'sccs_2021' | 'sccs_2010' | 'bcrs' | 'derogation' | 'none';
export interface ServiceProvider {
  id: string; tenantId: string;
  vendorName: string; vendorRole: ProviderRole;
  jurisdiction: string; primaryContactEmail: string | null;
  dataCategories: string[]; processingPurposes: string[];
  dpaOnFile: boolean; dpaSignedAt: string | null; dpaReviewDueAt: string | null;
  transferMechanism: TransferMechanism | null;
  lastAuditAt: string | null; nextAuditDueAt: string | null;
  status: ProviderStatus;
  pausedReason: string | null;
  offboardedAt: string | null; offboardedReason: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function listProviders(params: { status?: ProviderStatus; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ providers: ServiceProvider[]; byStatus: Record<ProviderStatus, number> }>(
    `/v1/compliance/service-providers${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createProvider(
  input: Partial<Omit<ServiceProvider, 'id' | 'tenantId' | 'createdByActorId' | 'createdAt' | 'updatedAt'>> & {
    vendorName: string; vendorRole: ProviderRole; jurisdiction: string;
  },
  idempotencyKey?: string,
): Promise<{ provider: ServiceProvider }> {
  return apiFetch('/v1/compliance/service-providers', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function patchProvider(
  id: string,
  input: Partial<Omit<ServiceProvider, 'id' | 'tenantId' | 'createdByActorId' | 'createdAt' | 'updatedAt'>>,
): Promise<{ provider: ServiceProvider }> {
  return apiFetch(`/v1/compliance/service-providers/${id}`, {
    method: 'PATCH', body: JSON.stringify(input),
  });
}
export async function offboardProvider(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ provider: ServiceProvider }> {
  return apiFetch(`/v1/compliance/service-providers/${id}/offboard`, {
    method: 'POST', body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ---- SDF obligations (singleton per tenant) ----
export interface SdfObligation {
  id: string; tenantId: string;
  isDesignatedSdf: boolean;
  designationBasis: string | null; designatedAt: string | null;
  dpoAppointed: boolean;
  dpoName: string | null; dpoEmail: string | null; dpoAppointedAt: string | null;
  annualDpiaCompletedAt: string | null; annualDataAuditCompletedAt: string | null;
  otherMeasures: Array<{ id: string; label: string; completedAt?: string; evidenceRef?: string }>;
  lastReviewedByActorId: string | null; lastReviewedAt: string | null; lastReviewerNote: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function getSdf(): Promise<{ sdf: SdfObligation | null }> {
  return apiFetch('/v1/compliance/sdf-obligations');
}
export async function upsertSdf(
  input: Partial<Omit<SdfObligation, 'id' | 'tenantId' | 'createdByActorId' | 'createdAt' | 'updatedAt' | 'lastReviewedByActorId' | 'lastReviewedAt'>>,
): Promise<{ sdf: SdfObligation }> {
  return apiFetch('/v1/compliance/sdf-obligations', {
    method: 'PUT', body: JSON.stringify(input),
  });
}

// ---- Sensitive-PI tier register ----
export type SensitivePiLawfulBasis =
  | 'art9_2_b_employment' | 'art9_2_c_vital' | 'art9_2_g_substantial_public'
  | 'art9_2_h_health' | 'art9_2_i_public_health' | 'art9_2_j_research';
export interface SensitivePiTier {
  id: string; tenantId: string;
  category: string; inScope: boolean;
  retentionDays: number | null;
  consentRequired: boolean; lawfulBasisOverride: SensitivePiLawfulBasis | null;
  processingRestrictions: Array<{ id: string; label: string; enforcedBy?: string }>;
  geoRestrictions: string[];
  lastReviewedByActorId: string | null; lastReviewedAt: string | null; lastReviewerNote: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function listSensitivePiTiers(params: { inScope?: boolean; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (typeof params.inScope === 'boolean') q.set('inScope', String(params.inScope));
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ tiers: SensitivePiTier[]; counts: { inScope: number; outOfScope: number } }>(
    `/v1/compliance/sensitive-pi-tiers${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createSensitivePiTier(
  input: {
    category: string; inScope?: boolean; retentionDays?: number;
    consentRequired?: boolean; lawfulBasisOverride?: SensitivePiLawfulBasis;
    processingRestrictions?: Array<{ id: string; label: string; enforcedBy?: string }>;
    geoRestrictions?: string[];
  },
  idempotencyKey?: string,
): Promise<{ tier: SensitivePiTier }> {
  return apiFetch('/v1/compliance/sensitive-pi-tiers', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function updateSensitivePiTier(
  id: string,
  input: Partial<Omit<SensitivePiTier, 'id' | 'tenantId' | 'category' | 'createdByActorId' | 'createdAt' | 'updatedAt'>>,
): Promise<{ tier: SensitivePiTier }> {
  return apiFetch(`/v1/compliance/sensitive-pi-tiers/${id}`, {
    method: 'PATCH', body: JSON.stringify(input),
  });
}

// ── Compliance / Breach dispatch (Phase 8 batch B item 13) ────────────

export type BreachStatus =
  | 'draft' | 'acknowledged' | 'notified_dpb' | 'notified_data_principals'
  | 'closed' | 'false_positive';

export type BreachRegulator =
  | 'dpdpa' | 'gdpr' | 'rbi' | 'sec_5b' | 'hipaa' | 'pipl' | 'lgpd' | 'pdpa_sg' | 'pipeda';

export interface BreachIncident {
  id: string;
  tenantId: string;
  decisionId: string;
  requestId: string;
  breachWindowStart: string | null;
  breachWindowEnd: string | null;
  /** null is "not established" (migration 0138), never 0. */
  affectedRecordCount: number | null;
  affectedPrincipalsCount: number | null;
  dataCategories: string[];
  industryAnchor: string;
  recommendedSeverity: string;
  severityNumeric: number;
  status: BreachStatus;
  draftPayload: Record<string, unknown>;
  notificationsRendered: Record<string, string>;
  acknowledgedByUserId: string | null;
  acknowledgedAt: string | null;
  notifiedDpbAt: string | null;
  notifiedDataPrincipalsAt: string | null;
  closedAt: string | null;
  remediationSummary: string | null;
  postIncidentReviewSummary: string | null;
  createdAt: string;
  updatedAt: string;
  // Q34: set when an operator confirmed a DDR incident as a breach.
  ddrIncidentId?: string | null;
  confirmedAt?: string | null;
  confirmedByUserId?: string | null;
  corePushStatus?: string | null;
  regulatoryClocks?: import('./detection-response-api').BreachClock[];
}

export async function listBreachIncidents(params: {
  status?: BreachStatus;
  limit?: number;
} = {}): Promise<{
  incidents: BreachIncident[];
  byStatus: {
    draft: number; acknowledged: number; notified_dpb: number;
    notified_data_principals: number; closed: number; false_positive: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/breach-incidents${suffix}`);
}

export async function getBreachIncident(id: string): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}`);
}

export async function acknowledgeBreach(
  id: string,
  reason?: string,
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function notifyBreachDpb(
  id: string,
  input: { regulators?: BreachRegulator[]; channelEvidence?: string } = {},
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/notify-dpb`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function notifyBreachDataPrincipals(
  id: string,
  input: { channelEvidence?: string } = {},
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/notify-data-principals`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function closeBreach(
  id: string,
  input: { remediationSummary: string; postIncidentReviewSummary: string },
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/close`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Egress log (Phase 5b item 8) ──────────────────────────────────────

/** Release 2.1 item 4: the egress-log cards over every matching row (server). */
export async function getEgressLogSummary(payloadKind?: EgressPayloadKind): Promise<{
  rows: number;
  totalBytes: number;
  distinctDestinations: number;
  byKind: Record<string, number>;
  byDestination: Record<string, number>;
}> {
  const suffix = payloadKind ? `?payloadKind=${encodeURIComponent(payloadKind)}` : '';
  return apiFetch(`/v1/foundational/egress-log/summary${suffix}`);
}

export async function listEgressLog(
  params: { cursor?: string; limit?: number; payloadKind?: EgressPayloadKind } = {},
): Promise<{
  entries: EgressLogEntry[];
  nextCursor: string | null;
}> {
  const q = new URLSearchParams();
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.payloadKind) q.set('payloadKind', params.payloadKind);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/foundational/egress-log${suffix}`);
}

export async function verifyEgressLog(): Promise<{
  valid: boolean;
  rowsVerified: number;
  firstBreakAt: string | null;
  message: string;
}> {
  return apiFetch(`/v1/foundational/egress-log/verify`);
}

/**
 * Wire shape returned by GET /v1/foundational/rbam. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (RbamMappingSchema). A mapping pins ONE role (either a tenant-authored
 * custom roleId OR a built-in role label like 'dpo' / 'compliance_analyst')
 * to a scope: purposes, allowedSystems, allowedDataClasses, businessUnits.
 *
 * NOTE 2026-05-16 - prior shape declared here was a guess that did not
 * match the server response (it had .roleName / .permissions / .scope.bu
 * / .custom / .approverIds, none of which the server returns). Corrected.
 */
export interface RbamMapping {
  id: string;
  tenantId: string;
  roleId: string | null;
  roleBuiltIn: string | null;
  purposes: string[];
  allowedSystems: string[];
  allowedDataClasses: string[];
  businessUnitScope: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UpsertRbamMappingInput {
  roleId?: string | null;
  roleBuiltIn?: string | null;
  purposes?: string[];
  allowedSystems?: string[];
  allowedDataClasses?: string[];
  businessUnitScope?: string[];
}

export async function listRbamMappings(): Promise<{ rbamMappings: RbamMapping[] }> {
  return apiFetch('/v1/foundational/rbam');
}

export async function createRbamMapping(
  input: UpsertRbamMappingInput,
  idempotencyKey?: string,
): Promise<{ rbamMapping: RbamMapping }> {
  return apiFetch('/v1/foundational/rbam', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Custom roles (Phase 5b item #5) ────────────────────────────────────

export type CustomRoleStatus = 'draft' | 'approved' | 'revoked';

export interface RbamScopeEnvelope {
  businessUnit?: 'all' | string[];
  system?: 'all' | string[];
  dataClass?: 'all' | string[];
  purpose?: 'all' | string[];
}

export interface TenantCustomRole {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  permissions: string[];
  scopeEnvelope: RbamScopeEnvelope;
  status: CustomRoleStatus;
  createdByActorId: string;
  approvedByActorId: string | null;
  approvedAt: string | null;
  approverReason: string | null;
  revokedByActorId: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomRoleInput {
  name: string;
  description?: string;
  permissions: string[];
  scopeEnvelope?: RbamScopeEnvelope;
}

export async function listCustomRoles(
  params: { status?: CustomRoleStatus; limit?: number } = {},
): Promise<{
  customRoles: TenantCustomRole[];
  byStatus: { draft: number; approved: number; revoked: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/foundational/custom-roles${suffix}`);
}

export async function getCustomRole(id: string): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch(`/v1/foundational/custom-roles/${id}`);
}

export async function createCustomRole(
  input: CreateCustomRoleInput,
  idempotencyKey?: string,
): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch('/v1/foundational/custom-roles', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveCustomRole(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch(`/v1/foundational/custom-roles/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function revokeCustomRole(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch(`/v1/foundational/custom-roles/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * Wire shape returned by GET /v1/foundational/purposes. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (PurposeMappingSchema). Each row binds an observed source
 * (application / endpoint / service_account / role / sql_tag /
 * bi_report / ropa_id / pia_id) to a declared processing purpose +
 * legal basis + confidence. This is how Discovery resolves "what is
 * this query for?" at scan-time.
 *
 * NOTE 2026-05-16 - prior shape declared here was a guess (key /
 * displayName / dataClasses / legalBases / retentionDays /
 * crossBorderAllowed) that did not match the server response. Corrected
 * to match PurposeMappingSchema.
 */
export type PurposeSourceType =
  | 'application'
  | 'endpoint'
  | 'service_account'
  | 'role'
  | 'sql_tag'
  | 'bi_report'
  | 'ropa_id'
  | 'pia_id';

export type PurposeConfidence = 'high' | 'medium' | 'low';

export interface PurposeMapping {
  id: string;
  tenantId: string;
  sourceType: PurposeSourceType;
  sourceIdentifier: string;
  purposeName: string;
  legalBasis: string | null;
  confidence: PurposeConfidence;
  evidence: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertPurposeMappingInput {
  sourceType: PurposeSourceType;
  sourceIdentifier: string;
  purposeName: string;
  legalBasis?: string | null;
  confidence?: PurposeConfidence;
  evidence?: string | null;
}

export async function listPurposes(): Promise<{ purposeMappings: PurposeMapping[] }> {
  return apiFetch('/v1/foundational/purposes');
}

export async function createPurpose(
  input: UpsertPurposeMappingInput,
  idempotencyKey?: string,
): Promise<{ purposeMapping: PurposeMapping }> {
  return apiFetch('/v1/foundational/purposes', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function resolvePurpose(input: {
  sourceType: PurposeSourceType;
  sourceIdentifier: string;
}): Promise<{ resolved: PurposeMapping | null; candidates: PurposeMapping[] }> {
  return apiFetch('/v1/foundational/purposes/resolve', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Wire shape returned by GET /v1/foundational/adapters/:axis. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (AdapterSchema). The four axes are first-class per CLAUDE.md
 * Architecture Rule 13 (symmetric-adapter architecture):
 *   • cmp  - alternative CMP that Discovery routes consent into
 *   • dspm - alternative DSPM that FOCTTA core routes data-discovery into
 *   • dam  - alternative DAM
 *   • ddr  - alternative DDR
 *
 * NOTE 2026-05-16 - prior axis values ('alt_dspm' / 'alt_dam' / 'alt_ddr')
 * did not match the server enum ('dspm' / 'dam' / 'ddr'). Prior row shape
 * (vendorKey / displayName / authMethod / configKmsRef) also did not match.
 * Corrected to match AdapterSchema.
 */
export type AdapterAxis = 'cmp' | 'dspm' | 'dam' | 'ddr';
export type AdapterStatus = 'active' | 'paused' | 'error';

export interface Adapter {
  id: string;
  tenantId: string;
  axis: AdapterAxis;
  vendor: string;
  configuration: Record<string, unknown>;
  webhookSecretCredentialId: string | null;
  status: AdapterStatus;
  cacheTtlSeconds: number;
  failOpenOnUnavailable: boolean;
  pullDirection: Record<string, boolean>;
  pushDirection: Record<string, boolean>;
  lastSyncAt: string | null;
  lastConformanceTestRunId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertAdapterInput {
  vendor: string;
  configuration?: Record<string, unknown>;
  webhookSecretCredentialId?: string | null;
  status?: AdapterStatus;
  cacheTtlSeconds?: number;
  failOpenOnUnavailable?: boolean;
  pullDirection?: Record<string, boolean>;
  pushDirection?: Record<string, boolean>;
}

export async function listAdapters(axis: AdapterAxis): Promise<{ adapters: Adapter[] }> {
  return apiFetch(`/v1/foundational/adapters/${axis}`);
}

export async function createAdapter(
  axis: AdapterAxis,
  input: UpsertAdapterInput,
  idempotencyKey?: string,
): Promise<{ adapter: Adapter }> {
  return apiFetch(`/v1/foundational/adapters/${axis}`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Foundational / Business Units (Phase 5b items 1-3) ─────────────────

export type BusinessUnitSource = 'local' | 'foctta_core' | 'system';
export type BusinessUnitStatus = 'active' | 'archived' | 'system';

export interface BusinessUnit {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  parentId: string | null;
  description: string | null;
  primaryOwnerUserId: string | null;
  status: BusinessUnitStatus;
  source: BusinessUnitSource;
  externalRef: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present when listed with `withIndustry`: the unit's packs and why. */
  industry?: BuIndustrySummary;
}

/** A unit's industry: its own pack rows (with origin) and the Discovery packs in force. */
export interface BuIndustrySummary {
  mode: 'override' | 'inherited' | 'tenant_default';
  inheritedFromBuId: string | null;
  fallThroughReason: 'unit_packs_carry_no_classifiers' | null;
  unitPacks: Array<{ id: string; source: string }>;
  packs: Array<PackProvenanceRow & { name: string }>;
  bundles: string[];
  unmapped: string[];
  suppressedByOverlay: string[];
}

export interface CreateBusinessUnitInput {
  name: string;
  slug?: string;
  parentId?: string | null;
  description?: string | null;
  primaryOwnerUserId?: string | null;
}

export interface UpdateBusinessUnitInput {
  name?: string;
  slug?: string;
  parentId?: string | null;
  description?: string | null;
  primaryOwnerUserId?: string | null;
  status?: 'active' | 'archived';
}

export interface ListBusinessUnitsFilter {
  /** Include each unit's industry packs and their origin. */
  withIndustry?: boolean;
  source?: BusinessUnitSource;
  status?: BusinessUnitStatus;
  /** 'root' = top-level BUs (parentId IS NULL); uuid = specific parent. */
  parentId?: string | 'root';
}

export interface ResyncFromCoreResponse {
  added: number;
  skippedExisting: number;
  localUntouched: number;
  sourceEventId: string | null;
  message: string;
}

export async function listBusinessUnits(
  filter: ListBusinessUnitsFilter = {},
): Promise<{ businessUnits: BusinessUnit[] }> {
  const params = new URLSearchParams();
  if (filter.source) params.set('source', filter.source);
  if (filter.status) params.set('status', filter.status);
  if (filter.parentId) params.set('parentId', filter.parentId);
  if (filter.withIndustry) params.set('withIndustry', '1');
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/v1/foundational/business-units${suffix}`);
}

export async function getBusinessUnit(
  id: string,
): Promise<{ businessUnit: BusinessUnit; children: BusinessUnit[] }> {
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(id)}`);
}

export async function createBusinessUnit(
  input: CreateBusinessUnitInput,
  idempotencyKey?: string,
): Promise<{ businessUnit: BusinessUnit }> {
  return apiFetch('/v1/foundational/business-units', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function updateBusinessUnit(
  id: string,
  patch: UpdateBusinessUnitInput,
): Promise<{ businessUnit: BusinessUnit }> {
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function archiveBusinessUnit(
  id: string,
): Promise<{ businessUnit: BusinessUnit }> {
  // Empty {} body is required because apiFetch sets Content-Type:
  // application/json by default, and Fastify's strict parser rejects an
  // empty body with that header. DELETE has no body semantics here, but
  // the wire shape needs to satisfy the content-type parser.
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: JSON.stringify({}),
  });
}

export async function resyncBusinessUnitsFromFocttaCore(
  idempotencyKey?: string,
): Promise<ResyncFromCoreResponse> {
  return apiFetch('/v1/foundational/business-units/resync-from-foctta-core', {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Discovery (additional routes - finding detail, scans CRUD, etc.) ───

export interface DiscoveryFindingDetail extends DiscoveryFinding {
  assetId: string | null;
  toxicCombinations: string[];
  evidenceSources: string[];
  resolutionNote: string | null;
  updatedAt: string;
}

export async function getDiscoveryFinding(id: string): Promise<DiscoveryFindingDetail> {
  return apiFetch(`/v1/discovery/findings/${encodeURIComponent(id)}`);
}

// F-2.8 - per-finding drill-down lineage (T1.3 surfaced 2026-05-29).
// GET /v1/discovery/findings/:id/lineage → finding-lineage.ts:FindingLineage.
// Each section degrades to an empty list when its source table/column
// isn't present, so the drawer renders rather than 500ing.
export interface FindingLineage {
  findingId: string;
  assetChain: Array<{ kind: string; id: string; label: string }>;
  linkedDecisions: Array<{
    id: string;
    requestId: string | null;
    decision: string;
    decidedAt: string;
    explanation: string | null;
  }>;
  linkedBreaches: Array<{
    id: string;
    severity: string;
    status: string;
    openedAt: string;
    affectedPrincipalsCount: number | null;
  }>;
  activityHistory: Array<{
    ts: string;
    eventType: string;
    actorId: string | null;
    summary: string;
  }>;
}

export async function getFindingLineage(id: string): Promise<FindingLineage> {
  return apiFetch<FindingLineage>(`/v1/discovery/findings/${encodeURIComponent(id)}/lineage`);
}

export async function patchDiscoveryFindingStatus(
  id: string,
  status: DiscoveryFinding['status'],
  resolutionNote?: string,
): Promise<DiscoveryFindingDetail> {
  return apiFetch(`/v1/discovery/findings/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, resolutionNote }),
  });
}

/**
 * Discovery scan. Wire shape matches server's ScanSchema in
 * apps/api-controller/src/modules/discovery/schemas.ts:209.
 * Previous client interface had completely different field names
 * (`connectorInstanceId / startedAt / finishedAt / itemsScanned`)
 * which would have silently failed at runtime - same drift class as
 * Section9 + asset + scan-trigger bugs fixed in the hardening pass.
 */
export interface DiscoveryScan {
  id: string;
  tenantId: string;
  scanMode: 'sketch' | 'full' | 'forensic';
  targetAssetIds: string[];
  targetSourceTypes: string[];
  classifierTiers: number[];
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string | null;
  completedAt: string | null;
  rowsScanned: number;
  findingsCreated: number;
  confidentialVmAttestationId: string | null;
  triggeredByUserId: string | null;
  errorMessage: string | null;
  triggeredByReason: string | null;
  breachId: string | null;
  incidentRef: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listDiscoveryScans(): Promise<{ scans: DiscoveryScan[] }> {
  return apiFetch('/v1/discovery/scans');
}

export async function getDiscoveryScan(id: string): Promise<DiscoveryScan> {
  return apiFetch(`/v1/discovery/scans/${encodeURIComponent(id)}`);
}

/**
 * Trigger a Discovery scan. Wire shape matches the server's
 * `TriggerScanSchema` in
 * apps/api-controller/src/modules/discovery/schemas.ts:202 - NOT the
 * earlier guess that used `connectorInstanceId / scanProfile`. The
 * shape mismatch was found during the Phase-2 hardening pass (it's
 * the same class of bug as the Section9 type drift; cf. tracker §9
 * OQ-2026-05-14-A).
 */
export interface TriggerScanInput {
  /** Server-side ScanModeSchema enum at schemas.ts:33 - one of 'sketch' / 'full' / 'forensic'. */
  scanMode: 'sketch' | 'full' | 'forensic';
  targetAssetIds?: string[];
  targetSourceTypes?: string[];
  classifierTiers?: number[];
}

export async function triggerDiscoveryScan(
  input: TriggerScanInput,
  idempotencyKey?: string,
): Promise<DiscoveryScan> {
  return apiFetch('/v1/discovery/scans', {
    method: 'POST',
    body: JSON.stringify({
      scanMode: input.scanMode,
      targetAssetIds: input.targetAssetIds ?? [],
      targetSourceTypes: input.targetSourceTypes ?? [],
      classifierTiers: input.classifierTiers ?? [1, 2, 3, 4],
    }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * Finalize a scan (completed / failed / cancelled). Wire shape matches the
 * server's CompleteScanSchema. Maps to POST /v1/discovery/scans/:id/complete
 * - the terminal transition that stamps completedAt + rowsScanned. The
 * resolver advances queued→running on ingest; this closes the run.
 */
export async function completeDiscoveryScan(
  id: string,
  patch: { status?: 'completed' | 'failed' | 'cancelled'; rowsScanned?: number; errorMessage?: string | null } = {},
): Promise<{ scan: DiscoveryScan }> {
  return apiFetch(`/v1/discovery/scans/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    body: JSON.stringify({ status: patch.status ?? 'completed', ...patch }),
  });
}

/**
 * Live catalog crawl - auto-discover the FULL schema/table/column catalog from
 * one DB connection. Maps to POST /v1/discovery/catalog-crawl. The "give creds,
 * we find everything inside" engine: registers every table as an asset +
 * classifies + flags PII, inside a tracked scan.
 */
export interface CatalogCrawlInput {
  connection: {
    vendor: 'postgres' | 'mssql' | 'oracle';
    host: string;
    port: number;
    database: string;
    user: string;
    password: string;
    /** TLS. SQL Server: on unless explicitly false. */
    ssl?: boolean;
    /** SQL Server only: skip certificate verification (dev/lab). Default false. */
    trustServerCertificate?: boolean;
    includeSchemas?: string[];
  };
  scanMode?: 'sketch' | 'full' | 'forensic';
  /** T2 - opt-in value-level checksum sampling ("second discovery layer"). */
  sampleValues?: boolean;
  /** Rows sampled per column when sampleValues=true (default 50). */
  sampleSize?: number;
}

export interface CatalogCrawlSummary {
  scanId: string;
  vendor: string;
  authority: string;
  schemas: string[];
  tableCount: number;
  columnCount: number;
  assetsCreated: number;
  assetsUpdated: number;
  columnsRegistered: number;
  piiFindings: number;
  /** T2 - columns classified by VALUE sampling. */
  valueFindings: number;
  /** T2 - value hits whose column name did NOT match (caught only by sampling). */
  valueOnlyFindings: number;
  status: string;
}

export async function catalogCrawl(input: CatalogCrawlInput): Promise<{ crawl: CatalogCrawlSummary }> {
  return apiFetch('/v1/discovery/catalog-crawl', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ── T2 control-plane→edge dispatch loop (2026-05-29) ─────────────────
// Operators enqueue scan jobs; edge pods (inside the customer VPC) claim
// the next queued job, scan locally, ingest findings, and mark complete.
export interface ScanJob {
  id: string;
  tenantId: string;
  status: 'queued' | 'claimed' | 'running' | 'completed' | 'failed';
  target: Record<string, unknown>;
  scanMode: string;
  sampleValues: boolean;
  claimedBy: string | null;
  claimedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  findingsCount: number | null;
  error: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function enqueueScanJob(input: {
  target: Record<string, unknown>;
  scanMode?: 'sketch' | 'full' | 'forensic';
  sampleValues?: boolean;
  /** The agent that runs it. Without one the job is only recorded. */
  podId?: string;
}): Promise<{ job: ScanJob; dispatched: boolean; commandId?: string; note?: string }> {
  return apiFetch('/v1/discovery/scan-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function listScanJobs(): Promise<{ jobs: ScanJob[] }> {
  return apiFetch('/v1/discovery/scan-jobs');
}

// ── T3 DSAR erasure execution (2026-05-29) ───────────────────────────
// Dry-run by default: preview the rows that WOULD be anonymised; pass
// dryRun:false to SET NULL the principal's PII columns. Hash-chained.
export interface DsarEraseTarget {
  schema: string;
  table: string;
  matchColumn: string;
  eraseColumns: string[];
}
export interface DsarEraseSummary {
  dryRun: boolean;
  principalIdentifierHash: string;
  previews?: Array<{ schema: string; table: string; matchColumn: string; eraseColumns: string[]; matchedRows: number }>;
  results?: Array<{ schema: string; table: string; matchColumn: string; eraseColumns: string[]; rowsAnonymized: number }>;
  totalRowsAffected: number;
}
export async function runDsarErasure(input: {
  connection: {
    vendor: 'postgres' | 'mssql' | 'oracle';
    host: string; port: number; database: string; user: string; password: string; ssl?: boolean;
  };
  principalValue: string;
  targets: DsarEraseTarget[];
  dryRun: boolean;
}): Promise<{ erasure: DsarEraseSummary }> {
  return apiFetch('/v1/discovery/dsar/erase', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ── Time-Travel Discovery (#2) - immutable posture snapshots + diff ───────

export interface DiscoverySnapshot {
  id: string;
  snapshotDate: string;
  assetCount: number;
  findingCountBySeverity: Record<string, number>;
  classificationByLabel: Record<string, number>;
  classificationBySourceType: Record<string, number>;
  createdAt: string;
}

export interface SnapshotDiff {
  from: { date: string; assetCount: number } | null;
  to: { date: string; assetCount: number };
  assetCountDelta: number;
  findingSeverityDelta: Record<string, number>;
  classificationLabelDelta: Record<string, { from: number; to: number; delta: number }>;
  addedLabels: string[];
  removedLabels: string[];
}

export async function takeDiscoverySnapshot(snapshotDate?: string): Promise<{ snapshot: DiscoverySnapshot }> {
  return apiFetch('/v1/discovery/snapshots', {
    method: 'POST',
    body: JSON.stringify(snapshotDate ? { snapshotDate } : {}),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function listDiscoverySnapshots(): Promise<{ snapshots: DiscoverySnapshot[] }> {
  return apiFetch('/v1/discovery/snapshots');
}

export async function diffDiscoverySnapshots(from: string, to: string): Promise<{ diff: SnapshotDiff }> {
  return apiFetch(`/v1/discovery/snapshots/diff?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
}

// T2 (2026-05-29) - continuous rediscovery: snapshot now + diff vs prior,
// alerting on drift. Maps to POST /v1/discovery/rediscovery/run.
export interface RediscoveryResult {
  snapshotId: string;
  snapshotDate: string;
  assetCount: number;
  firstSnapshotToday: boolean;
  driftDetected: boolean;
  assetCountDelta: number;
  criticalDelta: number;
  addedLabels: string[];
  removedLabels: string[];
  alerted: boolean;
}

export async function runRediscovery(): Promise<{ rediscovery: RediscoveryResult }> {
  return apiFetch('/v1/discovery/rediscovery/run', {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Wire shape returned by GET /v1/discovery/section-9-report. Server-side
 * source: apps/api-controller/src/modules/discovery/service.ts:generateSection9Report
 * which delegates to repository.ts:aggregateSection9Counts.
 *
 * Note: the prior shape declared here (`bu_breakdown` / `totalMinorRecords`
 * / `generatedAt`) did NOT match the server - that mismatch crashed the
 * Section 9 page with "Cannot read properties of undefined (reading
 * 'length')" until corrected in the Phase-2-part-2 hardening pass.
 */
export interface Section9Report {
  totalMinorRecordsDetected: number;
  bySystem: Array<{ sourceType: string; minorRecordCount: number }>;
  byBusinessUnit: Array<{ businessUnitId: string | null; minorRecordCount: number }>;
  /**
   * Phase 6 close-out (2026-05-16) - per-pack §9 coverage view.
   * For each tenant-activated pack, surfaces how many minor-flagged
   * assets fall inside the pack's classifier vocab + a coverage score
   * + a green/amber/red tri-state.
   */
  byPack?: Array<{
    packId: string;
    packLabel: string;
    assetCount: number;
    coverageScore: number;
    // 'unknown' = the coverage ratio is undefined (0/0, no minor records
    // detected), which is not the same as zero coverage.
    coverageState: 'green' | 'amber' | 'red' | 'unknown';
  }>;
  openFindings: number;
  remediatedFindings: number;
  reportGeneratedAt: string;
  asOfDate: string;
  regulations: string[];
}

export async function getSection9Report(): Promise<Section9Report> {
  return apiFetch('/v1/discovery/section-9-report');
}

/**
 * Coverage = the real shape returned by GET /v1/discovery/coverage
 * (service.getCoverage). It returns scan/classification counts +
 * per-framework coverage. NOTE: the endpoint does NOT return `byCategory`,
 * `bySeverity`, or a `posture{dpdpa/rbi/sebi}` object - those were declared
 * here historically but never sent by the backend, so the dashboard derives
 * PII-by-type from /assets and severity from /findings instead. Kept optional
 * below only so any forward-compat consumer compiles.
 */
export interface DiscoveryCoverage {
  totalAssets: number;
  scannedAssets: number;
  classifiedAssets: number;
  byFramework: Array<{ framework: string; coverageScore: number; openFindings: number }>;
  byIndustryPack: Array<{ industryPackId: string; activated: boolean; contributingFindings: number }>;
  generatedAt: string;
  // Not currently returned by the endpoint - optional, do not rely on them.
  byCategory?: Record<string, number>;
  bySeverity?: Record<DiscoveryFinding['severity'], number>;
  posture?: { score: number; dpdpaScore: number; rbiScore: number; sebiScore: number };
}

export async function getDiscoveryCoverage(): Promise<DiscoveryCoverage> {
  return apiFetch('/v1/discovery/coverage');
}

/** Unified data-risk ranking - shape LOCKED by DSPM-RISK-SIGNALS-CONTRACT v1.0. */
export interface RiskRankedAsset {
  assetRef: string;
  name: string;
  score: number;
  band: string;
  topFactors: string[];
  findingCounts: { critical: number; high: number; medium: number; low: number };
  sourceType: string;
  orphan: boolean;
}
export interface DiscoveryRiskRanking {
  items: RiskRankedAsset[];
  bandSummary: Record<string, number>;
  total: number;
  limit: number;
  offset: number;
  generatedAt: string;
}
export async function getDiscoveryRiskRanking(
  opts: { excludeLaptopFiles?: boolean } = {},
): Promise<DiscoveryRiskRanking> {
  // Release 2.1 item 16: the dashboard leaves laptop files out (one asset per
  // file, about a million at 13,000 laptops) and ranks laptops per device from
  // the fleet rollups instead.
  return apiFetch(`/v1/discovery/risk-ranking${opts.excludeLaptopFiles ? '?excludeLaptopFiles=true' : ''}`);
}

/** Board-report summary - the canonical DSPM roll-up (also retrieved by Core
 *  to embed in its board report). Mirrors the api-controller BoardSummary. */
export interface DiscoveryBoardSummary {
  generatedAt: string;
  posture: { score: number; band: string };
  exposures: {
    criticalOpen: number;
    highOpen: number;
    topRisks: Array<{ name: string; assetRef: string; score: number; band: string; sourceType: string; orphan: boolean; critical: number; high: number }>;
  };
  classification: { totalAssets: number; classifiedAssets: number; classifiedPct: number; scannedPct: number };
  childrensData: { minorRecordsDetected: number; status: 'none' | 'detected' };
  compliance: Array<{ framework: string; coverageScore: number; openFindings: number }>;
}

export async function getDiscoveryBoardSummary(): Promise<DiscoveryBoardSummary> {
  return apiFetch('/v1/discovery/board-summary');
}

/** A connector config field as described by the registry. */
export interface DiscoveryConnectorField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  enum?: string[];
}

/**
 * Real shape returned by GET /v1/discovery/connectors/registry. (The prior
 * type declared vendorKey/description/supportsScan which the endpoint never
 * sends - `kind`/`discoveryCapability`/`configFields` are the real fields, so
 * those UI bits were silently blank.)
 */
export interface DiscoveryConnector {
  kind: string;
  displayName: string;
  category: 'relational' | 'warehouse' | 'nosql' | 'object_storage' | 'm365' | 'workspace' | 'cache' | 'streaming' | 'saas' | 'mainframe' | 'file_share';
  tier: number;
  authMethod: string;
  configFields: DiscoveryConnectorField[];
  discoveryCapability: { directScan: boolean; noDbAccessMode?: boolean; attestationSupported?: boolean };
  activityCapability?: Record<string, boolean>;
  industryHints?: string[];
  canonicalAssetPrefix?: string;
}

export async function listDiscoveryConnectorRegistry(): Promise<{ connectors: DiscoveryConnector[] }> {
  return apiFetch('/v1/discovery/connectors/registry');
}

// ── Phase 2 part 2 additions (Classifier / Lineage / Bootstrap /
//    HoneyPii / NoDbAccess / ReverseDsar export) ─────────────────

export interface ClassifierDetectorRow {
  /** How this built-in relates to the resolved industry pack. Advisory only. */
  relevance?: 'core' | 'not_typical' | 'general';
  name: string;
  label: string;
  tier: number;
  indiaSpecific: boolean;
  checksum: string | null;
  regulations: string[];
  /** Modular Rule Engine: per-tenant enabled state (absence = enabled). */
  enabled?: boolean;
}

export interface CustomDetectorRow {
  id: string;
  label: string;
  patternKind: string;
  pattern: string;
  checksum: string;
  enabled: boolean;
  confidenceBoost: number | string;
  packId?: string;
  /**
   * Q26 (migration 0225): the last time this rule was NOT EVALUATED on a
   * classify call and why. `refused`: the linear engine will not run the
   * stored pattern, so the rule detects nothing until it is rewritten.
   * `unevaluated`: the call's step budget ran out before the rule finished.
   */
  notEvaluatedStatus?: 'refused' | 'unevaluated' | null;
  notEvaluatedReason?: string | null;
  notEvaluatedAt?: string | null;
}

export interface ClassifierCatalogue {
  industryPacks?: ClassifierPackSection;
  detectors: ClassifierDetectorRow[];
  tier2Available: boolean;
  tier3Available: boolean;
  tier4Available: boolean;
  tier5Available: boolean;
  tenantOverlay: { customDetectors: ReadonlyArray<CustomDetectorRow> };
}

export interface PackDetectorRow {
  /** false when switched off for this unit. */
  enabled?: boolean;
  scope?: DetectorScope;
  label: string;
  pack: string;
  /**
   * column-name vocabulary vs a value shape - they match different things. A
   * `value-pattern-named-column` counts only in a column the label's terms name.
   */
  match: 'column-name' | 'value-pattern' | 'value-pattern-named-column';
  pattern: string;
  confidence: number;
}
export type DetectorScope = 'business_unit' | 'tenant';

export interface ClassifierPackSection {
  packIds: string[];
  /** Ids with no classification mapping at all - the only true "contributes nothing". */
  inertPackIds: string[];
  resolution: 'override' | 'inherited' | 'tenant_default';
  inheritedFromBuId: string | null;
  /** The unit's own stored rows and where each came from. */
  unitPacks?: Array<{ id: string; source: string }>;
  /** Why each pack in force is on. */
  provenance?: PackProvenanceRow[];
  /** Regulatory bundles on the unit; they carry no detectors by design. */
  bundles?: string[];
  /** Consent parents narrowed away by one of their overlays. */
  suppressedByOverlay?: string[];
  /** Set when the unit's own rows carry no classifiers, so a parent's or the tenant default applies. */
  fallThroughReason?: 'unit_packs_carry_no_classifiers' | null;
  detectors: PackDetectorRow[];
  /** Per label: column-name phrases that veto its terms. */
  exclusions?: Record<string, string[]>;
}

/**
 * Scoped to a business unit. Tier 1 is identical everywhere; the industry packs
 * are what differ between a hospital unit and a banking one, so the catalogue
 * has to be asked per BU or the page cannot show the difference.
 */
export async function listClassifiers(buId?: string | null): Promise<ClassifierCatalogue> {
  return apiFetch(`/v1/discovery/classifiers${buId ? `?buId=${encodeURIComponent(buId)}` : ''}`);
}

// ── Modular Rule Engine - per-tenant detector enable/disable ──
export async function getDetectorConfig(): Promise<{ config: Record<string, boolean> }> {
  return apiFetch('/v1/discovery/detector-config');
}
export async function setDetectorEnabled(
  label: string,
  enabled: boolean,
  buId?: string | null,
): Promise<{ detectorLabel: string; enabled: boolean; businessUnitId: string | null }> {
  return apiFetch(
    `/v1/discovery/detector-config/${encodeURIComponent(label)}${buId ? `?buId=${encodeURIComponent(buId)}` : ''}`,
    { method: 'PUT', body: JSON.stringify({ enabled }) },
  );
}

/** Drop this unit's own decision so the label follows the tenant default. */
export async function clearDetectorOverride(
  label: string,
  buId: string,
): Promise<{ cleared: boolean }> {
  return apiFetch(
    `/v1/discovery/detector-config/${encodeURIComponent(label)}?buId=${encodeURIComponent(buId)}`,
    { method: 'DELETE' },
  );
}

export interface AccessGraphNode {
  id: string;
  kind: 'asset' | 'finding_category';
  label: string;
  sourceType?: string;
  countryCode?: string | null;
  count?: number;
}
export interface AccessGraphEdge {
  from: string;
  to: string;
  severity: string;
  findingId: string;
}
export interface AccessGraph {
  nodes: AccessGraphNode[];
  edges: AccessGraphEdge[];
  stats: {
    assetCount: number;
    categoryCount: number;
    edgeCount: number;
    rootHint: string | null;
    depthRequested: number;
  };
}

export async function getAccessGraph(params: { root?: string; depth?: number } = {}): Promise<AccessGraph> {
  const q = new URLSearchParams();
  if (params.root) q.set('root', params.root);
  if (params.depth) q.set('depth', String(params.depth));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/discovery/access-graph${suffix}`);
}

// ── SQL-object usage index - "which SQL objects touch this column" ──

/**
 * The sentinel a row carries in `sourceDatabase` when the object lives on a
 * target that names no database at all (matches the controller's
 * `SQL_OBJECT_DATABASE_UNSET` and the scanner's `sql_lineage.rs::ABSENT_DATABASE`).
 *
 * It is a LITERAL and not an empty string on purpose: every real segment is an
 * escaped identifier, so `%absent%` cannot collide with a database actually
 * called that. The UI must render it as an explicit "not applicable" - a blank
 * or a dash would read as "we did not record the database", which is a
 * different and much weaker statement.
 */
export const SQL_OBJECT_DATABASE_UNSET = '%absent%';

/** Closed set - the controller's `SQL_OBJECT_STATUSES`. Do not extend locally. */
export type SqlObjectStatus =
  | 'ok'
  | 'parse_failed'
  | 'partially_parsed'
  | 'unsupported_dialect'
  | 'sidecar_absent'
  // Not sent to the parser because the object was past a scan bound (object
  // count, body size or time budget). `errorClass` names which.
  | 'not_analysed';

/** Closed set - the controller's `SQL_OBJECT_KINDS`. */
export type SqlObjectKind =
  | 'view'
  | 'procedure'
  | 'function'
  | 'package_body'
  | 'job_step'
  | 'materialized_view'
  | 'trigger'
  | 'java_class';

/** The closed codes a run may give for partial coverage (sql-object-ingest.ts). */
export type SqlSourceCoverageWarningCode =
  | 'jpa_entities_not_in_tree'
  | 'jpql_entities_not_found'
  | 'orm_mapping_files_not_read'
  | 'unknown_entity_statements'
  | 'ddl_files_not_read'
  | 'mybatis_mapper_files_not_read'
  | 'other_language_files_not_read'
  | 'resource_files_not_read'
  | 'links_not_followed'
  | 'run_not_finished';

export interface SqlObjectPartialCoverageSource {
  sourceEngine: string;
  sourceHost: string;
  sourcePort: number;
  /** For a Java application, the application name given to the run. */
  sourceDatabase: string;
  /** Codes and counts only. A code this page does not know is shown by its count. */
  warnings: Array<{ code: SqlSourceCoverageWarningCode | (string & {}); count: number }>;
  reportedAt: string;
}

export interface SqlObjectReference {
  /**
   * WHERE the object lives. All four segments together are the location
   * identity per docs/superpowers/specs/2026-09-08-data-location-identity.md -
   * `dbo.vw_customer` on its own names nothing, because two servers can both
   * have one.
   */
  sourceEngine: string;
  sourceHost: string;
  sourcePort: number;
  /** May be `SQL_OBJECT_DATABASE_UNSET`. Never render that value raw. */
  sourceDatabase: string;
  objectKind: SqlObjectKind;
  objectSchema: string;
  objectName: string;
  columnSchema: string | null;
  columnTable: string | null;
  columnName: string;
  /**
   * `referenced` = the object mentions the column (a WHERE filter or a join
   * key counts). `derived` = data actually flowed from it into `targetColumn`.
   *
   * A CLOSED PAIR, like `status` and `direction`, because that is what the wire
   * contract enforces at both ends of the egress boundary
   * (`sql_lineage.rs::ALLOWED_RELATIONSHIPS` and `SQL_REFERENCE_RELATIONSHIPS`).
   * It was typed `string` with a comment saying "Phase 1 writes only
   * `referenced`; a sibling workstream lands `derived`" - which went stale inside
   * the same commit series that landed `derived`.
   */
  relationship: 'referenced' | 'derived';
  direction: 'read' | 'write';
  /**
   * Where the data went, for a `derived` row. `null` for a `referenced` one.
   *
   * The ingest boundary enforces both halves of that as a cross-field rule
   * (`sql-object-ingest.ts`): a `derived` row with a null target is REFUSED,
   * and a `referenced` row carrying a target is refused too. So the pairing is
   * an invariant, not a convention - which is what lets the page render
   * "derived into X" without having to hedge. The page still guards the
   * impossible case rather than rendering nothing, because a silent omission
   * would read as "the data went nowhere".
   *
   * Nullable, not optional: the field is always on the wire
   * (`SqlObjectColumnAnswer` in service.ts declares it and the mapper carries
   * it), and `null` is a meaningful answer rather than an absent one.
   */
  targetColumn: string | null;
  /**
   * `false` = the reference was seen but could not be fully located (an
   * unqualified column in a procedure body, or a cross-database reference the
   * row cannot carry). Real information, but NOT a confirmed location.
   */
  resolved: boolean;
  status: SqlObjectStatus;
  /** A bare exception/subsystem class, never a message. `null` where none was named. */
  errorClass: string | null;
  parserVersion: string | null;
  dialect: string | null;
  parsedAt: string;
}

/**
 * One reason some objects in scope were not examined, and how many.
 *
 * The read path for migration 0149. A reason is only ever named for an object
 * whose column list is untrustworthy, and such an object's only stored row
 * carries `column_name = ''` - which the read route's `column = :value`
 * predicate can never match, because the query parameter is `min(1)`. So the
 * reason cannot travel on a row, and it travels here instead.
 */
export interface SqlObjectUnexaminedReason {
  /**
   * `stored` - the object has rows and its status says the parser could not read
   * it. `refused_at_ingest` - the controller's wire gate refused the object, so
   * there are no rows about it at all.
   */
  origin: 'stored' | 'refused_at_ingest';
  /** One of the five stored statuses for `stored`; `null` for a refusal. */
  status: SqlObjectStatus | null;
  /** A bare class (`CmdExec`, `RecursionError`), or `null` where none was named. */
  errorClass: string | null;
  /** The wire field paths the gate refused. Paths, never values. `[]` for `stored`. */
  fields: string[];
  /** Distinct objects this reason accounts for. */
  objects: number;
}

export interface SqlObjectReferencesResponse {
  /** The PAGE of references - compare against `total` to detect truncation. */
  references: SqlObjectReference[];
  /** Genuine `count(*)` behind the page, not `references.length`. */
  total: number;
  /**
   * References in scope that are DERIVATIONS, and references whose enclosing
   * table could not be resolved.
   *
   * SCOPE-WIDE, over the same predicates as `total`. The page used to derive
   * both by filtering its own page of rows and render them in the same tile row
   * as `total`: 3,000 references to `aadhaar_number` with 412 derivations
   * displayed "References found 3,000" beside "Data actually flowed 4". Render
   * these as whole-answer numbers, never as page numbers.
   */
  derivedReferences: number;
  unresolvedReferences: number;
  /**
   * Objects in this tenant whose body could NOT be parsed, so their column
   * list is unknown.
   *
   * This is the number that makes `total: 0` with an unreadable procedure
   * distinguishable from a genuinely clean estate - the API answers both
   * byte-identically otherwise, because a parse_failed object's only row
   * carries `column_name = ''` and no column filter can ever match it.
   * Deliberately NOT narrowed by column: an object whose body did not parse
   * has an unknown column list, so no column predicate can decide whether it
   * is relevant. Never drop it from a rendered answer.
   */
  unparsedObjects: number;
  /**
   * Objects the INGEST GATE refused, which therefore have NO row anywhere in the
   * reference table - so `unparsedObjects`, which counts rows, can never see
   * them. One refused `procedure dbo.usp_settle_pan` used to leave this answer
   * at `{total: 0, unparsedObjects: 0}`, byte-identical to a clean estate.
   */
  refusedObjects: number;
  /**
   * `unparsedObjects + refusedObjects` - every object in scope that was NOT
   * examined, by either route.
   *
   * THIS IS THE NUMBER A COMPLETENESS STATEMENT MUST BE KEYED ON. It is summed
   * by the API rather than by the page on purpose: a sum computed in the UI is a
   * sum a later edit forgets a term of, and this one decides whether the answer
   * a person reads is labelled complete.
   */
  unexaminedObjects: number;
  /** WHY each of them was not examined. Empty when nothing was unexamined. */
  unexaminedByReason: SqlObjectUnexaminedReason[];
  /**
   * Locations (a Java application, for now) whose last run said its own
   * coverage was PARTIAL: it may have read some JPQL as SQL, so a class of it
   * shown as fully read may not be. Not narrowed by column. Optional so an
   * older controller's answer still renders (as no notice).
   */
  partialCoverageSources?: SqlObjectPartialCoverageSource[];
  /** Every such location; the list holds at most 100. */
  partialCoverageTotal?: number;
  /**
   * The session sees only some business units, so only references whose table
   * is tied to one of them are on this answer.
   */
  businessUnitScoped: boolean;
  /**
   * For a unit-limited session: references to the column, in databases where
   * the session can see a table, that are not shown because their table could
   * not be tied to one of its units. A count, never names; 0 otherwise.
   */
  hiddenReferences: number;
}

/**
 * GET /v1/discovery/sql-objects/references - the SQL-object usage index.
 *
 * Parameter names are the controller's `SqlObjectReferenceQuerySchema`:
 * `column`, `table`, `schema`, `limit`, `offset`. `table`/`schema` narrow;
 * `column` is required and `min(1)`.
 */
export async function getSqlObjectReferences(params: {
  column: string;
  table?: string;
  schema?: string;
  limit?: number;
  offset?: number;
}): Promise<SqlObjectReferencesResponse> {
  const q = new URLSearchParams({ column: params.column });
  if (params.table) q.set('table', params.table);
  if (params.schema) q.set('schema', params.schema);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  if (params.offset !== undefined) q.set('offset', String(params.offset));
  return apiFetch(`/v1/discovery/sql-objects/references?${q.toString()}`);
}

/** One step of database-object lineage (controller `SqlObjectLineageEdge`). */
export interface SqlObjectLineageEdge {
  /** 1 = reads or writes the table itself; 2 and 3 = through a view found at the hop before. */
  hop: number;
  objectKind: SqlObjectKind;
  objectSchema: string;
  objectName: string;
  /** What the object reads or writes: the table at hop 1, a view after. */
  relationSchema: string;
  relationName: string;
  columnName: string;
  /** Hop 1: is the column a personal-data column. Later hops: `null` (a view's column). */
  personalData: boolean | null;
  direction: 'read' | 'write';
  relationship: 'referenced' | 'derived';
  targetColumn: string | null;
  resolved: boolean;
  status: SqlObjectStatus;
  errorClass: string | null;
}

export interface SqlObjectLineageTable {
  assetId: string;
  displayName: string;
  engine: string | null;
  host: string | null;
  port: number | null;
  database: string | null;
  /** `<schema>.<table>`. */
  relation: string | null;
  /** `false`: the asset key is not a SQL table key, so nothing was looked up. */
  lineageAvailable: boolean;
  personalDataColumns: Array<{ columnName: string; labels: string[] }>;
  personalDataColumnsTruncated: boolean;
  edges: SqlObjectLineageEdge[];
  /** Distinct objects among `edges`. */
  objects: number;
  depthReached: number;
  truncated: { edges: boolean; relations: boolean };
  /** Objects naming the table with no schema: counted, not listed. */
  unplacedObjects: number;
  unparsedObjects: number;
  refusedObjects: number;
  /** In this table's database: `unparsedObjects + refusedObjects`. */
  unexaminedObjects: number;
  unexaminedByReason: SqlObjectUnexaminedReason[];
}

export interface SqlObjectLineageResponse {
  depth: number;
  limit: number;
  offset: number;
  /** Tables matching the request that this session can see. */
  tablesTotal: number;
  /** The session sees some business units only. */
  businessUnitScoped: boolean;
  tables: SqlObjectLineageTable[];
}

/**
 * GET /v1/discovery/sql-objects/lineage - which database objects read or write
 * a scanned table, directly or through views, and which of its columns hold
 * personal data. Exactly one of `assetId` or `table` (`schema` only with
 * `table`); `depth` 1 to 3; `limit` 1 to 5.
 */
export async function getSqlObjectLineage(params: {
  assetId?: string;
  table?: string;
  schema?: string;
  depth?: number;
  limit?: number;
  offset?: number;
}): Promise<SqlObjectLineageResponse> {
  const q = new URLSearchParams();
  if (params.assetId) q.set('assetId', params.assetId);
  if (params.table) q.set('table', params.table);
  if (params.schema) q.set('schema', params.schema);
  if (params.depth !== undefined) q.set('depth', String(params.depth));
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  if (params.offset !== undefined) q.set('offset', String(params.offset));
  return apiFetch(`/v1/discovery/sql-objects/lineage?${q.toString()}`);
}

/**
 * Egress lineage: which jurisdictions sensitive data actually left towards.
 *
 * Separate from the access graph on purpose. The access graph answers "who can
 * REACH this data"; this answers "where did it GO". Neither substitutes for the
 * other, and merging them into one endpoint would make an empty result
 * ambiguous between the two questions.
 */
export interface EgressLineageNode {
  id: string;
  label: string;
  cat: 'app' | 'pipeline' | 'storage' | 'analytics' | 'cross-border' | 'export' | 'db';
  kind: 'asset' | 'destination';
  sev?: 'critical' | 'high' | 'medium' | 'low';
  pii?: string[];
  sourceType?: string;
  countryCode?: string | null;
  decisions?: number;
}
export interface EgressLineageEdge {
  source: string;
  target: string;
  decisions: number;
  verdicts: string[];
}
export interface EgressLineage {
  nodes: EgressLineageNode[];
  edges: EgressLineageEdge[];
  /** Enforced decisions whose source country could not be resolved. */
  unresolvedDecisions: number;
  /** Enforced decisions with a country but no finding to draw from. */
  unattributedDecisions: number;
  homeCountry: string;
  windowDays: number;
  coverage: {
    decisionsConsidered: number;
    decisionsPlaced: number;
    /** Always true: only non-allow decisions are persisted. */
    enforcedOnly: boolean;
  };
}

export async function getEgressLineage(
  params: { days?: number; homeCountry?: string } = {},
): Promise<EgressLineage> {
  const q = new URLSearchParams();
  if (params.days) q.set('days', String(params.days));
  if (params.homeCountry) q.set('homeCountry', params.homeCountry);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/discovery/egress-lineage${suffix}`);
}

export interface BootstrapLevel {
  level: number;
  title: string;
  complete: boolean;
  signal: string;
}
export interface BootstrapProgress {
  levels: BootstrapLevel[];
  summary: {
    completeCount: number;
    totalLevels: number;
    completionPct: number;
    assets: number;
    classifiedAssets: number;
    findings: number;
    criticalFindings: number;
    activeCanaries: number;
  };
}

export async function getBootstrapProgress(): Promise<BootstrapProgress> {
  return apiFetch('/v1/discovery/bootstrap-progress');
}

/**
 * Re-run a single bootstrap level. Wire shape matches
 * `apps/api-controller/src/modules/discovery/part2-handlers.ts:runBootstrapLevel`.
 * Levels 1 and 7 are actionable today (cloud-API discovery + scan);
 * levels 2-6 + 8 return `pending_connector` until the corresponding
 * connector is configured.
 */
export interface BootstrapRunResponse {
  level: number;
  accepted: boolean;
  status: 'queued' | 'use_connector_endpoint' | 'pending_connector';
  message: string;
  queuedAt: string;
}

export async function runBootstrapLevel(
  level: number,
  idempotencyKey?: string,
): Promise<BootstrapRunResponse> {
  return apiFetch(`/v1/discovery/bootstrap/run/${level}`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Saved bootstrap connectors (Phase 6 UI) ──────────────────────────
// Persist a connector config once (secret encrypted server-side), then
// list / delete / run it. Backend: modules/discovery/bootstrap/connector-store.ts

export interface StoredBootstrapConnector {
  id: string;
  level: number;
  kind: 'servicenow_cmdb' | 'scim' | 'generic_rest';
  displayName: string;
  config: Record<string, unknown>;
  hasSecret: boolean;
  status: string;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunSummary: Record<string, unknown> | null;
  createdAt: string;
}

export async function listBootstrapConnectors(): Promise<{ connectors: StoredBootstrapConnector[] }> {
  return apiFetch('/v1/discovery/bootstrap/connectors');
}

export interface CreateBootstrapConnectorInput {
  level: number;
  kind: 'servicenow_cmdb' | 'scim' | 'generic_rest';
  displayName: string;
  config: Record<string, unknown>;
  secret?: Record<string, unknown>;
}

export async function createBootstrapConnector(
  input: CreateBootstrapConnectorInput,
): Promise<{ connector: StoredBootstrapConnector }> {
  return apiFetch('/v1/discovery/bootstrap/connectors', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function deleteBootstrapConnector(id: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/v1/discovery/bootstrap/connectors/${id}`, { method: 'DELETE' });
}

export interface RunBootstrapConnectorResponse {
  status: 'ok' | 'error';
  result: { level: number; levelTag: string; received: number; created: number; updated: number; skipped: number } | null;
  errors: string[];
}

export async function runBootstrapConnector(id: string): Promise<RunBootstrapConnectorResponse> {
  return apiFetch(`/v1/discovery/bootstrap/connectors/${id}/run`, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface HoneyCanary {
  id: string;
  canaryType: 'aadhaar' | 'pan' | 'email' | 'phone' | 'identity_bundle';
  plantedInAssetId: string;
  plantedInColumnId: string | null;
  plantedValueHash: string;
  plantedAt: string;
  triggeredAt: string | null;
  triggeredByActor: string | null;
  triggeredViaQueryHash: string | null;
  status: 'active' | 'retired' | 'triggered';
}

export async function listHoneyCanaries(): Promise<{
  canaries: HoneyCanary[];
  /** Over every canary (server); `canaries` is the 200 newest. */
  counts?: { total: number; active: number; triggered: number; retired: number };
}> {
  return apiFetch('/v1/discovery/honey-pii/canaries');
}

export async function plantHoneyCanary(
  input: {
    canaryType: HoneyCanary['canaryType'];
    plantedInAssetId: string;
    plantedInColumnId?: string;
    plantedValueHash: string;
  },
  idempotencyKey?: string,
): Promise<{ canary: HoneyCanary }> {
  return apiFetch('/v1/discovery/honey-pii/canaries', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function retireHoneyCanary(id: string): Promise<{ canary: HoneyCanary }> {
  return apiFetch(`/v1/discovery/honey-pii/canaries/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Trigger a canary - simulates an unauthorised access to the planted
 * row. Emits a `honey_pii_read` finding (severity = critical) and
 * marks the canary as `triggered`. Wire shape matches
 * `apps/api-controller/src/modules/discovery/part2-handlers.ts:triggerHoneyCanary`.
 */
export interface CanaryTriggerResponse {
  canary: HoneyCanary;
  finding: {
    id: string;
    findingType: 'honey_pii_read';
    severity: 'critical';
    status: string;
    title: string;
    createdAt: string;
  };
}

export async function triggerHoneyCanary(
  id: string,
  idempotencyKey?: string,
): Promise<CanaryTriggerResponse> {
  return apiFetch(`/v1/discovery/honey-pii/canaries/${encodeURIComponent(id)}/trigger`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface EvidenceSourceRow {
  sourceType: string;
  displayName: string;
  category: string;
  assetCount: number;
  findingCount: number;
  evidenceMode: 'direct_query' | 'graph_api';
}

export async function listEvidenceSources(): Promise<{ sources: EvidenceSourceRow[] }> {
  return apiFetch('/v1/discovery/evidence-sources');
}

/**
 * No-Direct-DB-Access - schema-export ingestion (Day 3, 2026-05-22).
 *
 * Customer DBA pastes/uploads schema-only DDL; backend parses CREATE
 * TABLE statements, classifies column NAMES against the Indian-context
 * PII vocabulary, and persists findings - never touches the live DB.
 * Unblocks BFSI deals where DBAs share schemas but never credentials.
 *
 * POST /v1/discovery/evidence/schema-export
 */
export interface SchemaExportRequest {
  assetExternalRef: string;
  ddl: string;
  uploadLabel?: string;
  /** The file the DDL was loaded from, for the audit record. */
  fileName?: string;
  /**
   * The business unit the schema belongs to. Omitted: the uploader's own unit
   * when they hold exactly one; tenant-wide when they can see every unit. A
   * user scoped to several units must choose (the server answers 400).
   */
  businessUnitId?: string;
}

export interface SchemaExportResponse {
  assetExternalRef: string;
  tablesParsed: number;
  columnsParsed: number;
  findings: number;
  accepted: number;
  rejected?: number;
  rejections?: ReadonlyArray<{ findingType?: string; reason: string }>;
  message?: string;
  /** Always 'operator_upload': a person uploaded this, no scanner attested it. */
  trustLevel?: 'operator_upload';
  /** The signed-in user the findings are recorded against. */
  uploadedBy?: string;
  /** SHA-256 of the uploaded DDL, as recorded in the audit event. */
  uploadSha256?: string;
  /**
   * The unit the upload's findings are in; null = tenant-wide. An upload only
   * ever writes its own upload asset for this unit, never a scanned asset.
   */
  businessUnitId?: string | null;
  businessUnitBasis?: 'chosen' | 'own_unit' | 'tenant_wide';
  assetsCreated?: number;
  /** An earlier upload of the same reference into the same unit, updated in place. */
  assetsUpdated?: number;
}

export async function ingestSchemaExport(body: SchemaExportRequest): Promise<SchemaExportResponse> {
  return apiFetch('/v1/discovery/evidence/schema-export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** The controller's body limit for POST /v1/discovery/evidence/mobile-app (upload-guard.ts). */
export const MOBILE_APP_MAX_BYTES = 96 * 1024 * 1024;

export interface MobileAppTracker {
  id: string;
  name: string;
  vendor: string;
  category: string;
  personalData: string[];
  evidence: string[];
}

/**
 * How much of the app the scan read (mobile-trackers/scan.ts). `complete` is
 * false for a packed or shielded build, a split, unread or obfuscated code,
 * and every iOS app; such a scan lists what it found and closes nothing.
 */
export interface MobileAppCoverage {
  complete: boolean;
  gaps: string[];
  dexFilesRead: number;
  dexFilesNotRead: number;
  classesRead: number;
  classesShortNamed: number;
  componentClassesChecked: number;
  componentClassesMissing: number;
  /** #315: the entries that raised runtime_code_not_read and embedded_code (at most 20 each). Absent from an older controller. */
  runtimeEntries?: string[];
  embeddedEntries?: string[];
  /** #315, iOS: Mach-O binaries read in full, and those that could not be. */
  binariesRead?: number;
  binariesNotRead?: number;
}

export interface MobileAppScanResponse {
  assetExternalRef: string;
  platform: 'android' | 'ios';
  signatureVersion: string;
  trackers: MobileAppTracker[];
  sensitivePermissions: string[];
  declaredPermissions: string[];
  coverage: MobileAppCoverage;
  app: { packageName: string; versionName: string | null; versionCode: number | null } | null;
  /** The APK v2/v3 signer: certificate digest and whether the signature verifies (#315). */
  signer?: { scheme: 'v2' | 'v3'; certSha256: string; verified?: boolean; verifyFailure?: string | null } | null;
  uploadSha256: string;
  trustLevel: 'operator_upload';
  businessUnitId: string | null;
  findings: number;
  accepted: number;
  rejected: number;
  /** Earlier findings of this app ref and unit closed as no longer detected. */
  closed: number;
  /** Why the close did not run: 'partial', 'version_unknown', 'signer_unknown' or 'signer_unverified'; null when it ran. */
  closeSkipped: string | null;
  /** Open findings this upload did not produce and kept open (recorded from another app, signer or a newer build). */
  keptOpen: number;
  /** Kept findings by reason (mobile-trackers/close.ts KEEP_OPEN_REASONS). */
  keptOpenReasons?: Record<string, number>;
  keptOpenFindings?: Array<{ id: string; findingType: string; reason: string }>;
  limits: string;
}

export interface MobileAppUploadParams {
  assetExternalRef: string;
  fileName?: string;
  uploadLabel?: string;
  businessUnitId?: string;
}

/**
 * Thrown for a refused app upload. `retryAfterSeconds` carries the
 * Retry-After header of a 429 or 503 so the card can say when to try again.
 */
export class MobileAppUploadError extends ApiError {
  retryAfterSeconds: number | null;
  constructor(status: number, message: string, body: unknown, retryAfterSeconds: number | null) {
    super(status, message, body);
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Sends the APK or IPA as the raw body. XMLHttpRequest rather than fetch,
 * because fetch reports no upload progress and a 96 MB app on a slow link
 * takes minutes. Status 0 (connection dropped) is reported as 0: the
 * controller closes the connection when it cuts off a slow upload, and the
 * browser can see that before the 408 arrives.
 */
/** #315 item 5: one app analysed by upload, as GET /v1/discovery/mobile-apps lists it. */
export interface AnalysedMobileApp {
  assetId: string;
  assetExternalRef: string;
  businessUnitId: string | null;
  platform: string | null;
  packageName: string | null;
  versionCode: number | null;
  signerVerified: boolean | null;
  coverageComplete: boolean | null;
  signatureVersion: string | null;
  analysedAt: string;
  trackersInLastScan: number;
  openTrackers: number;
  openPermissions: number;
  openWithoutPurpose: number;
}

/** #315 item 5: the apps analysed by upload that the caller can see (unit-scoped by the controller). */
export async function listMobileApps(limit = 100): Promise<{ apps: AnalysedMobileApp[]; limit: number }> {
  return apiFetch(`/v1/discovery/mobile-apps?limit=${limit}`);
}

export function uploadMobileApp(
  file: Blob,
  params: MobileAppUploadParams,
  onProgress?: (sent: number, total: number) => void,
): Promise<MobileAppScanResponse> {
  const q = new URLSearchParams({ assetExternalRef: params.assetExternalRef });
  if (params.fileName) q.set('fileName', params.fileName);
  if (params.uploadLabel) q.set('uploadLabel', params.uploadLabel);
  if (params.businessUnitId) q.set('businessUnitId', params.businessUnitId);
  const url = `${API_BASE}/v1/discovery/evidence/mobile-app?${q.toString()}`;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.setRequestHeader('accept', 'application/json');
    const token = getAuthToken();
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    if (onProgress) xhr.upload.onprogress = (e) => onProgress(e.loaded, e.lengthComputable ? e.total : file.size);
    xhr.onerror = () => reject(new MobileAppUploadError(0, 'The connection closed before the upload finished.', null, null));
    xhr.onload = () => {
      let body: unknown = null;
      if (isJsonMediaType(xhr.getResponseHeader('content-type') ?? '')) {
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          body = null;
        }
      } else {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as MobileAppScanResponse);
        return;
      }
      if (xhr.status === 401) {
        clearAuth();
        if (!window.location.pathname.startsWith('/login')) window.location.assign('/login');
      }
      const b = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
      const detail =
        (typeof b?.detail === 'string' && b.detail) ||
        (typeof b?.message === 'string' && b.message) ||
        (typeof b?.title === 'string' && b.title) ||
        `${xhr.status} ${xhr.statusText}`;
      const message = xhr.status === 403 ? forbiddenMessage(detail) : detail;
      const retry = Number(xhr.getResponseHeader('retry-after'));
      reject(new MobileAppUploadError(xhr.status, message, body, Number.isFinite(retry) && retry > 0 ? retry : null));
    };
    xhr.send(file);
  });
}

export interface DsarSignature {
  algorithm: 'HMAC-SHA256';
  keyId: string;
  signedAt: string;
  payloadSha256: string;
  signature: string;
}

export interface ReverseDsarExport {
  exportedAt: string;
  tenantId: string;
  principalIdentifier: string;
  format: 'json' | 'pdf';
  body: ReverseDsarResponse;
  integrityNote: string;
  /** Detached HMAC-SHA256 signature over the canonical JSON of `body`. */
  signature?: DsarSignature;
  deferred?: string;
}

type IdentifierKind = 'email' | 'phone' | 'aadhaar' | 'pan' | 'customer_number' | 'custom';

// 'json' (default) -> signed JSON envelope; 'pdf' -> raw application/pdf bytes.
//
// Release 3.1 persona D1: the identifier travels in the BODY
// (POST /v1/discovery/subject-data-discovery/export), never in the URL, where
// access logs and traces keep it; and the export is computed for the same
// identifier KIND as the search (it used to default to 'email', so a PAN or
// phone search exported an email-kind answer).
export async function exportReverseDsar(
  principalIdentifier: string,
  format: 'json',
  identifierKind: IdentifierKind,
): Promise<ReverseDsarExport>;
export async function exportReverseDsar(
  principalIdentifier: string,
  format: 'pdf',
  identifierKind: IdentifierKind,
): Promise<Blob>;
export async function exportReverseDsar(
  principalIdentifier: string,
  format: 'json' | 'pdf',
  identifierKind: IdentifierKind,
): Promise<ReverseDsarExport | Blob> {
  const path = '/v1/discovery/subject-data-discovery/export';
  const body = JSON.stringify({ principalIdentifier, identifierKind, format });
  if (format === 'pdf') {
    const token = getAuthToken();
    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/pdf',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new ApiError(res.status, txt || `${res.status} ${res.statusText}`, txt);
    }
    return res.blob();
  }
  return apiFetch<ReverseDsarExport>(path, { method: 'POST', body });
}

// ── Activity (additional routes) ───────────────────────────────────────

export interface ActivityEvent {
  id: string;
  occurredAt: string;
  actorUserId: string;
  actorRole: string | null;
  application: string | null;
  source: string;
  statementHash: string;
  tablesTouched: string[];
  columnsReturned: string[];
  rowCount: number | null;
  bytesReturned: number | null;
  decision: 'allow' | 'mask' | 'step_up' | 'block' | null;
  requestId: string | null;
}

/**
 * Server returns ClickHouse rows in snake_case (the canonical
 * activity_events column names). Wrapper transforms to the camelCase
 * shape this codebase uses everywhere. Same pattern as the
 * `getDiscoveryAsset` envelope unwrap.
 */
interface ActivityEventRowSnake {
  tenant_id: string;
  asset_id: string;
  occurred_at: string;
  actor_user_id: string;
  actor_role: string;
  application: string;
  statement_hash: string;
  statement_type: string;
  tables_touched: string[];
  columns_returned: string[];
  rows_returned: number;
  duration_ms: number;
  pii_categories_touched: string[];
  decision: string;
  decision_explanation: string;
  severity: string;
}

function mapActivityEvent(row: ActivityEventRowSnake, idx: number): ActivityEvent {
  // The ClickHouse row has no `id` column - use statement_hash + index
  // as a stable React-key surrogate. Decision is mapped to the client's
  // discriminated union; unknown values pass through to `null`.
  const decision = (['allow', 'mask', 'step_up', 'block'] as const).includes(row.decision as never)
    ? (row.decision as ActivityEvent['decision'])
    : null;
  return {
    id: `${row.statement_hash}-${idx}`,
    occurredAt: row.occurred_at,
    actorUserId: row.actor_user_id,
    actorRole: row.actor_role ?? null,
    application: row.application ?? null,
    source: 'clickhouse',
    statementHash: row.statement_hash,
    tablesTouched: row.tables_touched ?? [],
    columnsReturned: row.columns_returned ?? [],
    rowCount: row.rows_returned ?? null,
    bytesReturned: null,
    decision,
    requestId: null,
  };
}

export async function listActivityEvents(params: {
  since?: string;
  actorUserId?: string;
  limit?: number;
} = {}): Promise<{ events: ActivityEvent[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (params.since) q.set('since', params.since);
  if (params.actorUserId) q.set('actorUserId', params.actorUserId);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  const raw = await apiFetch<{ events: ActivityEventRowSnake[]; nextCursor?: string | null }>(
    `/v1/activity/events${suffix}`,
  );
  return {
    events: (raw.events ?? []).map(mapActivityEvent),
    nextCursor: raw.nextCursor ?? null,
  };
}

/**
 * Activity baseline row. Wire shape matches the server's
 * `actor_baselines` table at
 * apps/api-controller/src/shared/database/schema/activity.ts:60 - NOT
 * the earlier guessed shape. Same drift class as Section9 + DiscoveryAsset
 * + scanMode bugs caught in Phase-2 hardening.
 */
export interface ActivityBaseline {
  id: string;
  tenantId: string;
  actorId: string;
  baselineMetric: string;
  baselineValue: string; // numeric → string over the wire
  baselineStddev: string | null;
  sampleSize: number;
  extra: Record<string, unknown>;
  computedAt: string;
}

export async function listActivityBaselines(params: { actorId?: string } = {}): Promise<{
  baselines: ActivityBaseline[];
}> {
  const q = new URLSearchParams();
  if (params.actorId) q.set('actorId', params.actorId);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/activity/baselines${suffix}`);
}

/**
 * Application sonar signature row. Mirrors
 * `application_sonar_signatures` at schema/activity.ts:89.
 */
export interface SonarSignature {
  id: string;
  tenantId: string;
  applicationName: string;
  signatureDistribution: Record<string, Record<string, number>>;
  sampleSize: number;
  lastKlDivergence: string | null;
  computedAt: string;
}

export async function listSonarSignatures(): Promise<{ signatures: SonarSignature[] }> {
  return apiFetch('/v1/activity/sonar');
}

/**
 * Sonar persisted finding (low-and-slow exfil, etc.). Subset of
 * fields needed by the page; backend may include more.
 */
export interface SonarFinding {
  id: string;
  tenantId: string;
  applicationName: string;
  pattern: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'acknowledged' | 'resolved' | 'false_positive';
  detectedAt: string;
  rationale?: string;
  metadata?: Record<string, unknown>;
}

export async function listSonarFindings(params: { status?: string; limit?: number } = {}): Promise<{
  findings: SonarFinding[];
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/activity/sonar/findings${suffix}`);
}

/**
 * Breach readiness payload. The server returns ClickHouse-derived
 * counters merged with an `rbi6HourClock` block; the response is
 * intentionally permissive because the engine emits richer metadata
 * on real breaches. We surface only the fields the page renders.
 */
export interface BreachReadiness {
  generatedAt: string;
  // ClickHouse-derived fields (engine emits more; client renders these).
  recentBreaches?: number;
  averageDetectionMinutes?: number | null;
  averageContainmentMinutes?: number | null;
  rbi6HourClock: {
    minutesSinceLastDetection: number | null;
    hoursRemaining: number | null;
  };
  // Optional richer fields the engine may populate. The page should
  // render whatever is present without exploding on absence.
  openIncidents?: Array<{
    id: string;
    severity: string;
    detectedAt: string;
    dpdpa72HoursRemaining?: number | null;
    gdpr72HoursRemaining?: number | null;
    rbi6HoursRemaining?: number | null;
  }>;
  recentClosed?: Array<{ id: string; closedAt: string; outcome: string }>;
  /** 24h activity counters; null when the activity store is not available. */
  eventsLast24h?: number | null;
  /** Absent on controllers older than this field. */
  activityStore?: ActivityStoreStatus;
}

export async function getBreachReadiness(): Promise<BreachReadiness> {
  return apiFetch('/v1/activity/breach-readiness');
}

/**
 * Early-warning indicator row. Wire shape matches the server's
 * `early_warning_indicators` table at schema/activity.ts:116. Server
 * wraps the list as `{ indicators: [...] }` (not `{ events: [...] }`).
 */
export interface EarlyWarningIndicator {
  id: string;
  tenantId: string;
  indicatorType:
    | 'reconnaissance_burst'
    | 'off_pattern_query'
    | 'credential_creation'
    | 'audit_log_disable_attempt'
    | 'schema_mapping'
    | 'cold_table_burst';
  actorId: string | null;
  assetId: string | null;
  firedAt: string;
  resolvedAt: string | null;
  severity: 'critical' | 'high' | 'medium' | 'low';
  metadata: Record<string, unknown>;
  relatedEventCount: number;
  createdAt: string;
}

export async function listEarlyWarning(): Promise<{ indicators: EarlyWarningIndicator[] }> {
  return apiFetch('/v1/activity/early-warning');
}

// Deprecated alias for any code still referencing the old name. Remove
// after grep returns zero callers.
export const listEarlyWarnings = listEarlyWarning;

/**
 * Phase 3 Part 2 - unified anomaly stream. Aggregates open UEBA + Sonar
 * findings and live EarlyWarning indicators into one severity-sorted
 * list. Wire shape: `apps/api-controller/src/modules/activity/service.ts:listAnomalies`.
 */
export interface AnomalyRow {
  id: string;
  engine: 'ueba' | 'sonar' | 'early_warning';
  severity: 'critical' | 'high' | 'medium' | 'low';
  actor: string | null;
  detectedAt: string;
  title: string;
  summary: string;
}

export async function listAnomalies(): Promise<{ anomalies: AnomalyRow[] }> {
  return apiFetch('/v1/activity/anomalies');
}

// ── Detection (additional routes - rules CRUD, playbook run, break-glass) ─

/**
 * The rule statuses the server knows (`RuleStatusSchema` in
 * apps/api-controller/src/modules/detection/schemas.ts). Q03: the Rules page
 * used to patch `active` and offer publish on `pending_approval`, neither of
 * which the server has.
 */
export const DETECTION_RULE_STATUSES = ['draft', 'dry_run', 'published', 'paused', 'deprecated'] as const;
export type DetectionRuleStatus = (typeof DETECTION_RULE_STATUSES)[number];

/**
 * DDR rule row. Wire shape matches `$inferSelect` on `ddr_rules`
 * (schema/detection.ts:28). Previous client guess used `category`,
 * `body`, `regoBody`, `source`, `createdBy` - none of which exist
 * on the server table. Aligned for Phase 4 wiring.
 *
 * Derived UI fields:
 *   `category` is recomputed in the page (one-of 'discovery' /
 *   'activity' / 'detection' / 'frontier') from `ruleType` or
 *   industry-pack metadata; the server doesn't carry a literal
 *   category column.
 *   `source` is `built_in === true ? 'built_in' : 'tenant'`.
 */
export interface DetectionRule {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  ruleType: 'discovery_finding' | 'activity_event' | 'combined' | string;
  severity: 'critical' | 'high' | 'medium' | 'low' | string;
  /** The JSON rule body as text (column `rego_source`; the name is historical). */
  regoSource: string;
  /** A server status; `| string` because older rows may hold other text. */
  status: DetectionRuleStatus | string;
  builtIn: boolean;
  industryPackId: string | null;
  createdByUserId: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function listDetectionRules(): Promise<{ rules: DetectionRule[] }> {
  return apiFetch('/v1/detection/rules');
}

/** The single-rule routes answer `{ rule }`; unwrapped here. */
export async function getDetectionRule(id: string): Promise<DetectionRule> {
  const r = await apiFetch<{ rule: DetectionRule }>(`/v1/detection/rules/${encodeURIComponent(id)}`);
  return r.rule;
}

/**
 * New-rule input. Matches `CreateRuleSchema` server-side (strict). The server
 * stamps tenant_id from RLS context and defaults status='draft'.
 * `regoSource` carries a JSON rule body as text (`RuleBodySchema` in
 * detection/rule-engine.ts: `kind`, `name`, `any` and/or `all` predicates,
 * optional `recommendation`). Rego text is refused with a 400.
 */
export interface CreateDetectionRuleInput {
  name: string;
  description?: string;
  ruleType: 'discovery_finding' | 'activity_event' | 'combined';
  severity: 'critical' | 'high' | 'medium' | 'low';
  regoSource: string;
}

export async function createDetectionRule(
  input: CreateDetectionRuleInput,
  idempotencyKey?: string,
): Promise<DetectionRule> {
  const r = await apiFetch<{ rule: DetectionRule }>('/v1/detection/rules', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
  return r.rule;
}

/** The input a rule is tried against: the same three objects /evaluate takes. */
export interface DetectionRuleTestInput {
  finding?: Record<string, unknown>;
  event?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

/**
 * Dry-run request. Matches `DryRunRuleSchema` (strict): a saved rule by
 * `ruleId`, or an unsaved JSON rule body as text in `regoSource`, plus the
 * `input` to try it against. Nothing is written.
 */
export type DryRunDetectionRuleInput =
  | { ruleId: string; regoSource?: never; input: DetectionRuleTestInput }
  | { ruleId?: never; regoSource: string; input: DetectionRuleTestInput };

/** The rule engine's answer (`RuleEngineResult` in detection/rule-engine.ts). */
export interface DryRunDetectionRuleResult {
  finalDecision: 'allow' | 'mask' | 'alert' | 'step_up' | 'break_glass' | 'quarantine' | 'block';
  finalExplanation: string;
  matchedRules: Array<{
    ruleName: string;
    output: {
      matched: boolean;
      decision: string;
      explanation: string;
      recommendation?: string;
      matchedPredicates: Array<{ path: string; op: string; value: unknown }>;
    };
  }>;
  /**
   * Present when a `matches` pattern could not be evaluated within the
   * controller's cost budget (Q25): the rule was counted as a match (fail
   * closed). Shown under the dry-run decision.
   */
  notes?: string[];
}

export async function dryRunDetectionRule(input: DryRunDetectionRuleInput): Promise<DryRunDetectionRuleResult> {
  return apiFetch('/v1/detection/rules/dry-run', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * A rule status change as the server answers it. `decideWarnings` (Q06 round
 * 3) is present when a publish put a rule on the gateway's /decide path that
 * reads a field /decide never carries: one sentence per such predicate. The
 * Rules page shows them; an empty list means nothing to say.
 */
export interface RuleStatusChangeResult {
  rule: DetectionRule;
  decideWarnings: string[];
}

export async function publishDetectionRule(
  id: string,
  approverNote?: string,
  idempotencyKey?: string,
): Promise<RuleStatusChangeResult> {
  const r = await apiFetch<{ rule: DetectionRule; decideWarnings?: string[] }>(
    `/v1/detection/rules/${encodeURIComponent(id)}/publish`,
    {
      method: 'POST',
      body: JSON.stringify({ approverNote }),
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    },
  );
  return { rule: r.rule, decideWarnings: Array.isArray(r.decideWarnings) ? r.decideWarnings : [] };
}

export async function patchDetectionRuleStatus(
  id: string,
  status: DetectionRuleStatus,
): Promise<RuleStatusChangeResult> {
  const r = await apiFetch<{ rule: DetectionRule; decideWarnings?: string[] }>(
    `/v1/detection/rules/${encodeURIComponent(id)}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    },
  );
  return { rule: r.rule, decideWarnings: Array.isArray(r.decideWarnings) ? r.decideWarnings : [] };
}

/**
 * DDR playbook row. Wire shape matches Drizzle `$inferSelect` on
 * `ddr_playbooks` (schema/detection.ts:89). Previous client guess
 * used `twoPartyApproval` / `actions` / `category` - server has
 * `requiresTwoPersonRule` / `steps` / `builtIn`. Realigned for
 * Phase 4 wiring.
 */
export interface DetectionPlaybook {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  steps: Array<{ kind: string; description: string }>;
  requiresTwoPersonRule: boolean;
  builtIn: boolean;
  industryPackId: string | null;
  workflowId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listDetectionPlaybooks(): Promise<{ playbooks: DetectionPlaybook[] }> {
  return apiFetch('/v1/detection/playbooks');
}

export async function runDetectionPlaybook(
  name: string,
  input: { context: Record<string, unknown> },
  idempotencyKey?: string,
): Promise<{
  playbookName: string;
  runId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  actionsExecuted: Array<{ kind: string; outcome: string }>;
  requestId: string;
}> {
  return apiFetch(`/v1/detection/playbooks/${encodeURIComponent(name)}/run`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * DDR break-glass session row. Wire shape matches Drizzle
 * `$inferSelect` on `ddr_break_glass_sessions` (schema/detection.ts:133).
 * The server does NOT carry a literal `status` column - the listing
 * service derives status from (approved_at, expires_at, revoked_at)
 * in the page logic. We keep an optional `status` here so handlers
 * that compute it server-side still typecheck.
 */
export interface BreakGlassSession {
  id: string;
  tenantId: string;
  userId: string;
  justification: string;
  approvedByUserId: string | null;
  approvedAt: string | null;
  expiresAt: string;
  revokedAt: string | null;
  followUpSummary: string | null;
  followUpFiledAt: string | null;
  createdAt: string;
  // Derived in the UI (or, in future, by the listing service): one of
  // 'pending_approval' | 'approved' | 'active' | 'expired' | 'closed'.
  status?: 'pending_approval' | 'approved' | 'active' | 'expired' | 'closed';
}

// -- API abuse from API gateway access logs (bank rows II-12, III-58) -------
// Per-route limits, the sessions with a block recorded, and the allowlist.
// Backend: apps/api-controller/src/modules/detection/api-abuse/.

export type ApiAbuseAction = 'alert' | 'block';
export type ApiAbuseDetector = 'request_volume' | 'bulk_bytes' | 'enumeration';

export interface ApiAbuseRule {
  id: string;
  /** "GET /v1/accounts/{id}", or "*" for the session rule (all routes of a session together). */
  route: string;
  enabled: boolean;
  windowSeconds: number;
  maxRequests: number | null;
  maxResponseBytes: number | null;
  enumerationDistinctIds: number | null;
  action: ApiAbuseAction;
  blockSeconds: number;
  /**
   * Whether a block also blocks a session that is a source IP (it may be many
   * people: a load balancer without real_ip, NAT, CGNAT). Default false: an IP
   * session that passes a limit raises an alert finding instead.
   */
  blockIpSessions: boolean;
  updatedByActorId: string;
  updatedAt: string;
}

export type ApiAbuseRuleInput = Omit<ApiAbuseRule, 'id' | 'updatedByActorId' | 'updatedAt'>;

export interface ApiSessionBlock {
  id: string;
  route: string;
  ruleId: string | null;
  detector: ApiAbuseDetector;
  requestId: string;
  blockedUntil: string;
  createdAt: string;
  releasedAt: string | null;
  releasedByActorId: string | null;
  /** Windows that tripped again while the block was in force. */
  tripsWhileBlocked: number;
  lastTripAt: string | null;
  /** 'ip': the session is a source IP address, which may be many people. Absent from an older controller. */
  sessionKind?: 'principal' | 'ip';
  active: boolean;
}

/** A gateway credential (decision_gateway) and when it last asked the session check. */
export interface ApiSessionCheckCaller {
  credentialId: string | null;
  label: string;
  revoked: boolean;
  /** Null: this credential has never asked the session check. */
  lastCheckAt: string | null;
}

export interface ApiAbuseAllowlistEntry {
  id: string;
  label: string;
  createdByActorId: string;
  createdAt: string;
}

export async function listApiAbuseRules(): Promise<{ rules: ApiAbuseRule[] }> {
  return apiFetch('/v1/detection/api-abuse/rules');
}

export async function createApiAbuseRule(input: ApiAbuseRuleInput, idempotencyKey?: string): Promise<{ rule: ApiAbuseRule }> {
  return apiFetch('/v1/detection/api-abuse/rules', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function updateApiAbuseRule(
  id: string,
  patch: Partial<Omit<ApiAbuseRuleInput, 'route'>>,
): Promise<{ rule: ApiAbuseRule }> {
  return apiFetch(`/v1/detection/api-abuse/rules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteApiAbuseRule(id: string): Promise<{ rule: ApiAbuseRule }> {
  return apiFetch(`/v1/detection/api-abuse/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Shown with the blocks: what is waiting to be delivered, and when a log line was last counted. */
export interface ApiAbuseDeliveryStatus {
  /** Audit events not written yet; the tenant's next ingest writes them. */
  auditPending: number;
  notificationsPending: number;
  oldestPendingAt: string | null;
  /** Null when no line was counted in the last seenLineHours. */
  lastLineCountedAt: string | null;
  /**
   * Lines counted in the last seenLineHours whose path did not match their
   * gateway template (read as concrete paths; at most maxMismatchCounted).
   * Absent from an older controller.
   */
  pathTemplateMismatch?: number;
  lastPathTemplateMismatchAt?: string | null;
  maxMismatchCounted?: number;
  seenLineHours: number;
  maxSeenLines: number;
  /** Absent from an older controller. */
  sessionCheckCallers?: ApiSessionCheckCaller[];
}

export async function listApiSessionBlocks(): Promise<{ blocks: ApiSessionBlock[]; status?: ApiAbuseDeliveryStatus }> {
  return apiFetch('/v1/detection/api-abuse/blocks');
}

export async function releaseApiSessionBlock(id: string, idempotencyKey?: string): Promise<{ block: ApiSessionBlock }> {
  return apiFetch(`/v1/detection/api-abuse/blocks/${encodeURIComponent(id)}/release`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function listApiAbuseAllowlist(): Promise<{ entries: ApiAbuseAllowlistEntry[] }> {
  return apiFetch('/v1/detection/api-abuse/allowlist');
}

/** Allowlist the session of a block on record (the session itself never reaches the browser). */
export async function allowlistApiSessionOfBlock(
  blockId: string,
  label: string,
  idempotencyKey?: string,
): Promise<{ entry: ApiAbuseAllowlistEntry }> {
  return apiFetch('/v1/detection/api-abuse/allowlist', {
    method: 'POST',
    body: JSON.stringify({ blockId, label }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function removeApiAbuseAllowlistEntry(id: string): Promise<{ entry: ApiAbuseAllowlistEntry }> {
  return apiFetch(`/v1/detection/api-abuse/allowlist/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function listBreakGlassSessions(): Promise<{ sessions: BreakGlassSession[] }> {
  return apiFetch('/v1/detection/break-glass');
}

export async function activateBreakGlass(
  input: { justification: string; requestedPermissions: string[]; durationMinutes: number },
  idempotencyKey?: string,
): Promise<BreakGlassSession> {
  return apiFetch('/v1/detection/break-glass/activate', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveBreakGlass(
  id: string,
  input: { approverNote: string },
  idempotencyKey?: string,
): Promise<BreakGlassSession> {
  return apiFetch(`/v1/detection/break-glass/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function fileBreakGlassSummary(
  id: string,
  input: { summary: string; actionsTaken: string[] },
): Promise<BreakGlassSession> {
  return apiFetch(`/v1/detection/break-glass/${encodeURIComponent(id)}/file-summary`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * DDR SIEM adapter row. Wire shape matches `$inferSelect` on
 * `ddr_siem_adapters` (schema/detection.ts:158). Previous client
 * guess used `vendorKey` / `displayName` / `authMethod` /
 * `configKmsRef` - none of which exist on the server table.
 */
export interface SiemAdapter {
  id: string;
  tenantId: string;
  vendor: 'splunk' | 'sentinel' | 'xsoar' | 'servicenow' | 'unieye' | 'qradar' | 'elastic' | 'generic_webhook' | string;
  configuration: Record<string, unknown>;
  status: 'active' | 'paused' | 'failed' | string;
  lastEventAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  healthScore: string | null;
  /** Release 3.6 #11 round 5 (migration 0211): delivery health. */
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  consecutiveFailures?: number;
  /** One of the controller's SIEM_ERROR_CLASSES. */
  lastErrorClass?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One setting of a registry adapter, as the add/edit form renders it (T-11 slice 2). */
export interface SiemAdapterConfigField {
  name: string;
  type: 'string' | 'url' | 'secret_ref' | 'enum';
  required: boolean;
  description: string;
  enum?: string[];
}

export interface SiemAdapterDescriptor {
  id: string;
  displayName: string;
  vendor: string;
  protocol: string;
  authMethod: string;
  maturity: string;
  /** The adapter's settings; secret_ref values are masked on every read once saved. */
  configFields?: SiemAdapterConfigField[];
}

export async function listSiemAdapterRegistry(): Promise<{
  adapters: SiemAdapterDescriptor[];
}> {
  return apiFetch('/v1/detection/siem-adapters/registry');
}

export async function listSiemAdapters(): Promise<{ adapters: SiemAdapter[] }> {
  return apiFetch('/v1/detection/siem-adapters');
}

export async function createSiemAdapter(
  input: { vendor: string; configuration: Record<string, unknown> },
  idempotencyKey?: string,
): Promise<SiemAdapter> {
  // The controller answers 201 { adapter } (handlers.ts configureSiemAdapter).
  const wrapped = await apiFetch<{ adapter: SiemAdapter }>('/v1/detection/siem-adapters', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
  return wrapped.adapter;
}

/**
 * T-11 slice 2: PUT /v1/detection/siem-adapters/:id, the adapter's whole new
 * settings. A secret sent back exactly as it was shown ([set]) keeps the
 * stored value; a key left out is removed. The vendor does not change.
 */
export async function updateSiemAdapter(adapterId: string, configuration: Record<string, unknown>): Promise<SiemAdapter> {
  const wrapped = await apiFetch<{ adapter: SiemAdapter }>(`/v1/detection/siem-adapters/${encodeURIComponent(adapterId)}`, {
    method: 'PUT',
    body: JSON.stringify({ configuration }),
  });
  return wrapped.adapter;
}

export interface DecisionDetail extends Decision {
  rule_trace: Array<{ rule_name: string; matched: boolean; explanation: string }>;
  consent_state?: { granted: boolean; purpose?: string; expiresAt?: string };
  cross_border_state?: { allowed: boolean; reason?: string };
  section9_state?: { applicable: boolean; guardianConsentVerified?: boolean };
  ueba_state?: { zScore: number; baseline: string };
}

export async function getDecisionDetail(reqId: string): Promise<DecisionDetail> {
  const wrapped = await apiFetch<{ decision: DecisionDetail }>(
    `/v1/detection/decisions/${encodeURIComponent(reqId)}`,
  );
  // Server wraps as { decision: ... } per handlers.ts:getDecisionByReqId.
  return wrapped.decision;
}

/**
 * Phase 4 - Why-Blocked composer. Returns the full reason chain that
 * the Decisions detail drawer renders: rule, redundancy votes, plus
 * per-source verdicts (consent, cross-border, section-9, UEBA).
 */
export interface WhyBlockedResponse {
  requestId: string;
  decision: string;
  decidedAt: string;
  ruleName: string | null;
  ruleSeverity: string | null;
  explanation: string;
  redundancy: Array<{ engine: string; vote: string; confidence: number }>;
  reasons: Array<{ source: string; verdict: string; detail: string }>;
}

export async function getWhyBlocked(reqId: string): Promise<WhyBlockedResponse> {
  return apiFetch(`/v1/detection/why-blocked/${encodeURIComponent(reqId)}`);
}

export async function acknowledgeDecision(
  reqId: string,
  idempotencyKey?: string,
): Promise<{ ok: boolean; decisionId: string; acknowledgedBy: string }> {
  return apiFetch(`/v1/detection/decisions/${encodeURIComponent(reqId)}/acknowledge`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function suppressDecision(
  reqId: string,
  input: { reason: string },
  idempotencyKey?: string,
): Promise<{ ok: boolean; decisionId: string; suppressedBy: string }> {
  return apiFetch(`/v1/detection/decisions/${encodeURIComponent(reqId)}/suppress`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function killSessionForDecision(
  reqId: string,
  idempotencyKey?: string,
): Promise<{ ok: boolean; decisionId: string; killedBy: string; note: string; at: string }> {
  return apiFetch(`/v1/detection/decisions/${encodeURIComponent(reqId)}/kill-session`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function testSiemAdapter(
  adapterId: string,
  idempotencyKey?: string,
): Promise<{
  ok: boolean;
  adapterId: string;
  vendor: string;
  sentAt: string;
  /** The transport the controller used (`syslog`, `sentinel_dcr`, ...). */
  transport?: string;
  statusCode?: number;
  /** Why it failed, or what a success means (UDP: no acknowledgement). */
  detail?: string;
  /** Round 5: the failure class, on a failure. */
  errorClass?: string;
  latencyMs?: number;
  payload: Record<string, unknown>;
}> {
  return apiFetch(`/v1/detection/siem-adapters/${encodeURIComponent(adapterId)}/test`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * Release 3.6 #11 round 8: PATCH /v1/detection/siem-adapters/:id. A paused
 * adapter receives no decision; Send test event still works on it.
 */
export async function setSiemAdapterStatus(adapterId: string, status: 'active' | 'paused'): Promise<SiemAdapter> {
  const wrapped = await apiFetch<{ adapter: SiemAdapter }>(`/v1/detection/siem-adapters/${encodeURIComponent(adapterId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  return wrapped.adapter;
}

/** Release 3.6 #11 round 8: DELETE /v1/detection/siem-adapters/:id (204). */
export async function deleteSiemAdapter(adapterId: string): Promise<void> {
  await apiFetch(`/v1/detection/siem-adapters/${encodeURIComponent(adapterId)}`, { method: 'DELETE' });
}

/**
 * P1-6 (2026-05-26) - GET /v1/detection/siem-adapters/:id/dispatches
 * returns in-memory ring buffer of recent test dispatches.
 */
export interface SiemDispatchHistoryEntry {
  ok: boolean;
  adapterId: string;
  tenantId: string;
  vendor: string;
  sentAt: string;
  errorMessage?: string;
  /** Round 5: failure class and attempts made. */
  errorClass?: string;
  attempts?: number;
  /** Round 8: the event's request id (absent on a test event). */
  requestId?: string;
}
export async function getSiemDispatchHistory(
  adapterId: string,
  limit: number = 10,
): Promise<{ entries: SiemDispatchHistoryEntry[]; cap: number }> {
  return apiFetch(
    `/v1/detection/siem-adapters/${encodeURIComponent(adapterId)}/dispatches?limit=${limit}`,
  );
}

// ── DDR enforcement contracts (T1.2, 2026-05-29) ─────────────────
//
// These four clients were drifted from the live Zod schemas in
// apps/api-controller/src/modules/detection/schemas.ts - they sent the
// wrong request fields (strict schemas → 400) and typed the wrong
// response. Re-aligned to the deployed handlers, which are the source
// of truth.

export type DbVendor = 'postgres' | 'mysql' | 'mssql' | 'oracle' | 'mongodb';

// POST /v1/detection/enforcement/synthetic-error
// schema: SyntheticErrorRequestSchema → generateSyntheticError() : SyntheticError
export interface SyntheticErrorInput {
  /** Request ID from a block decision - must match /^BLK-[A-Z0-9]{8,}$/. */
  requestId: string;
  vendor: DbVendor;
  tableName?: string;
  subtype?: 'permission' | 'not_found' | 'auth';
}
export interface SyntheticErrorResult {
  code: string;
  errorNumber: number;
  message: string;
  severity: string;
}
export async function postSyntheticError(
  input: SyntheticErrorInput,
): Promise<SyntheticErrorResult> {
  return apiFetch<SyntheticErrorResult>('/v1/detection/enforcement/synthetic-error', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// POST /v1/detection/enforcement/breach-pre-fill
// schema: BreachPreFillSchema → buildBreachPreFill() : BreachPreFillResult
// NOTE: server-side this builder is also auto-invoked by the
// `breach_console_pre_fill` auto-fire playbook on every block/alert
// decision (see detection/service.ts). This client is the manual
// equivalent - the full breach-console surface is a Tier-3 item.
export interface BreachPreFillInput {
  decisionId: string;
  requestId: string;
  breachWindowStart: string;
  breachWindowEnd: string;
  affectedRecordCount: number;
  dataCategories: string[];
  affectedPrincipalsCount: number;
  affectedPrincipalIds?: string[];
  timelineEvents: Array<{
    occurredAt: string;
    eventType: string;
    actorUserId: string;
    statementHash: string;
  }>;
  tenantName: string;
  tenantContactDpoEmail: string;
  recommendedActions: string[];
  industryAnchor: string;
  recommendedSeverity: 'critical' | 'high' | 'medium' | 'low';
}
export interface BreachPreFillResult {
  breachIncident: {
    tenant_id: string;
    detected_at: string;
    detection_source: string;
    affected_record_count: number;
    data_categories: ReadonlyArray<string>;
    affected_principals_count: number;
    affected_principal_ids: ReadonlyArray<string>;
    breach_window_start: string;
    breach_window_end: string;
    timeline_events: ReadonlyArray<unknown>;
    recommended_severity: string;
    recommended_actions: ReadonlyArray<string>;
    auto_filled_from_dam_event_id: string;
    request_id: string;
  };
  draftDpbNotification: string;
  draftPrincipalNotification: string;
  clocks: {
    rbi6Hour: { hoursElapsed: number; hoursRemaining: number };
    dpdpa72Hour: { hoursElapsed: number; hoursRemaining: number };
    gdpr72Hour: { hoursElapsed: number; hoursRemaining: number };
    hipaa60Day: { daysElapsed: number; daysRemaining: number };
  };
}
export async function pushBreachPreFill(
  input: BreachPreFillInput,
  idempotencyKey?: string,
): Promise<BreachPreFillResult> {
  return apiFetch<BreachPreFillResult>('/v1/detection/enforcement/breach-pre-fill', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Activity (ingest, for synthetic demo data + Phase 3 seed) ─────────

export async function postActivityEvent(
  input: Omit<ActivityEvent, 'id'>,
  idempotencyKey?: string,
): Promise<{ id: string; received: true }> {
  return apiFetch('/v1/activity/events', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Activity Capture Configs (DAM Sources - Tier A1, 2026-05-26) ─────
//
// Wire shape mirrors apps/api-controller/src/modules/activity/capture-configs.ts:CaptureConfigDto
// and CaptureSourcesSummary. Backend ships 7 routes (list / summary /
// get / upsert / patch / soft-delete / self-register). The /sources
// page builds on this surface.

export type ActivityCaptureMode =
  | 'native_audit'
  | 'cloud_stream'
  | 'proxy'
  | 'ebpf'
  | 'network'
  | 'external_feed';

export type ActivityCaptureStatus = 'active' | 'paused' | 'deleted' | 'error';

export interface ActivityCaptureConfig {
  id: string;
  tenantId: string;
  dataSourceConnectorInstanceId: string;
  captureMode: ActivityCaptureMode;
  configuration: Record<string, unknown>;
  status: ActivityCaptureStatus | string;
  healthScore: number | null;
  lastEventAt: string | null;
  eventsPerMinuteRecent: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityCaptureSourcesSummary {
  totalActive: number;
  totalPaused: number;
  totalError: number;
  byCaptureMode: Array<{ captureMode: string; count: number }>;
  avgHealthScore: number | null;
}

export async function listActivityCaptureConfigs(
  params: { status?: ActivityCaptureStatus | 'any' } = {},
): Promise<{ configs: ActivityCaptureConfig[]; count: number }> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/activity/capture-configs${suffix}`);
}

export async function getActivityCaptureSourcesSummary(): Promise<{
  summary: ActivityCaptureSourcesSummary;
}> {
  return apiFetch('/v1/activity/capture-configs/summary');
}

export async function getActivityCaptureConfig(
  id: string,
): Promise<{ config: ActivityCaptureConfig }> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}`);
}

export interface UpsertActivityCaptureConfigInput {
  dataSourceConnectorInstanceId: string;
  captureMode: ActivityCaptureMode;
  configuration?: Record<string, unknown>;
  status?: ActivityCaptureStatus;
  healthScore?: number;
}

export async function upsertActivityCaptureConfig(
  input: UpsertActivityCaptureConfigInput,
  idempotencyKey?: string,
): Promise<{ config: ActivityCaptureConfig }> {
  return apiFetch('/v1/activity/capture-configs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface PatchActivityCaptureConfigInput {
  status?: ActivityCaptureStatus;
  healthScore?: number;
  configuration?: Record<string, unknown>;
  eventsPerMinuteRecent?: number;
  lastEventAt?: string;
}

export async function patchActivityCaptureConfig(
  id: string,
  patch: PatchActivityCaptureConfigInput,
): Promise<{ config: ActivityCaptureConfig }> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteActivityCaptureConfig(id: string): Promise<void> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * P1-4 (2026-05-26) - run the 3-check verdict for a capture config.
 * Backend at apps/api-controller/src/modules/activity/capture-configs.ts:
 * testCaptureConfig. Returns ok + per-check status + summary line.
 */
export interface ActivityCaptureConfigTestCheck {
  id: 'status' | 'health' | 'freshness';
  ok: boolean;
  detail: string;
}
export interface ActivityCaptureConfigTestResult {
  ok: boolean;
  checks: ActivityCaptureConfigTestCheck[];
  summary: string;
  testedAt: string;
}
export async function testActivityCaptureConfig(
  id: string,
): Promise<ActivityCaptureConfigTestResult> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}/test`, {
    method: 'POST',
  });
}

// ── Third-party feed coverage (release 3.6 item 21) ───────────────────
// Mirrors CoverageReport in apps/api-controller/src/modules/activity/feed-ingest.ts.
export interface ActivityFeedCoverage {
  generatedAt: string;
  withinHours: number;
  /** False for a business-unit-limited viewer: only their stores are in the report. */
  allUnits: boolean;
  totals: { assets: number; covered: number; gaps: number };
  feeds: Array<{
    id: string; sourceId: string; vendor: string; status: string; expectedIntervalMinutes: number;
    storesCovered: number; lastEventAt: string | null; stale: boolean;
    /**
     * Events delivered but neither ingested nor rejected, and not being worked
     * on (released and not resent within the lease, or a dead delivery's).
     * null for a business-unit-limited viewer.
     */
    heldBack?: { count: number; atLeast: boolean; oldestAt: string | null } | null;
    /** T-21: PII elements the feed's ingested events carried, on the taxonomy (absent on an older controller). */
    piiElements?: ActivityFeedPiiElements;
  }>;
  stores: Array<{
    assetId: string;
    displayName: string;
    sourceType: string;
    covered: boolean;
    feeds: Array<{ feedId: string; sourceId: string; vendor: string; eventsTotal: number; lastEventAt: string }>;
    piiElements?: ActivityFeedPiiElements;
  }>;
  gaps: { items: Array<{ assetId: string; displayName: string; sourceType: string }>; truncated: boolean };
  unmatched: {
    hiddenForScope: boolean;
    total: number;
    truncated: boolean;
    items: Array<{
      storeRef: string; feedId: string; sourceId: string; eventsTotal: number; lastEventAt: string;
      reason:
        | 'not_in_catalogue'
        | 'ambiguous'
        | 'matched_after_delivery'
        | 'matched_outside_sender_units'
        | 'matched_sender_scope_unknown'
        | 'outside_sender_units';
      /** Of eventsTotal, the events this reason covers (delivered, not in the activity pipeline). */
      eventsNotIngested?: number;
    }>;
  };
  linksTruncated: boolean;
  /** T-21: stores FOCTTA's own sensors cover (absent on an older controller). */
  ownSensors?: ActivityOwnSensorCoverage;
}

/**
 * T-21: taxonomy labels with counts; `unmapped` counts names that mapped to
 * nothing (never their text). `names` is name occurrences (each event's
 * distinct names, summed over events), not distinct names across events.
 */
export interface ActivityFeedPiiElements {
  elements: Array<{ element: string; events: number; names: number; lastSeenAt: string }>;
  unmapped: { events: number; names: number };
}

export type ActivityOwnSensor = 'dam_collector' | 'ddr_gateway' | 'discovery_scan';

export interface ActivityOwnSensorCoverage {
  sensors: Array<{
    sensor: ActivityOwnSensor;
    kind: 'activity' | 'scan';
    available: boolean;
    unavailableReason: string | null;
    windowHours: number;
    storesCovered: number;
    truncated: boolean;
    /** False: this sensor's reports cannot be tied to one store, so it credits none (the gateway, round 2). */
    creditsStores?: boolean;
  }>;
  totals: {
    assets: number;
    ownActivityCovered: number;
    activityCoveredAny: number;
    activityGaps: number;
    scanned: number;
    gapsComplete: boolean;
  };
  stores: {
    items: Array<{
      assetId: string;
      displayName: string;
      sourceType: string;
      activityCovered: boolean;
      coveredByFeed: boolean;
      sensors: Array<{ sensor: ActivityOwnSensor; lastSeenAt: string; count: number | null }>;
    }>;
    truncated: boolean;
  };
  activityGaps: { items: Array<{ assetId: string; displayName: string; sourceType: string; scanned: boolean }>; truncated: boolean };
  /** Gateway table names, none credited: name-only (match one or more stores) and uncatalogued (match none). */
  ddrUnplacedTables: { uncatalogued: number; nameOnlyNotCredited: number; nameOnlyCalls: number; truncated: boolean } | null;
}

export async function getActivityFeedCoverage(withinHours = 24): Promise<ActivityFeedCoverage> {
  return apiFetch(`/v1/activity/feeds/coverage?withinHours=${withinHours}`);
}

// ── Phase 1B - shared Data Source registry ────────────────────────────
// A data source = one connector_instance consumed by every licensed module
// (Discovery scans it, DAM captures it, DDR routes to it). Configure once.
export const DATA_SOURCE_CATEGORIES = [
  'dspm_database',
  'dspm_warehouse',
  'dspm_storage',
  'dam_native_audit',
  'dam_cloud_stream',
  'dam_proxy',
  'ddr_target',
] as const;
export type DataSourceCategory = (typeof DATA_SOURCE_CATEGORIES)[number];

export interface DataSource {
  id: string;
  displayName: string;
  category: DataSourceCategory | string;
  vendor: string | null;
  status: string;
  healthStatus: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function listDataSources(): Promise<{ sources: DataSource[]; count: number }> {
  return apiFetch('/v1/data-sources');
}

export interface CreateDataSourceInput {
  category: DataSourceCategory;
  displayName: string;
  config?: Record<string, unknown>;
}

export async function createDataSource(
  input: CreateDataSourceInput,
  idempotencyKey?: string,
): Promise<{ source: DataSource }> {
  return apiFetch('/v1/data-sources', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function deleteDataSource(id: string): Promise<void> {
  await apiFetch(`/v1/data-sources/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Phase 0 - config-key module-relevance matrix ──────────────────────
export type ConfigModuleKey = 'discovery' | 'activity' | 'detection' | 'compliance';
export type ConfigRelevance = 'required' | 'optional' | 'n/a';
export interface ConfigCatalogKey {
  key: string;
  label: string;
  href: string;
  group: string;
  relevance: Record<ConfigModuleKey, ConfigRelevance>;
  source: 'local' | 'federates' | 'adapter';
  inheritPolicy: 'silent' | 'accept-gate' | 'n/a';
  blurb: string;
}
export type ConfigReadiness = 'yes' | 'no' | 'unknown';
export async function getConfigCatalog(): Promise<{
  catalog: ConfigCatalogKey[];
  modules: ConfigModuleKey[];
  byModule: Record<ConfigModuleKey, { required: string[]; optional: string[] }>;
  readiness: Record<string, ConfigReadiness>;
}> {
  return apiFetch('/v1/config-catalog');
}

// ── Foundational / Industry Packs (Phase 6) ───────────────────────────

export type IndustryPackAnchor =
  | 'bfsi_banking'
  | 'bfsi_capital_markets'
  | 'bfsi_insurance'
  | 'bfsi_fintech'
  | 'healthcare'
  | 'pharma'
  | 'insurance'
  | 'edtech'
  | 'retail_ecommerce'
  | 'telecom'
  | 'government_psu'
  | 'manufacturing'
  | 'hospitality_travel'
  | 'saas_tech';

export type IndustryPackSourceKind = 'local' | 'foctta_core';

export interface IndustryPackActivationState {
  packId: IndustryPackAnchor;
  activated: boolean;
  activatedAt: string | null;
  deactivatedAt: string | null;
  source: IndustryPackSourceKind | null;
  externalRef: string | null;
  activatedByUserId: string | null;
  lastSyncedAt: string | null;
}

export interface IndustryPackSummary {
  anchor: IndustryPackAnchor;
  sourcePackId?: string;
  name: string;
  kind?: 'parent' | 'overlay' | 'bundle';
  geographies: string[];
  regulatoryClauses: string[];
  tenantActivation: IndustryPackActivationState;
  perBuActiveCount: number;
}

export interface IndustryPackDetail {
  summary: IndustryPackSummary;
  discoveryAdditions: {
    classifierOverlays: unknown[];
    expectedSystems: unknown[];
    ropaTemplates: unknown[];
    toxicCombinations: unknown[];
  };
  perBuActivations: Array<{
    businessUnitId: string;
    businessUnitName: string;
    businessUnitSlug: string;
    activatedAt: string;
    source: IndustryPackSourceKind;
    externalRef: string | null;
  }>;
}

export interface BusinessUnitPackActivationState {
  businessUnitId: string;
  /** A Discovery anchor ticked here, or a consent slug synced from the consent console. */
  packId: IndustryPackAnchor | (string & {});
  activated: boolean;
  activatedAt: string | null;
  deactivatedAt: string | null;
  source: IndustryPackSourceKind | null;
  externalRef: string | null;
  lastSyncedAt: string | null;
}

/** Why a pack is in force: the consent slug(s) or Discovery tick, through which family. */
export interface PackProvenanceRow {
  packId: string;
  anchor: string;
  /** Display name; shown instead of the anchor when present. */
  name?: string;
  origins: Array<'consent' | 'discovery'>;
  fromIds: string[];
  families: string[];
}

export interface BuPackActivationsResponse {
  activations: BusinessUnitPackActivationState[];
  /** Anchors of the packs in force, translated from what the unit carries. */
  effectivePackIds: IndustryPackAnchor[];
  resolutionMode: 'override' | 'inherited' | 'tenant_default';
  inheritedFromBuId?: string | null;
  fallThroughReason?: 'unit_packs_carry_no_classifiers' | null;
  provenance?: PackProvenanceRow[];
  bundles?: string[];
  unmappedPackIds?: string[];
  suppressedByOverlay?: string[];
}

export async function listIndustryPacks(): Promise<{ packs: IndustryPackSummary[] }> {
  return apiFetch('/v1/foundational/industry-packs');
}

export async function getIndustryPackDetail(
  packId: IndustryPackAnchor,
): Promise<IndustryPackDetail> {
  return apiFetch(`/v1/foundational/industry-packs/${encodeURIComponent(packId)}`);
}

export async function activateIndustryPack(
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: IndustryPackActivationState }> {
  return apiFetch(`/v1/foundational/industry-packs/${encodeURIComponent(packId)}/activate`, {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function deactivateIndustryPack(
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: IndustryPackActivationState }> {
  return apiFetch(`/v1/foundational/industry-packs/${encodeURIComponent(packId)}/deactivate`, {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function listBuIndustryPacks(buId: string): Promise<BuPackActivationsResponse> {
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(buId)}/industry-packs`);
}

export async function activateBuIndustryPack(
  buId: string,
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: BusinessUnitPackActivationState }> {
  return apiFetch(
    `/v1/foundational/business-units/${encodeURIComponent(buId)}/industry-packs/${encodeURIComponent(packId)}/activate`,
    {
      method: 'POST',
      body: JSON.stringify({}),
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

export async function deactivateBuIndustryPack(
  buId: string,
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: BusinessUnitPackActivationState }> {
  return apiFetch(
    `/v1/foundational/business-units/${encodeURIComponent(buId)}/industry-packs/${encodeURIComponent(packId)}/deactivate`,
    {
      method: 'POST',
      body: JSON.stringify({}),
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

// ── Discovery / Classifier Vocab (Phase 6) ────────────────────────────

export type ClassifierOverlayPatternKind = 'regex' | 'keyword' | 'column_name';

export interface ClassifierOverlay {
  id: string;
  tenantId: string;
  packId: IndustryPackAnchor;
  patternKind: ClassifierOverlayPatternKind;
  pattern: string;
  label: string;
  confidenceBoost: number;
  regulations: string[];
  notes: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listClassifierVocab(
  packId: IndustryPackAnchor,
): Promise<{ overlays: ClassifierOverlay[] }> {
  return apiFetch(`/v1/discovery/classifier-vocab/${encodeURIComponent(packId)}`);
}

export type ClassifierChecksum = 'none' | 'luhn' | 'verhoeff' | 'gstin_mod36' | 'mod11';

export async function createClassifierVocab(
  packId: IndustryPackAnchor,
  input: {
    patternKind: ClassifierOverlayPatternKind;
    pattern: string;
    label: string;
    confidenceBoost?: number;
    regulations?: string[];
    notes?: string;
    // Modular Rule Engine: attach a checksum validator to a custom rule.
    checksum?: ClassifierChecksum;
    enabled?: boolean;
  },
  idempotencyKey?: string,
): Promise<{ overlay: ClassifierOverlay }> {
  return apiFetch(`/v1/discovery/classifier-vocab/${encodeURIComponent(packId)}`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function deleteClassifierVocab(
  packId: IndustryPackAnchor,
  overlayId: string,
): Promise<{ overlay: ClassifierOverlay }> {
  return apiFetch(
    `/v1/discovery/classifier-vocab/${encodeURIComponent(packId)}/${encodeURIComponent(overlayId)}`,
    {
      method: 'DELETE',
      body: JSON.stringify({}),
    },
  );
}

// ── Discovery / India Moats (Phase 6) ─────────────────────────────────

export interface AadhaarVaultConfig {
  enabled: boolean;
  protectedFields?: string[];
  tokenizationProvider?: string;
  vaultKmsKeyRef?: string;
}

export interface AccountAggregatorConfig {
  enabled: boolean;
  fipEndpointUrl?: string;
  fiuEndpointUrl?: string;
  callbackUrl?: string;
  kmsKeyRef?: string;
  consentMaxDays?: number;
}

export interface DigilockerBreakGlassConfig {
  enabled: boolean;
  breakGlassApprovers?: string[];
  sessionMaxMinutes?: number;
  allowedReasonCodes?: string[];
}

export interface IndiaMoatsConfig {
  tenantId: string;
  aadhaarVault: AadhaarVaultConfig;
  accountAggregator: AccountAggregatorConfig;
  digilockerBreakGlass: DigilockerBreakGlassConfig;
  updatedAt: string;
}

export type IndiaMoatName = 'aadhaar-vault' | 'account-aggregator' | 'digilocker';

export async function getIndiaMoatsStatus(): Promise<IndiaMoatsConfig> {
  return apiFetch('/v1/discovery/india-moats/status');
}

export async function configureIndiaMoat(
  moat: IndiaMoatName,
  config: AadhaarVaultConfig | AccountAggregatorConfig | DigilockerBreakGlassConfig,
  idempotencyKey?: string,
): Promise<IndiaMoatsConfig> {
  return apiFetch(`/v1/discovery/india-moats/${moat}/configure`, {
    method: 'POST',
    body: JSON.stringify(config),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Platform outbound IPs (G6.4 - doc 22 §6.6.5) ─────────────────────

export interface OutboundEgressEntry {
  id: string;
  cidr: string;
  label: string;
  region: 'in-south-1' | 'ap-south-1' | 'us-east-1' | 'eu-west-1' | 'global';
  provider: 'self_hosted' | 'aws' | 'gcp' | 'azure' | 'cloudflare';
  addedAt: string;
  removingAt?: string;
}

export interface OutboundIpsResponse {
  revision: string;
  generatedAt: string;
  entries: OutboundEgressEntry[];
  notes: string;
}

export async function getOutboundIps(): Promise<OutboundIpsResponse> {
  return apiFetch('/v1/foundational/integrations/outbound-ips');
}

// ── Compliance evidence packs (G3 - doc 22 §4) ────────────────────────

export type EvidencePackFramework = 'DPDPA' | 'SEBI_CSCRF' | 'GDPR' | 'CCPA';
export type EvidencePackStatus = 'pending' | 'building' | 'ready' | 'failed';

export interface EvidencePack {
  id: string;
  framework: EvidencePackFramework;
  periodStart: string;
  periodEnd: string;
  status: EvidencePackStatus;
  generatedAt: string;
  generatedBy: string;
  fileCount: number | null;
  totalBytes: number | null;
  storageUrl: string | null;
  manifestHash: string | null;
  manifestSignature: string | null;
  expiresAt: string | null;
  errorMessage: string | null;
}

export async function listEvidencePacks(params: {
  framework?: EvidencePackFramework;
  status?: EvidencePackStatus;
  limit?: number;
} = {}): Promise<{ packs: EvidencePack[]; count: number }> {
  const q = new URLSearchParams();
  if (params.framework) q.set('framework', params.framework);
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/evidence-packs${suffix}`);
}

export async function getEvidencePack(id: string): Promise<{ pack: EvidencePack }> {
  return apiFetch(`/v1/compliance/evidence-packs/${encodeURIComponent(id)}`);
}

export async function buildEvidencePack(input: {
  framework: EvidencePackFramework;
  periodStart: string;
  periodEnd: string;
}, idempotencyKey?: string): Promise<{ pack: EvidencePack }> {
  return apiFetch('/v1/compliance/evidence-packs/build', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * OBS-13 (2026-05-26) - pull the ZIP bytes for a ready pack and
 * trigger a browser download. Tier A2 backend at
 * apps/api-controller/src/modules/compliance/evidence-packs/handlers.ts:
 * downloadEvidencePack returns 200 application/zip with
 * Content-Disposition attachment when status=ready, 404 if missing,
 * 409 if not ready, 410 if expired. apiFetch can't be used directly
 * because the response is binary, not JSON, so this helper mirrors
 * apiFetch's bearer-token plumbing and converts the body to a Blob
 * that the browser saves via a synthetic <a download> click.
 */
export async function downloadEvidencePack(id: string): Promise<void> {
  const token = getAuthToken();
  const url = `${API_BASE}/v1/compliance/evidence-packs/${encodeURIComponent(id)}/download`;
  const headers: Record<string, string> = { accept: 'application/zip' };
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail =
        (typeof body?.title === 'string' ? body.title : '') +
        (typeof body?.detail === 'string' ? ` - ${body.detail}` : '');
    } catch {
      detail = `${res.status} ${res.statusText}`;
    }
    throw new ApiError(res.status, detail || `Download failed (${res.status})`, null);
  }
  const blob = await res.blob();
  // Prefer the server-suggested filename from Content-Disposition; fall
  // back to a deterministic name if the header isn't readable.
  const cd = res.headers.get('content-disposition') ?? '';
  const filenameMatch = cd.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] ?? `evidence-pack-${id}.zip`;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Firefox finishes the download write before the URL
  // is invalidated (Chrome / Safari handle synchronous revoke fine).
  setTimeout(() => URL.revokeObjectURL(objUrl), 5_000);
}

// ── T1.5.B (2026-05-24) - Compliance Posture (DPO + grievance officer) ──

export interface CompliancePostureResponse {
  coreIntegrated: boolean;
  dpo: {
    appointed: boolean;
    name: string | null;
    email: string | null;
    designationStatus: 'unassigned' | 'partial' | 'complete';
  };
  organization: {
    legalName: string | null;
    industry: string | null;
    primaryRegion: string | null;
    dataResidency: string | null;
    crossBorderTransfer: boolean | null;
  };
  grievances: {
    open: number;
    countsByStatus: Record<string, number>;
    recent: Array<{
      id: string;
      referenceNumber: string;
      status: string;
      createdAt: string;
      slaDeadline: string | null;
      category: string | null;
    }>;
  };
}

export async function getCompliancePosture(): Promise<CompliancePostureResponse> {
  return apiFetch<CompliancePostureResponse>('/v1/foundational/compliance-posture');
}

// ── F (2026-05-25) - Compliance Posture Federation Dashboard ────────

export interface ComplianceFederationResponse {
  coreIntegrated: boolean;
  dataConnections: {
    total: number;
    connected: number;
    error: number;
    pending: number;
  };
  dsars: {
    total: number;
    open: number;
    pastSla: number;
    closed: number;
    rejected: number;
  };
  erasures: {
    total: number;
    active: number;
    blocked: number;
    completed: number;
    partial: number;
    certIssued: number;
  };
  breaches: {
    total: number;
    detected: number;
    contained: number;
    authorityNotified: number;
    pastAuthorityDeadline: number;
    involvingMinors: number;
  };
  vendors: {
    total: number;
    signed: number;
    pending: number;
    expired: number;
    expiringSoon: number;
  };
  risks: {
    total: number;
    open: number;
    mitigating: number;
    closed: number;
    highSeverity: number;
    criticalSeverity: number;
  };
  training: {
    total: number;
    assigned: number;
    completed: number;
    overdue: number;
    complianceRate: number;
  };
}

export async function getCompliancePostureFederation(): Promise<ComplianceFederationResponse> {
  return apiFetch<ComplianceFederationResponse>(
    '/v1/foundational/compliance-posture-federation',
  );
}

// ============================================================
// Wave 1 (2026-05-27) - Notifications module client. Wraps the
// previously-dark /v1/notifications/* surface.
// ============================================================

export type NotificationSeverity = 'critical' | 'high' | 'medium' | 'info';
/**
 * The sources the controller records (SourceProductSchema in
 * apps/api-controller/src/modules/notifications/schemas.ts). A value outside
 * this set is refused with a 400 when sent as the `source` filter, which is
 * what the Notifications page's "Activity" and "Detection" buttons used to do.
 * Held to the controller by src/lib/__tests__/notifications-query-contract.test.ts.
 */
export type NotificationSource =
  | 'discovery'
  | 'core'
  | 'edge'
  | 'compliance'
  | 'foundational';
export type NotificationType =
  | 'finding.critical'
  | 'finding.high'
  | 'decision.blocked'
  | 'breach.opened'
  | 'breach.clock.warning'
  | 'breach.clock.expired'
  | 'dsar.due_soon'
  | 'dsar.overdue'
  | 'consent.revoked'
  | 'evidence.ready'
  | 'connector.failing'
  | 'connector.unhealthy'
  | 'scan.completed'
  | 'rule.proposed'
  | 'playbook.executed'
  | 'system.maintenance'
  | 'system.update'
  | 'bridge.invitation';

export interface NotificationAction {
  key: string;
  label: string;
  icon?: string;
  confirm?: boolean;
}

export interface NotificationItem {
  id: string;
  tenantId: string;
  userId: string | null;
  type: NotificationType;
  severity: NotificationSeverity;
  source: NotificationSource;
  title: string;
  body: string;
  icon: string;
  url: string;
  actions: NotificationAction[];
  unread: boolean;
  unacknowledged: boolean;
  emittedAt: string;
  readAt: string | null;
  acknowledgedAt: string | null;
  metadata: Record<string, unknown>;
}

export interface ListNotificationsResponse {
  notifications: NotificationItem[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface ChannelPrefs {
  in_app: boolean;
  sound: boolean;
  push: boolean;
  email: boolean;
}
export interface NotificationPreferences {
  userId?: string;
  tenantId?: string;
  channels?: Record<string, ChannelPrefs>;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  quietHoursTz?: string;
  pushSubscription?: Record<string, unknown> | null;
  soundEnabled?: boolean;
  pushEnabled?: boolean;
  updatedAt?: string;
}

export async function listNotifications(params?: {
  limit?: number;
  cursor?: string;
  includeRead?: boolean;
  includeDismissed?: boolean;
  type?: NotificationType;
  severity?: NotificationSeverity;
  source?: NotificationSource;
}): Promise<ListNotificationsResponse> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.cursor) q.set('cursor', params.cursor);
  if (params?.includeRead !== undefined) q.set('includeRead', String(params.includeRead));
  if (params?.includeDismissed !== undefined) q.set('includeDismissed', String(params.includeDismissed));
  if (params?.type) q.set('type', params.type);
  if (params?.severity) q.set('severity', params.severity);
  if (params?.source) q.set('source', params.source);
  const qs = q.toString();
  return apiFetch<ListNotificationsResponse>(
    `/v1/notifications/${qs ? `?${qs}` : ''}`,
  );
}

export async function markNotificationRead(id: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/mark-read`, { method: 'POST' });
}

export async function acknowledgeNotification(id: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/acknowledge`, { method: 'POST' });
}

export async function dismissNotification(id: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/dismiss`, { method: 'POST' });
}

export async function snoozeNotification(id: string, untilIso: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/snooze`, {
    method: 'POST',
    body: JSON.stringify({ until: untilIso }),
  });
}

export async function markAllNotificationsRead(): Promise<{ updated: number }> {
  return apiFetch<{ updated: number }>('/v1/notifications/mark-all-read', { method: 'POST' });
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return apiFetch<NotificationPreferences>('/v1/notifications/preferences');
}

export async function updateNotificationPreferences(
  prefs: NotificationPreferences,
): Promise<NotificationPreferences> {
  return apiFetch<NotificationPreferences>('/v1/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

export async function getNotificationVapidPublicKey(): Promise<{ publicKey: string }> {
  return apiFetch<{ publicKey: string }>('/v1/notifications/vapid-public-key');
}

export async function emitTestNotification(): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/v1/notifications/test', { method: 'POST' });
}

// ============================================================
// Wave 1 (2026-05-27) - DPIA + SDF document/scaffold helpers.
// Backend has `/v1/compliance/dpia/scaffold` (inventory pre-fill)
// + `/dpia/:id/document?format=pdf|html` + `/sdf-obligations/document`
// - these have been ready since Tier A4 (2026-05-26) but never wired
// in the Dpia + Sdf pages. SEBI/DPDPA evidence shortcut.
// ============================================================

export interface DpiaScaffold {
  title: string | null;
  purpose: string | null;
  legalBasis: string | null;
  dataCategories: string[];
  affectedSubjectsEstimate: number | null;
  riskRegister: Array<{
    id: string;
    description: string;
    likelihood: 'low' | 'medium' | 'high';
    impact: 'low' | 'medium' | 'high';
    residualRisk: 'low' | 'medium' | 'high';
    mitigation: string;
  }>;
  sourceArtefacts: Array<{ kind: string; ref: string; label?: string }>;
}

export async function getDpiaScaffold(params?: {
  businessUnitId?: string;
  processingActivityId?: string;
}): Promise<DpiaScaffold> {
  const q = new URLSearchParams();
  if (params?.businessUnitId) q.set('businessUnitId', params.businessUnitId);
  if (params?.processingActivityId) q.set('processingActivityId', params.processingActivityId);
  return apiFetch<DpiaScaffold>(
    `/v1/compliance/dpia/scaffold${q.toString() ? `?${q}` : ''}`,
  );
}

/**
 * Triggers a browser download of the DPIA document. format='pdf' is
 * the regulator-grade artefact; 'html' is the human-review draft.
 */
export function downloadDpiaDocument(id: string, format: 'pdf' | 'html' = 'pdf'): void {
  const token = getAuthToken();
  const url = `${API_BASE}/v1/compliance/dpia/${encodeURIComponent(id)}/document?format=${format}${
    token ? `&access_token=${encodeURIComponent(token)}` : ''
  }`;
  // Direct download via temporary anchor - preserves Content-Disposition.
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.click();
}

export function downloadSdfDocument(): void {
  const token = getAuthToken();
  const url = `${API_BASE}/v1/compliance/sdf-obligations/document${
    token ? `?access_token=${encodeURIComponent(token)}` : ''
  }`;
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.click();
}

// ============================================================
// Wave 2 (2026-05-27) - on-demand UEBA / Sonar / P-Wave analyze.
// These POST endpoints have shipped since Phase 3 Day 7-10 but
// were only callable from a CronJob - no SOC operator had a way
// to trigger an analysis pass over the last N hours.
// ============================================================

export interface AnalyzeUebaInput {
  baselineDays?: number;
  recentHours?: number;
  actorUserId?: string;
}
export interface AnalyzeSonarInput {
  baselineDays?: number;
  recentHours?: number;
  application?: string;
  klThreshold?: number;
  minEventCount?: number;
}
export interface AnalyzeEarlyWarningInput {
  recentHours?: number;
  actorUserId?: string;
  reconBurstColdTableThreshold?: number;
  reconBurstWindowMinutes?: number;
}

export interface AnalyzeResult {
  findings: unknown[];
  meta?: Record<string, unknown>;
}

export async function analyzeUeba(input: AnalyzeUebaInput = {}): Promise<AnalyzeResult> {
  return apiFetch<AnalyzeResult>('/v1/activity/ueba/analyze', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function analyzeSonar(input: AnalyzeSonarInput = {}): Promise<AnalyzeResult> {
  return apiFetch<AnalyzeResult>('/v1/activity/sonar/analyze', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function analyzeEarlyWarning(
  input: AnalyzeEarlyWarningInput = {},
): Promise<AnalyzeResult> {
  return apiFetch<AnalyzeResult>('/v1/activity/early-warning/analyze', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============================================================
// Wave 3 (2026-05-27) - Industry-overview + BU-compliance status.
// These two endpoints surface the three-axis taxonomy (Industry
// Profile × Classification Family × Compliance Bundle) - the
// platform's differentiator narrative. They've been DB-backed
// since the foundational module shipped but no page consumed
// them: the unified rollup story was invisible to evaluators.
// ============================================================

export interface SeverityCounts {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface IndustryOverview {
  filter: {
    industryProfile?: string;
    classificationFamily?: string;
    complianceBundle?: string;
  };
  findings: SeverityCounts;
  decisions: { total: number; byDecisionType: Record<string, number> };
  snapshotsActive: number;
  generatedAt: string;
}

export interface BuComplianceStatus {
  businessUnitId: string;
  activeProfileIds: string[];
  activeFamilyIds: string[];
  activeBundleIds: string[];
  activeOverlayIds: string[];
  taxonomyVersion: string;
  findings: SeverityCounts;
  decisions: { total: number; byDecisionType: Record<string, number> };
  latestSnapshotAt: string | null;
  generatedAt: string;
}

export async function getIndustryOverview(filter?: {
  industryProfile?: string;
  classificationFamily?: string;
  complianceBundle?: string;
}): Promise<IndustryOverview> {
  const q = new URLSearchParams();
  if (filter?.industryProfile) q.set('industryProfile', filter.industryProfile);
  if (filter?.classificationFamily) q.set('classificationFamily', filter.classificationFamily);
  if (filter?.complianceBundle) q.set('complianceBundle', filter.complianceBundle);
  return apiFetch<IndustryOverview>(
    `/v1/foundational/industry-overview${q.toString() ? `?${q}` : ''}`,
  );
}

export async function getBuComplianceStatus(businessUnitId: string): Promise<BuComplianceStatus> {
  return apiFetch<BuComplianceStatus>(
    `/v1/foundational/bu-compliance-status?businessUnitId=${encodeURIComponent(businessUnitId)}`,
  );
}

// ============================================================
// Wave 4 (2026-05-27) - Frontier operator endpoints that finish
// the Federated / SMPC / PQ workflows. Existing pages already
// wire the "open"/"generate"/"recruit" endpoints but the
// follow-up "submit/aggregate/sign/verify" endpoints had no UI.
// ============================================================

export interface SubmitFederatedUpdateInput {
  roundId: string;
  delta: number[];
  sampleCount: number;
  differentialPrivacyEpsilon?: number;
  differentialPrivacyDelta?: number;
}
export interface SubmitFederatedUpdateResult {
  participantId: string;
  encryptedUpdateRef: string;
  accepted: boolean;
}
export async function submitFederatedUpdate(
  input: SubmitFederatedUpdateInput,
): Promise<SubmitFederatedUpdateResult> {
  return apiFetch<SubmitFederatedUpdateResult>('/v1/frontier/federated/updates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface AggregateFederatedRoundInput {
  roundId: string;
}
export interface AggregateFederatedRoundResult {
  roundId: string;
  status: string;
  participantCount: number;
  aggregatedUpdateRef: string | null;
}
export async function aggregateFederatedRound(
  input: AggregateFederatedRoundInput,
): Promise<AggregateFederatedRoundResult> {
  return apiFetch<AggregateFederatedRoundResult>('/v1/frontier/federated/aggregate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface SubmitSmpcShareInput {
  sessionId: string;
  shareIndex: number;
  encryptedShareRef: string;
}
export interface SubmitSmpcShareResult {
  shareId: string;
  sessionId: string;
  shareIndex: number;
}
export async function submitSmpcShare(input: SubmitSmpcShareInput): Promise<SubmitSmpcShareResult> {
  return apiFetch<SubmitSmpcShareResult>('/v1/frontier/smpc/shares', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface PqSignInput {
  keyId: string;
  targetType: 'audit_chain_block' | 'breach_notification' | 'compliance_replay';
  targetId: string;
  payload: string;
}
export interface PqSignResult {
  signatureId: string;
  targetType: string;
  targetId: string;
  payloadHash: string;
  signatureB64: string;
  algorithm: string;
}
export async function pqSign(input: PqSignInput): Promise<PqSignResult> {
  return apiFetch<PqSignResult>('/v1/frontier/pq/sign', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface PqVerifyInput {
  algorithm: string;
  publicKeyB64: string;
  privateKeyKmsRef: string;
  payload: string;
  signatureB64: string;
}
export interface PqVerifyResult {
  valid: boolean;
  payloadHash: string;
}
export async function pqVerify(input: PqVerifyInput): Promise<PqVerifyResult> {
  return apiFetch<PqVerifyResult>('/v1/frontier/pq/verify', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============================================================
// Wave 5 (2026-05-27) - quick-win surfaces from the B+D bucket.
// ============================================================

export interface ReplayRegulator {
  key: string;
  breachClockHours: number;
  enforcesSection9Equivalent: boolean;
  activeForTenant: boolean;
}
export async function listComplianceReplayRegulators(): Promise<{
  regulators: ReplayRegulator[];
  coreIntegrated: boolean;
  tenantId: string;
}> {
  return apiFetch('/v1/frontier/replay/regulators');
}

export interface RegisterNetworkTapInput {
  tapName: string;
  sourceKind:
    | 'aws_vpc_traffic_mirror'
    | 'azure_vnet_tap'
    | 'gcp_packet_mirror'
    | 'on_prem_span_port'
    | 'erspan';
  sourceRef: string;
  targetSqlProtocols: string;
  bytesPerSecond?: number;
}
export interface RegisterNetworkTapResult {
  tapId: string;
  status: string;
}
export async function registerNetworkTap(
  input: RegisterNetworkTapInput,
): Promise<RegisterNetworkTapResult> {
  return apiFetch<RegisterNetworkTapResult>('/v1/frontier/sensors/network-tap', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function acknowledgeUebaFinding(id: string): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/ueba/findings/${encodeURIComponent(id)}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
export async function acknowledgeSonarFinding(id: string): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/sonar/findings/${encodeURIComponent(id)}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Fix D (2026-05-28) - dismiss or escalate a finding with a regulatory-
 * grade reason string. Reason is captured into the audit-chain payload;
 * the row carries the new status + the transitioning actor.
 *
 * Status values: 'dismissed' (false-positive, no action) or 'escalated'
 * (confirmed-threat, hand-off to higher tier).
 *
 * Reason must be 3..1000 chars per the server-side Zod schema
 * (PatchFindingStatusSchema in apps/api-controller/src/modules/activity/
 * schemas.ts). The UI enforces the min upfront.
 */
export async function patchUebaFindingStatus(
  id: string,
  status: 'dismissed' | 'escalated',
  reason: string,
): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/ueba/findings/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
}
export async function patchSonarFindingStatus(
  id: string,
  status: 'dismissed' | 'escalated',
  reason: string,
): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/sonar/findings/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
}

// ============================================================
// Wave 5 Group-A (2026-05-27) - synchronous rule + enforcement
// testers, on-demand classify. All backends shipped previously.
// ============================================================

// POST /v1/detection/evaluate
// schema: EvaluateInputSchema {finding?, event?, context?} (strict) →
// evaluateAndRecord() : { decision(row), engineResult, redundancy, requestId }
export interface EvaluateRuleInput {
  finding?: Record<string, unknown>;
  event?: Record<string, unknown>;
  context?: Record<string, unknown>;
}
export interface EvaluateRuleResult {
  decision: { id: string; decision: string; requestId: string };
  engineResult: {
    finalDecision: string;
    finalExplanation: string;
    matchedRules: Array<{ ruleName: string }>;
  };
  redundancy: {
    finalDecision: 'allow' | 'mask' | 'alert' | 'step_up' | 'break_glass' | 'quarantine' | 'block';
    explanation: string;
    degraded: boolean;
    votes: Array<{
      engine: string;
      decision: string;
      confidence: number;
      explanation: string;
    }>;
  };
  requestId: string;
}
export async function evaluateDetectionRule(input: EvaluateRuleInput): Promise<EvaluateRuleResult> {
  return apiFetch<EvaluateRuleResult>('/v1/detection/evaluate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// POST /v1/detection/enforcement/run
// schema: RunEnforcementSchema → runEnforcementPipeline() : EnforcementPipelineResult.
// This simulates a SINGLE database query through the full enforcement
// pipeline (geofence, mass-export pre-cog, consent firewall, provenance,
// section-9) and returns the strictest decision across engines.
export interface RunEnforcementInput {
  vendor: DbVendor;
  tablesTouched: Array<{ name: string; holdsMinorData?: boolean }>;
  piiCategoriesTouched?: string[];
  actorRole: string;
  actorPermissions?: string[];
  sourceIp?: string;
  /** ISO 3166-1 alpha-2, e.g. "IN". */
  sourceCountryCode: string;
  statementType: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'EXPORT' | 'DDL' | 'OTHER';
  rowsReturned?: number;
  actorBaselineRowsPerQuery?: number;
  guardianConsentToken?: string;
  consentRecordId?: string;
  purposeDeclared?: string;
  principalIdentifierHashes?: string[];
  applicableRegulators?: Array<'DPDPA' | 'GDPR' | 'CCPA' | 'PIPL' | 'POPIA' | 'LGPD'>;
  transferSafeguardInPlace?: 'scc' | 'bcr' | 'explicit_consent' | 'none';
  failOpenOnUnavailable?: boolean;
  piiColumnsBeingWritten?: string[];
  testConsentLedger?: Array<{
    principalIdentifierHash: string;
    purpose: string;
    hasActiveConsent: boolean;
  }>;
  testValidGuardianTokens?: string[];
  testValidConsentRecordIds?: string[];
}
export interface RunEnforcementResult {
  decision: 'allow' | 'mask' | 'step_up' | 'block';
  requestId: string;
  reasons: Array<{ engine: string; decision: string; reason: string }>;
  syntheticError?: {
    code: string;
    errorNumber: number;
    message: string;
    severity: string;
  };
  massExportMultiplier?: number;
}
export async function runDetectionEnforcement(
  input: RunEnforcementInput,
): Promise<RunEnforcementResult> {
  return apiFetch<RunEnforcementResult>('/v1/detection/enforcement/run', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface ClassifySamplesInput {
  columnName: string;
  sampleValues: string[];
  /**
   * Declare that these samples came from OCR of a scanned document. Checksum-
   * backed identifiers then get one OCR-confusion repair attempt, and values
   * that match the format but cannot be confirmed surface as `ocr_unverified`
   * instead of being dropped. MUST stay false for digital text - relaxing the
   * checksum there would resurrect the 12-digit account-number false positives.
   */
  fromOcr?: boolean;
}
export interface ClassifySamplesResult {
  /** #273 item 4: the trained classifiers could not vote on this call (the vote shown is without them). */
  trainedUnavailable?: { reason: 'error' | 'timeout' };
  label: string;
  confidence: number;
  rationale: string;
  tier: number;
  /**
   * Set when the winning value was RECONSTRUCTED from OCR damage rather than
   * read cleanly, so the UI can say so instead of implying a confirmed read.
   */
  ocrUnverified?: boolean;
  /** The rescue's own explanation (which substitution, or why unconfirmable). */
  ocrRationale?: string;
  alternateLabels: Array<{ label: string; confidence: number }>;
  /**
   * Checksum prevalence for the winning label, present only for
   * checksum-backed labels (Aadhaar / credit card / GSTIN). Drives the
   * "1 of 6 sampled values passed the Verhoeff checksum" line.
   */
  validation?: { matched: number; validated: number; checksum: string };
  /**
   * Q26 (decision 146): the tenant's custom regex rules this call could not
   * run (`refused`) or finish (`unevaluated`). They detected nothing on these
   * samples; the page must say so, never hide it.
   */
  notEvaluated?: NotEvaluatedRule[];
}

export interface NotEvaluatedRule {
  label: string;
  pattern: string;
  status: 'refused' | 'unevaluated';
  reason: string;
  /** Samples it matched before it stopped (it voted on those); 0 = it detected nothing. */
  matched?: number;
}

/**
 * Raw shape the backend /v1/discovery/classify actually returns (the
 * edge-scanner contract): a ranked `votes` array + per-engine health. The
 * on-demand panel had drifted from this - it sent `columnName`/`sampleValues`
 * (the backend's `.strict()` schema wants `samples`/`columnHint`) and expected
 * a flat `{label,confidence,rationale}`, so every submit 400'd with
 * "Request did not match expected schema" before the classifier ran. We now
 * speak the real contract and adapt the response for display.
 */
interface ClassifyRawResponse {
  votes: Array<{
    label: string;
    confidence: number;
    agreement: 'exact' | 'strong' | 'medium' | 'single_engine' | 'disagreement';
    flagged: boolean;
    engineVotes: Array<{ engine: string; confidence: number }>;
    validation?: { matched: number; validated: number; checksum: string };
    /** Present on OCR-rescued votes: the value was reconstructed, not read cleanly. */
    tier?: string;
    /** Human-readable explanation for an OCR-rescued vote. */
    rationale?: string;
  }>;
  engines: Array<{ engine: string; ok: boolean; latencyMs: number; errorMessage?: string }>;
  edm?: { datasetsConsulted: number; matchedValues: number };
  notEvaluated?: NotEvaluatedRule[];
  trainedClassifiers?: { state: 'ran' | 'none' | 'unavailable'; reason?: 'error' | 'timeout'; classifiers: number };
}

const AGREEMENT_TEXT: Record<string, string> = {
  strong: 'strong agreement',
  medium: 'moderate agreement',
  single_engine: 'a single engine',
  disagreement: 'partial agreement',
};

function tierFromEngine(engine: string): number {
  // EDM is Tier-0: exact membership in the tenant's own data, above every
  // pattern tier. It carries no digit, so the generic regex would mislabel it
  // Tier-1.
  if (engine === 'edm_exact') return 0;
  const m = /tier(\d)/.exec(engine);
  return m ? Number(m[1]) : 1;
}

export async function classifySamples(input: ClassifySamplesInput): Promise<ClassifySamplesResult> {
  const raw = await apiFetch<ClassifyRawResponse>('/v1/discovery/classify', {
    method: 'POST',
    // Correct field names for the strict backend schema.
    body: JSON.stringify({
      samples: input.sampleValues,
      columnHint: input.columnName,
      ...(input.fromOcr ? { fromOcr: true } : {}),
    }),
  });
  const notEvaluated = {
    ...(raw.notEvaluated && raw.notEvaluated.length > 0 ? { notEvaluated: raw.notEvaluated } : {}),
    ...(raw.trainedClassifiers?.state === 'unavailable'
      ? { trainedUnavailable: { reason: raw.trainedClassifiers.reason ?? 'error' } }
      : {}),
  };
  const top = raw.votes[0];
  if (!top) {
    return {
      ...notEvaluated,
      label: 'no PII detected',
      confidence: 0,
      rationale:
        'No detector matched these values - expected for non-PII data, or when a value fails its checksum (e.g. a 12-digit number that is not a valid Aadhaar).',
      tier: 1,
      alternateLabels: [],
    };
  }
  const enginesUsed = [...new Set(top.engineVotes.map((e) => e.engine))];
  const winningTier = enginesUsed.length ? Math.min(...enginesUsed.map(tierFromEngine)) : 1;
  // An exact match is a different KIND of claim, so it gets its own sentence.
  // Saying it "won with strong agreement across N engines" would describe a
  // vote that never happened - the value was simply in the customer's list.
  const rationale =
    top.agreement === 'exact'
      ? `"${top.label}" is an EXACT match against a dataset you uploaded - no pattern matching involved, so this cannot be a false positive.`
      : `"${top.label}" won with ${AGREEMENT_TEXT[top.agreement] ?? top.agreement} across ${enginesUsed.length} engine(s): ${enginesUsed.join(', ')}.`;
  return {
    label: top.label,
    confidence: top.confidence,
    // An OCR-rescued value was RECONSTRUCTED. Saying it "won with agreement
    // across N engines" would overstate it, so the rescue's own explanation
    // replaces the generic voting sentence.
    rationale: top.tier === 'ocr_unverified' && top.rationale ? top.rationale : rationale,
    tier: winningTier,
    ...(top.tier === 'ocr_unverified'
      ? { ocrUnverified: true, ...(top.rationale ? { ocrRationale: top.rationale } : {}) }
      : {}),
    alternateLabels: raw.votes.slice(1, 4).map((v) => ({ label: v.label, confidence: v.confidence })),
    ...(top.validation ? { validation: top.validation } : {}),
    ...notEvaluated,
  };
}

// ============================================================
// Wave 5 Group-C (2026-05-27) - effective-config audit resolver.
// Returns the union of every classifier overlay / expected system /
// playbook / toxic combo that applies under the current
// taxonomy snapshot for this BU (or tenant default).
// ============================================================

export interface EffectiveConfig {
  businessUnitId: string | null;
  resolution: string;
  /** Stored ids at the level in force (consent slugs and/or Discovery anchors). */
  activePackIds: string[];
  /** The Discovery packs those ids resolved to. */
  resolvedPackIds?: string[];
  provenance?: PackProvenanceRow[];
  fallThroughReason?: 'unit_packs_carry_no_classifiers' | null;
  activeProfileIds?: string[];
  activeFamilyIds?: string[];
  activeBundleIds?: string[];
  suppressedProfileIds?: string[];
  unmappedProfileIds?: string[];
  taxonomyVersion?: string;
  snapshotHash?: string;
  classifierOverlays: Array<{
    packId: string;
    label: string;
    regexPatterns: string[];
    keywords: string[];
    confidenceBoost: number;
    regulations: string[];
  }>;
  expectedSystems: Array<{
    packId: string;
    slug: string;
    name: string;
    connectorCategory: string | null;
  }>;
  ropaTemplates: Array<{
    packId: string;
    name: string;
    defaultLegalBasis: string;
    dataCategories: string[];
  }>;
  toxicCombinations: Array<{
    packId: string;
    labels: string[];
    severity: 'critical' | 'high' | 'medium';
    rationale: string;
  }>;
  regulatoryClauses: string[];
  ddrPlaybooks: Array<{
    packId: string;
    playbookId: string;
    name: string;
    regulations: string[];
  }>;
}

export async function getEffectiveConfig(businessUnitId?: string): Promise<EffectiveConfig> {
  const q = businessUnitId ? `?buId=${encodeURIComponent(businessUnitId)}` : '';
  // The API answers `{ effectiveConfig: {...} }`. This returned the envelope as
  // if it were the config, so the page read `config.activePackIds.length` off
  // the wrapper and threw before it could render anything.
  const res = await apiFetch<{ effectiveConfig: EffectiveConfig }>(
    `/v1/foundational/effective-config${q}`,
  );
  return res.effectiveConfig;
}

// ============================================================
// Wave 7 (2026-05-27) - backend GET routes added for the last
// 5 dark surfaces. Each pairs with a viewer page or section.
// ============================================================

export interface NotifySendLogEntry {
  id: string;
  tenantId: string;
  channel: 'email' | 'sms';
  providerId: string | null;
  providerKind: string;
  idempotencyKey: string | null;
  toAddress: string;
  subject: string | null;
  templateKey: string | null;
  status: string;
  attemptNumber: number;
  providerMessageId: string | null;
  errorMessage: string | null;
  triggeredByKind: string | null;
  triggeredById: string | null;
  createdAt: string;
  updatedAt: string;
}
export async function listNotifySendLog(params?: {
  limit?: number;
  channel?: 'email' | 'sms';
  status?: string;
}): Promise<{ entries: NotifySendLogEntry[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.channel) q.set('channel', params.channel);
  if (params?.status) q.set('status', params.status);
  return apiFetch(`/v1/notify/send-log${q.toString() ? `?${q}` : ''}`);
}

export interface BootstrapLogEntry {
  id: string;
  tenantId: string;
  eventId: string;
  eventType: string;
  coreTenantId: string | null;
  status: string;
  payload: Record<string, unknown>;
  stepResults: unknown[];
  errorDetail: string | null;
  receivedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export async function listBootstrapLog(params?: {
  limit?: number;
  status?: string;
}): Promise<{ entries: BootstrapLogEntry[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.status) q.set('status', params.status);
  return apiFetch(`/v1/foundational/bootstrap-log${q.toString() ? `?${q}` : ''}`);
}

export interface InboundEventEntry {
  id: string;
  tenantId: string;
  connectorId: string;
  eventCategory: string;
  eventType: string;
  severity: string | null;
  externalRef: string | null;
  correlationId: string | null;
  rawPayload: Record<string, unknown>;
  normalized: Record<string, unknown> | null;
  processingStatus: string;
  processedAt: string | null;
  actionsTaken: unknown[];
  errorMessage: string | null;
  createdAt: string;
}
export async function listInboundEvents(params?: {
  limit?: number;
  eventType?: string;
}): Promise<{ entries: InboundEventEntry[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.eventType) q.set('eventType', params.eventType);
  return apiFetch(`/v1/foundational/inbound-events${q.toString() ? `?${q}` : ''}`);
}

export interface ScannerCapability {
  id: string;
  label: string;
  enabled: boolean;
}
export interface EdgeScannerCapabilities {
  capabilities: {
    discovery: {
      scanners: ScannerCapability[];
      classifierTiers: ScannerCapability[];
      features: ScannerCapability[];
    };
    activity: { readers: ScannerCapability[] };
    detection: { sidecarRewriters: ScannerCapability[] };
  };
}
export async function getEdgeScannerCapabilities(): Promise<EdgeScannerCapabilities> {
  return apiFetch<EdgeScannerCapabilities>('/v1/edge/scanner-capabilities');
}

export interface Tier4ExemplarSummary {
  label: string;
  sampleCount: number;
  examples: string[];
}
export async function listTier4Exemplars(): Promise<{
  exemplars: Tier4ExemplarSummary[];
  total: number;
}> {
  return apiFetch('/v1/discovery/tier4-exemplars');
}

// ── Admin → Users (T1.1, 2026-05-28) ─────────────────────────────
//
// Frontend for the /v1/admin/users routes shipped in commit c409666
// (backend ready; this finishes the end-to-end loop). Per Discovery
// Independence Principle (CLAUDE.md §14), every standalone customer
// has this surface - they don't need a Core deployment to manage
// their own users.

export type DiscoveryUserStatus = 'invited' | 'active' | 'suspended';

export interface DiscoveryUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  status: DiscoveryUserStatus;
  externalRef: string | null;
  invitedAt: string;
  activatedAt: string | null;
  lastLoginAt: string | null;
  mfaEnrolledAt: string | null;
  invitedByActor: string | null;
  /**
   * A secret was staged but never confirmed (an abandoned enrolment). Optional
   * on the wire for an older controller.
   */
  mfaSecretStaged?: boolean;
  /** The MFA step is locked until then (5 wrong codes); null when not locked. */
  mfaLockedUntil?: string | null;
  /** Consecutive wrong MFA codes since the last correct one. */
  mfaFailedAttempts?: number;
  /**
   * 'foctta_core' when FOCTTA Consent keeps this person (30 Oct 2026): shown
   * read-only; the API refuses role change, suspend and delete (409).
   */
  managedBy?: string | null;
  /** Who suspended this person: 'scim' | 'admin' | 'consent' | 'core_role'; null otherwise. */
  suspendedBy?: string | null;
}

/**
 * Whether FOCTTA Consent owns this tenant's people and roles, and where to
 * manage them (30 Oct 2026). Absent from an older controller: read as not linked.
 */
export interface AdminUsersConsentLink {
  linked: boolean;
  /** Consent's base address, when it is known. */
  consentUrl: string | null;
  /** Consent's page where people are invited and their roles changed. */
  peopleUrl: string | null;
}

/** What an invitation from a standalone tenant did. */
export interface AdminInvitationResult {
  sent: boolean;
  /** 'mock': mail is not configured, the link went to the controller log. */
  provider?: string;
  warning?: string;
  expiresAt?: string;
}

export async function listAdminUsers(params?: {
  status?: DiscoveryUserStatus;
  role?: string;
  limit?: number;
  offset?: number;
}): Promise<{ users: DiscoveryUser[]; count: number; consent?: AdminUsersConsentLink }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.role) q.set('role', params.role);
  if (params?.limit !== undefined) q.set('limit', String(params.limit));
  if (params?.offset !== undefined) q.set('offset', String(params.offset));
  return apiFetch<{ users: DiscoveryUser[]; count: number; consent?: AdminUsersConsentLink }>(
    `/v1/admin/users${q.toString() ? `?${q}` : ''}`,
  );
}

export async function inviteAdminUser(args: {
  email: string;
  fullName: string;
  role: string;
}): Promise<{ user: DiscoveryUser; invitation?: AdminInvitationResult }> {
  return apiFetch<{ user: DiscoveryUser; invitation?: AdminInvitationResult }>('/v1/admin/users', {
    method: 'POST',
    body: JSON.stringify(args),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function patchAdminUser(
  id: string,
  patch: { role?: string; status?: DiscoveryUserStatus },
): Promise<{ user: DiscoveryUser }> {
  return apiFetch<{ user: DiscoveryUser }>(`/v1/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteAdminUser(id: string): Promise<{ user: DiscoveryUser }> {
  return apiFetch<{ user: DiscoveryUser }>(`/v1/admin/users/${id}`, {
    method: 'DELETE',
  });
}

export interface AdminMfaResetResult {
  user: DiscoveryUser;
  /** Outstanding sign-in links the reset revoked. */
  linksRevoked?: number;
  /** The password was cleared too ("also require a new password"). */
  passwordCleared?: boolean;
  /** Present when a new password was required: whether the reset link was emailed. */
  resetLink?: {
    sent: boolean;
    provider?: string;
    /** `link_failed`: the link could not be issued (the reset itself went through). */
    reason?: 'account_not_active' | 'tenant_not_found' | 'link_failed' | 'send_failed';
  };
}

/**
 * Remove a person's MFA factor so they enrol again at their next sign-in
 * (2026-09-27). `admin:users:write`; refused (403 `mfa_reset_self`) for one's
 * own account; audited as `user.mfa_reset`. It also clears an MFA lock and a
 * staged, unconfirmed enrolment, and revokes the person's outstanding sign-in
 * links. `requireNewPassword` also clears their password and emails a reset
 * link (active accounts): for an account that may be in someone else's hands.
 */
export async function resetAdminUserMfa(
  id: string,
  opts: { requireNewPassword?: boolean } = {},
): Promise<AdminMfaResetResult> {
  return apiFetch<AdminMfaResetResult>(`/v1/admin/users/${id}/mfa/reset`, {
    method: 'POST',
    body: JSON.stringify(opts.requireNewPassword ? { requireNewPassword: true } : {}),
  });
}

// ── Admin → Health (T1.2, 2026-05-28) ────────────────────────────
//
// Tenant-facing aggregate health snapshot. Backend = /v1/admin/health
// (modules/admin/health/routes.ts).

/** The newest scheduled verification of the audit chain (release 3.3 C3). */
export interface TenantHealthChainVerification {
  verifiedAt: string;
  status: 'intact' | 'broken' | 'error';
  blocksScanned: number;
  headBlockIndex: number | null;
  brokenAtBlockIndex: number | null;
  problem: string | null;
  detail: string | null;
}
export interface TenantHealthAuditChain {
  totalBlocks: number;
  headBlockIndex: number | null;
  lastWriteAt: string | null;
  /** true = verified intact, false = verified broken, null = not verified yet. */
  chainIntact: boolean | null;
  /** The newest verification run; null (or absent from an older controller) until one has finished. */
  lastVerification?: TenantHealthChainVerification | null;
  /** When the newest run could not read the chain: the newest run that could. */
  lastConclusiveVerification?: TenantHealthChainVerification | null;
}
export interface TenantHealthScannerSeries {
  lastFindingAt: string | null;
  recent24h: number;
}
export interface TenantHealthScanners {
  ueba: TenantHealthScannerSeries;
  sonar: TenantHealthScannerSeries;
  earlyWarning: TenantHealthScannerSeries;
}
export interface TenantHealthEdgeFleet {
  total: number;
  healthy: number;
  degraded: number;
  stale: number;
  down: number;
  pending: number;
  decommissioned: number;
}
export interface TenantHealthAuditVault {
  tenantId: string;
  totalBlocks: number;
  mirroredBlocks: number;
  unmirroredBlocks: number;
  oldestUnmirroredAt: string | null;
  newestMirroredAt: string | null;
  retentionYears: number;
  bucket: string | null;
}
export interface TenantHealthErrorRate {
  windowSeconds: number;
  total: number;
  status4xx: number;
  status5xx: number;
}
export interface TenantHealthCompound {
  openCompoundFindings: number;
  lastSignalAt: string | null;
}
export type SubsystemOrError<T> = T | { error: string };
export interface TenantHealthSnapshot {
  tenantId: string;
  generatedAt: string;
  auditChain: SubsystemOrError<TenantHealthAuditChain>;
  scanners: SubsystemOrError<TenantHealthScanners>;
  edgeFleet: SubsystemOrError<TenantHealthEdgeFleet>;
  auditVault: SubsystemOrError<TenantHealthAuditVault>;
  errorRate: SubsystemOrError<TenantHealthErrorRate>;
  compound: SubsystemOrError<TenantHealthCompound>;
  overall: {
    status: 'healthy' | 'degraded' | 'unknown';
    weakSpots: ReadonlyArray<string>;
  };
}

export async function getTenantHealth(): Promise<TenantHealthSnapshot> {
  return apiFetch<TenantHealthSnapshot>('/v1/admin/health');
}

// ── Audit-vault on-demand actions (T1.3, 2026-05-29) ─────────────
//
// Manual triggers for the S3 WORM mirror + tamper-verify runs that
// otherwise fire on a schedule. Surfaced as buttons on the admin
// Health page so a DPO/IT-Admin can force a mirror before an audit
// and prove chain integrity on demand.

export interface AuditVaultMirrorResult {
  tenantId: string;
  blocksScanned: number;
  blocksMirrored: number;
  blocksFailed: number;
  durationMs: number;
  vaultEnabled: boolean;
}

export interface AuditVaultVerifyResult {
  tenantId: string;
  blocksChecked: number;
  blocksVerified: number;
  blocksTampered: number;
  blocksMissing: number;
  details: ReadonlyArray<{
    blockId: string;
    blockIndex: number;
    s3Key: string;
    status: 'verified' | 'tampered' | 'missing';
    reason?: string;
  }>;
}

export async function mirrorAuditVault(maxBlocks?: number): Promise<AuditVaultMirrorResult> {
  return apiFetch<AuditVaultMirrorResult>('/v1/audit/vault/mirror', {
    method: 'POST',
    body: JSON.stringify(maxBlocks !== undefined ? { maxBlocks } : {}),
  });
}

export async function verifyAuditVault(limit?: number): Promise<AuditVaultVerifyResult> {
  return apiFetch<AuditVaultVerifyResult>('/v1/audit/vault/verify', {
    method: 'POST',
    body: JSON.stringify(limit !== undefined ? { limit } : {}),
  });
}

// ── Admin → Business-unit source (CSV import) ────────────────────
// Bulk, non-core path for loading a BU tree. Dry run executes the same
// validation as the real import and only skips the writes.

export interface BuSourceCatalogueEntry {
  kind: string;
  label: string;
  description: string;
  requiresPairing: boolean;
}

export interface BuSourceState {
  source: { kind: string; enabled: boolean; updatedAt?: string | null } | null;
  catalogue: BuSourceCatalogueEntry[];
  effective: string;
  note?: string;
}

export interface BuImportResult {
  dryRun: boolean;
  created: number;
  updated: number;
  rejected: number;
  parentsLinked: number;
  rejections: Array<{ line: number; reason: string }>;
  drift: Array<{ externalRef: string; name: string }>;
}

export async function getBuSource(): Promise<BuSourceState> {
  return apiFetch<BuSourceState>('/v1/admin/bu-source');
}

export async function setBuSource(args: {
  kind: string;
  enabled: boolean;
}): Promise<{ source: { kind: string; enabled: boolean } }> {
  return apiFetch<{ source: { kind: string; enabled: boolean } }>('/v1/admin/bu-source', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function importBuCsv(csv: string, dryRun: boolean): Promise<BuImportResult> {
  return apiFetch<BuImportResult>('/v1/admin/bu-source/import-csv', {
    method: 'POST',
    body: JSON.stringify({ csv, dryRun }),
  });
}

// ── Admin → Consent Source (alt-CMP) ─────────────────────────────
// Lets a standalone (no privacy-core) tenant point the consent-aware
// query firewall at their own CMP instead of needing a seeded DB row.

export interface ConsentSourceCatalogueEntry {
  kind: string;
  label: string;
  fields: string[];
  secretField: string | null;
}

export interface ConsentSourceConfig {
  kind: string;
  enabled: boolean;
  config: Record<string, unknown>;
  /** True when a credential is stored. The secret itself is never returned. */
  hasSecret: boolean;
  lastUsedAt?: string | null;
  updatedAt?: string | null;
}

export interface ConsentSourceAdminState {
  config: ConsentSourceConfig | null;
  catalogue: ConsentSourceCatalogueEntry[];
  effective?: string;
}

export interface ConsentSourceTestResult {
  ok: boolean;
  elapsedMs: number;
  reachable: boolean;
  answered?: { hasActiveConsent: boolean };
  note?: string;
  error?: string;
}

export async function getConsentSourceConfig(): Promise<ConsentSourceAdminState> {
  return apiFetch<ConsentSourceAdminState>('/v1/admin/consent-source');
}

export async function upsertConsentSourceConfig(args: {
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
}): Promise<{ config: ConsentSourceConfig }> {
  return apiFetch<{ config: ConsentSourceConfig }>('/v1/admin/consent-source', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function testConsentSource(purpose = 'marketing'): Promise<ConsentSourceTestResult> {
  return apiFetch<ConsentSourceTestResult>('/v1/admin/consent-source/test', {
    method: 'POST',
    body: JSON.stringify({ purpose }),
  });
}

export async function clearConsentSourceConfig(): Promise<{ cleared: boolean }> {
  return apiFetch<{ cleared: boolean }>('/v1/admin/consent-source', { method: 'DELETE' });
}

// ── Admin → PAN verifier (customer-credentialed Protean OPV, 2026-07-22) ──
// Same admin shape as the consent source: the credential is encrypted at rest
// and never returned; `hasSecret` is the only trace it exists.

export interface PanVerifierAdminState {
  config: ConsentSourceConfig | null;
  catalogue: ConsentSourceCatalogueEntry[];
  effective?: string;
}

export interface PanRegistryVerdict {
  configured: boolean;
  via?: string;
  ok?: boolean;
  /** Protean's published taxonomy: existing_valid / existing_valid_marked /
   *  not_found / deleted / deactivated / fake. */
  status?: string;
  aadhaarSeeding?: string;
  nameMatch?: boolean;
  dobMatch?: boolean;
  elapsedMs?: number;
  error?: string;
  note?: string;
}

export interface VerifyPanResult {
  /** Masked (AB*******F) - the raw PAN never comes back. */
  pan: string;
  format: { valid: boolean; explanation: string };
  registry: PanRegistryVerdict;
}

export async function getPanVerifierConfig(): Promise<PanVerifierAdminState> {
  return apiFetch<PanVerifierAdminState>('/v1/admin/pan-verifier');
}

export async function upsertPanVerifierConfig(args: {
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
}): Promise<{ config: ConsentSourceConfig }> {
  return apiFetch<{ config: ConsentSourceConfig }>('/v1/admin/pan-verifier', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function testPanVerifier(pan: string): Promise<PanRegistryVerdict> {
  return apiFetch<PanRegistryVerdict>('/v1/admin/pan-verifier/test', {
    method: 'POST',
    body: JSON.stringify({ pan }),
  });
}

export async function clearPanVerifierConfig(): Promise<{ cleared: boolean }> {
  return apiFetch<{ cleared: boolean }>('/v1/admin/pan-verifier', { method: 'DELETE' });
}

export async function verifyPan(pan: string): Promise<VerifyPanResult> {
  return apiFetch<VerifyPanResult>('/v1/discovery/verify/pan', {
    method: 'POST',
    body: JSON.stringify({ pan }),
  });
}

// ── Admin → QR trust anchor (signed-identity-QR verification, 2026-07-22) ──
// The trusted PUBLIC certificate used to verify signed identity QR codes.
// UIDAI Aadhaar Secure QR today; DigiLocker etc. are future provider kinds.
// The cert is public, so it is returned (as a preview) rather than hidden.

export interface QrProviderCatalogueEntry {
  kind: string;
  label: string;
  certBased: boolean;
  help: string;
}

export interface QrTrustAnchor {
  kind: string;
  enabled: boolean;
  hasCert: boolean;
  certPreview: string | null;
  updatedAt?: string | null;
  lastUsedAt?: string | null;
}

export interface QrTrustAnchorState {
  anchors: QrTrustAnchor[];
  catalogue: QrProviderCatalogueEntry[];
}

export interface AadhaarQrVerifyResult {
  parsed: boolean;
  signature: 'valid' | 'invalid' | 'no_cert' | 'unparseable';
  fields?: {
    lastFourDigits?: string;
    referenceId?: string;
    name?: string;
    dob?: string;
    gender?: string;
  };
  error?: string;
}

export async function getQrTrustAnchors(): Promise<QrTrustAnchorState> {
  return apiFetch<QrTrustAnchorState>('/v1/admin/qr-trust-anchor');
}

export async function upsertQrTrustAnchor(args: {
  kind: string;
  certPem: string;
  enabled: boolean;
}): Promise<{ ok: boolean; kind: string; enabled: boolean }> {
  return apiFetch('/v1/admin/qr-trust-anchor', { method: 'PUT', body: JSON.stringify(args) });
}

export async function testQrTrustAnchor(qr: string): Promise<AadhaarQrVerifyResult> {
  return apiFetch<AadhaarQrVerifyResult>('/v1/admin/qr-trust-anchor/test', {
    method: 'POST',
    body: JSON.stringify({ qr }),
  });
}

export async function clearQrTrustAnchor(kind: string): Promise<{ cleared: boolean }> {
  return apiFetch(`/v1/admin/qr-trust-anchor?kind=${encodeURIComponent(kind)}`, { method: 'DELETE' });
}

// ── Admin → AuditSink (T1.4, 2026-05-28) ─────────────────────────
//
// Tenant config for the secondary SIEM/webhook destination that
// audit events ship to alongside the DB chain.

export interface TenantAuditSinkConfig {
  tenantId: string;
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface AuditSinkAdminState {
  config: TenantAuditSinkConfig | null;
  registeredKinds: ReadonlyArray<string>;
}
export interface AuditSinkTestResult {
  ok: boolean;
  kind: string;
  elapsedMs?: number;
  error?: string;
  message?: string;
  health: {
    kind: string;
    reachable: boolean;
    lastSuccessAt: string | null;
    lastError?: string;
  };
}

export async function getAuditSinkConfig(): Promise<AuditSinkAdminState> {
  return apiFetch<AuditSinkAdminState>('/v1/admin/audit-sink');
}

export async function upsertAuditSinkConfig(args: {
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
}): Promise<{ config: TenantAuditSinkConfig }> {
  return apiFetch<{ config: TenantAuditSinkConfig }>('/v1/admin/audit-sink', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function testAuditSink(args?: {
  kind?: string;
  config?: Record<string, unknown>;
}): Promise<AuditSinkTestResult> {
  return apiFetch<AuditSinkTestResult>('/v1/admin/audit-sink/test', {
    method: 'POST',
    body: JSON.stringify(args ?? {}),
  });
}

// ── DAM → Compound findings / kill-chain (2026-05-29) ────────────
//
// Frontend for the compound-severity correlator shipped backend-only in
// commit 8a3ad1f. The cross-engine kill-chain (≥2 of UEBA/Sonar/Early-
// Warning firing on the same actor in a rolling window, severity boosted
// one rung) is the most differentiated DAM capability - this surfaces it.
// Endpoints: GET /v1/activity/compound-findings, GET /:id,
// POST /:id/acknowledge, PATCH /:id/status, POST /correlate.

export type CompoundStatus = 'open' | 'acknowledged' | 'dismissed' | 'escalated';

export interface CompoundContributingFinding {
  engine: string;
  findingId: string;
  severity: string;
  detectedAt: string;
  summary: string;
}

export interface CompoundFinding {
  id: string;
  actorUserId: string;
  enginesFired: string;
  engineCount: number;
  severity: string;
  baseSeverity: string;
  boostApplied: boolean;
  windowSince: string;
  windowUntil: string;
  firstSignalAt: string;
  lastSignalAt: string;
  contributingFindings: CompoundContributingFinding[];
  status: CompoundStatus;
  acknowledgedByUserId: string | null;
  acknowledgedAt: string | null;
  resolutionReason: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  detectedAt: string;
}

export interface CompoundCorrelateResult {
  tenantId: string;
  contributingFetched: { ueba: number; sonar: number; earlyWarning: number };
  draftsProduced: number;
  newCompoundFindings: number;
  durationMs: number;
}

export async function listCompoundFindings(params?: {
  status?: CompoundStatus;
  actorUserId?: string;
  since?: string;
  until?: string;
  limit?: number;
}): Promise<{ findings: CompoundFinding[]; count: number }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.actorUserId) q.set('actorUserId', params.actorUserId);
  if (params?.since) q.set('since', params.since);
  if (params?.until) q.set('until', params.until);
  if (params?.limit !== undefined) q.set('limit', String(params.limit));
  return apiFetch<{ findings: CompoundFinding[]; count: number }>(
    `/v1/activity/compound-findings${q.toString() ? `?${q}` : ''}`,
  );
}

export async function getCompoundFinding(id: string): Promise<{ finding: CompoundFinding }> {
  return apiFetch<{ finding: CompoundFinding }>(`/v1/activity/compound-findings/${id}`);
}

export async function acknowledgeCompoundFinding(
  id: string,
): Promise<{ finding: CompoundFinding }> {
  return apiFetch<{ finding: CompoundFinding }>(
    `/v1/activity/compound-findings/${id}/acknowledge`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

export async function patchCompoundFindingStatus(
  id: string,
  status: 'dismissed' | 'escalated',
  reason: string,
): Promise<{ finding: CompoundFinding }> {
  return apiFetch<{ finding: CompoundFinding }>(`/v1/activity/compound-findings/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
}

export async function runCompoundCorrelator(args?: {
  lookbackHours?: number;
  windowHours?: number;
  boostThreshold?: number;
}): Promise<CompoundCorrelateResult> {
  return apiFetch<CompoundCorrelateResult>('/v1/activity/compound-findings/correlate', {
    method: 'POST',
    body: JSON.stringify(args ?? {}),
  });
}

// ─── Agent entitlement: ceilings + increase requests ────────────────────────
//
// These five wrap the commercial control surface. NOTE the response shape
// differs from the older edge routes: they return `{ ok, data }`, so each
// helper unwraps `.data` and callers get the payload directly, matching how
// every other function in this file behaves.

export interface AgentIncreaseRequest {
  id: string;
  tenantId: string;
  agentClass: 'endpoint' | 'vpcPod' | 'gateway';
  currentLimit: number | null;
  liveAtRequest: number;
  requestedLimit: number;
  justification: string;
  requestedBy: string;
  state: 'requested' | 'approved' | 'rejected' | 'withdrawn';
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

/** Ceilings plus the live verdict per class, and who manages the ceiling. */
export async function getAgentLimits(): Promise<{
  limits: Record<string, unknown>;
  classes: { agentClass: string; verdict: AgentEntitlementVerdict }[];
  approvalSource: string;
}> {
  const r = await apiFetch<{ ok: boolean; data: {
    limits: Record<string, unknown>;
    classes: { agentClass: string; verdict: AgentEntitlementVerdict }[];
    approvalSource: string;
  } }>('/v1/entitlements/agent-limits');
  return r.data;
}

export async function listAgentIncreaseRequests(openOnly = false): Promise<AgentIncreaseRequest[]> {
  const r = await apiFetch<{ ok: boolean; data: AgentIncreaseRequest[] }>(
    `/v1/entitlements/increase-requests${openOnly ? '?open=true' : ''}`,
  );
  return r.data;
}

/**
 * Raise a request. currentLimit and liveAtRequest are deliberately NOT sent -
 * the server reads them itself, so a caller cannot understate their own overage
 * in the record the approver later reads.
 */
export async function createAgentIncreaseRequest(args: {
  agentClass: 'endpoint' | 'vpcPod' | 'gateway';
  requestedLimit: number;
  justification: string;
}): Promise<AgentIncreaseRequest & { upstream: { delivered: boolean; kind: string } }> {
  const r = await apiFetch<{ ok: boolean; data: AgentIncreaseRequest & {
    upstream: { delivered: boolean; kind: string };
  } }>('/v1/entitlements/increase-requests', {
    method: 'POST',
    body: JSON.stringify(args),
    idempotencyKey: newIdempotencyKey(),
  });
  return r.data;
}

/**
 * Decide. `applyLimit` is separate from `decision` on purpose: approving records
 * agreement, applying moves the ceiling, and they are different acts requiring
 * different authority.
 */
export async function decideAgentIncreaseRequest(
  id: string,
  args: { decision: 'approved' | 'rejected'; note?: string; applyLimit?: boolean },
): Promise<AgentIncreaseRequest & { ceilingApplied: number | null }> {
  const r = await apiFetch<{ ok: boolean; data: AgentIncreaseRequest & {
    ceilingApplied: number | null;
  } }>(`/v1/entitlements/increase-requests/${id}/decide`, {
    method: 'POST',
    body: JSON.stringify(args),
    idempotencyKey: newIdempotencyKey(),
  });
  return r.data;
}

// ============================================================
// Release 3.1 - "Sign in with SSO" through FOCTTA Core, and the settings
// Discovery inherits from its paired Core (modules/suite + modules/auth/core-sso
// in the api-controller).
//
// Sign-in: the page asks whether the workspace offers it, then starts an
// authorization-code request at Core with PKCE (S256), a state and a nonce
// that live only in this tab (sessionStorage), and Core returns the browser to
// /auth/core-sso/callback. That page checks the state and sends the callback's
// query with the verifier and nonce to /v1/auth/core-sso/complete, which
// answers exactly as /v1/auth/login does. The role comes from the Discovery
// account, never from Core.
// ============================================================

export interface CoreSsoConfig {
  available: boolean;
  issuer?: string;
  clientId?: string;
  authorizationEndpoint?: string;
  redirectUri?: string;
  scope?: string;
  consentName?: string;
}

export async function getCoreSsoConfig(tenantSlug: string): Promise<CoreSsoConfig> {
  try {
    const res = await fetch(
      `${API_BASE}/v1/auth/core-sso/config?slug=${encodeURIComponent(tenantSlug)}`,
      { headers: { accept: 'application/json' } },
    );
    if (!res.ok) return { available: false };
    const body = (await res.json()) as CoreSsoConfig;
    return body.available === true ? body : { available: false };
  } catch {
    return { available: false };
  }
}

/** What the starting tab keeps until Core returns (sessionStorage only). */
export interface CoreSsoPending {
  tenantSlug: string;
  state: string;
  nonce: string;
  codeVerifier: string;
  redirectUri: string;
  returnUrl: string;
  startedAt: number;
}

export const CORE_SSO_PENDING_KEY = 'foctta.discovery.coreSso.pending';

function randomUrlSafe(bytes: number): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return btoa(String.fromCharCode(...a)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function s256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Build the authorization request and remember its secrets in this tab.
 * Returns the address to send the browser to.
 */
export async function startCoreSso(
  tenantSlug: string,
  cfg: CoreSsoConfig,
  returnUrl = '/',
): Promise<string> {
  if (!cfg.available || !cfg.authorizationEndpoint || !cfg.clientId || !cfg.redirectUri) {
    throw new Error('Sign-in through FOCTTA Core is not available for this workspace.');
  }
  const pending: CoreSsoPending = {
    tenantSlug,
    state: randomUrlSafe(24),
    nonce: randomUrlSafe(24),
    codeVerifier: randomUrlSafe(48),
    redirectUri: cfg.redirectUri,
    returnUrl: returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : '/',
    startedAt: Date.now(),
  };
  sessionStorage.setItem(CORE_SSO_PENDING_KEY, JSON.stringify(pending));
  const url = new URL(cfg.authorizationEndpoint);
  url.searchParams.set('client_id', cfg.clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', cfg.scope ?? 'openid email profile');
  url.searchParams.set('redirect_uri', cfg.redirectUri);
  url.searchParams.set('state', pending.state);
  url.searchParams.set('nonce', pending.nonce);
  url.searchParams.set('code_challenge', await s256(pending.codeVerifier));
  url.searchParams.set('code_challenge_method', 'S256');
  return url.toString();
}

export function takeCoreSsoPending(): CoreSsoPending | null {
  try {
    const raw = sessionStorage.getItem(CORE_SSO_PENDING_KEY);
    sessionStorage.removeItem(CORE_SSO_PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as CoreSsoPending;
    // A request older than the provider's own interaction lifetime is dead.
    if (!p || typeof p.state !== 'string' || Date.now() - p.startedAt > 15 * 60 * 1000) return null;
    return p;
  } catch {
    return null;
  }
}

/** A refusal from /v1/auth/core-sso/complete, with its problem code. */
export class CoreSsoError extends Error {
  constructor(
    message: string,
    public readonly code: string | null,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'CoreSsoError';
  }
}

export async function completeCoreSso(args: {
  tenantSlug: string;
  callbackQuery: string;
  state: string;
  codeVerifier: string;
  nonce: string;
  redirectUri: string;
}): Promise<CustomerLoginResponse> {
  const res = await fetch(`${API_BASE}/v1/auth/core-sso/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    let code: string | null = null;
    try {
      const parsed = JSON.parse(text) as { detail?: string; title?: string };
      if (parsed.detail) detail = parsed.detail;
      if (parsed.title) code = parsed.title;
    } catch {
      /* keep default */
    }
    throw new CoreSsoError(detail, code, res.status);
  }
  return JSON.parse(text) as CustomerLoginResponse;
}

export type SuiteDomain = 'IDENTITY' | 'MAIL' | 'LIFECYCLE' | 'PEOPLE';

export interface InheritedDomainView {
  domain: SuiteDomain;
  source: 'INHERIT' | 'LOCAL';
  decided: boolean;
  canInherit: boolean;
  inherited: unknown;
  problem: string | null;
}

export interface InheritedSettingsView {
  paired: boolean;
  pairing: {
    consentApiUrl: string;
    consentDisplayName: string | null;
    pairedAt: string;
    state: 'ENABLED' | 'DISABLED';
  } | null;
  readAt: string | null;
  linkError: string | null;
  domains: InheritedDomainView[];
}

export async function getInheritedSettings(fresh = false): Promise<InheritedSettingsView> {
  const r = await apiFetch<{ data: InheritedSettingsView }>(
    `/v1/admin/integrations/foctta-core/inherited${fresh ? '?fresh=1' : ''}`,
  );
  return r.data;
}

export async function setInheritedSource(
  domain: SuiteDomain,
  source: 'INHERIT' | 'LOCAL',
): Promise<InheritedSettingsView> {
  const r = await apiFetch<{ data: InheritedSettingsView }>(
    `/v1/admin/integrations/foctta-core/inherited/${domain}`,
    { method: 'PUT', body: JSON.stringify({ source }) },
  );
  return r.data;
}
